/* Ring builder engine — Sapphire Row
   Vanilla ES + custom elements, matching Edge's house style (no framework in the
   theme). Reads pre-built catalog assets; talks to nothing but /cart/add.js.

   STATE lives in the URL, not in localStorage. Every step is a real, shareable,
   crawlable address and browser back/forward just works. sessionStorage carries
   only the scroll position and the last-seen filter set, which are conveniences,
   never the build itself — a build that only exists in localStorage cannot be
   emailed to a partner, which is the single most common thing an engagement-ring
   shopper does.

   THE PRICE THE CUSTOMER SEES IS ALWAYS A REAL VARIANT PRICE. Nothing here
   computes a price to charge; it reads variant prices out of the pre-built map
   and adds them. /cart/add.js has no price field, so a tampered client cannot
   change what is charged. */
(function () {
  'use strict';

  var RB = (window.RingBuilder = window.RingBuilder || {});

  /* ---------------------------------------------------------------- utils */
  var money = function (cents) {
    // Edge's money format is decimal-free ($235). Prices arrive as float dollars.
    return '$' + Math.round(cents).toLocaleString('en-US');
  };
  RB.money = money;

  var el = function (tag, attrs, kids) {
    var n = document.createElement(tag);
    Object.keys(attrs || {}).forEach(function (k) {
      if (k === 'class') n.className = attrs[k];
      else if (k === 'text') n.textContent = attrs[k];
      else if (k === 'html') n.innerHTML = attrs[k];
      else if (k.slice(0, 2) === 'on') n.addEventListener(k.slice(2), attrs[k]);
      else if (attrs[k] != null && attrs[k] !== false) n.setAttribute(k, attrs[k]);
    });
    (kids || []).forEach(function (c) { if (c) n.appendChild(c); });
    return n;
  };
  RB.el = el;

  /* A PRODUCT-PAGE BREADCRUMB (owner, 2026-09-02: the builder's info column should
     be "structured like the product pages"). Both configuration pages — the mounting
     detail (ring-builder-pages.js) and the stone detail (ring-builder-stone.js) —
     open their right column with one, the way `.product__info-container` does on a
     made-to-order PDP. ONE renderer, on the engine, because two pages drawing
     "Home › … › this thing" from two copies is how they come to disagree about the
     separator. The PDP's diamond separator (4px, rotated, 40% ink) is styled in the
     product section's own CSS and does not reach these pages, so ring-builder.css
     restates it under `.rb-crumb`.
     items: [{ text, href? }] — the last item is the current page and never a link. */
  RB.crumb = function (items) {
    var ol = el('ol', { class: 'rb-crumb__list', role: 'list' });
    (items || []).forEach(function (it, i) {
      var last = i === items.length - 1;
      var kids = [];
      if (i > 0) kids.push(el('span', { class: 'rb-crumb__sep', 'aria-hidden': 'true' }));
      kids.push((last || !it.href)
        ? el('span', { class: 'rb-crumb__cur', text: it.text, 'aria-current': last ? 'page' : null })
        : el('a', { class: 'rb-crumb__a', href: it.href, text: it.text }));
      ol.appendChild(el('li', { class: 'rb-crumb__item' }, kids));
    });
    return el('nav', { class: 'rb-crumb', 'aria-label': 'Breadcrumbs' }, [ol]);
  };

  /* -------------------------------------------------------------- the build */
  // The whole build is these values. Everything else is derived.
  // `kind` (owner decision 2026-07-28) is what makes ONE builder serve rings and
  // pendants: it rides in the URL like every other field, so a pendant build is
  // just as shareable and back-button-safe as a ring one. /pages/engagement-ring-builder
  // stays alive as ?kind=ring so the existing URL keeps its SEO.
  // `start` (owner decision 2026-08-05) records WHICH HALF THE SHOPPER CHOSE
  // FIRST, and it is in the URL for the same reason every other field is: the
  // step numbering has to survive a share, a back button and a page load. It is
  // NOT derivable after the fact — once both halves are chosen, a build that
  // began with a stone and one that began with a mounting look identical, and
  // renumbering the bar under someone mid-flow is worse than never reordering it.
  // `side` is the SIDE stone's variant id, and it belongs in FIELDS rather than
  // being threaded by hand: `RB.href` serialises exactly this list, so adding it
  // here is what makes every link, share and back-step carry the chosen side stone.
  // Every previous attempt to carry builder state through a hand-listed subset has
  // silently dropped the shopper's stone; this is the same rule, one field later.
  // `accentStone` (colored pavé, the full menu; owner option 1, 2026-11-03) is the accent stone line chosen for every
  // accent seat, a menu id ("menu:nat:sapphire:blue:faceted:aa"). Absent, the accents are the design's diamonds
  // (`accent`), which stays in the URL as the default to come back to. RB.accentFor has the rule.
  var FIELDS = ['kind', 'start', 'style', 'shape', 'seat', 'metal', 'accent', 'stone', 'side', 'size', 'origin',
                'accentStone'];

  // Rings are the default so every pre-existing link, share and crawl keeps
  // working untouched — a missing ?kind must never mean "no settings".
  // `mount` is the word for the metal half. The pendant flow's own page copy says
  // "Start with a mounting" and the stone product page's button says "Choose a
  // Mounting", while the step bar said "Choose a setting" for the same click —
  // three names for one thing inside two screens of each other.
  //
  // `pair` (owner decision 2026-08-02) is the earring wave's one structural
  // difference and it is deliberately a NUMBER, not a boolean: it is the quantity
  // of BOTH halves that one build orders. Stuller sells an earring mounting as ONE
  // earring — 865 of 945 semi-mount SKUs are `UnitOfSale: Each` — so a finished
  // pair is two mountings AND two stones, where a ring is one and one. Everything
  // that counts (the cart, the total, the copy) multiplies by this, so a fourth
  // wave that happened to sell singly needs no new branch.
  //
  // 🚨 A PAIR IS ONLY POSSIBLE FROM CALIBRATED STOCK. Every serialized stone in
  // this catalogue is one of one — that is the whole premise of the /v2/gem feed —
  // so "order another one" does not exist for them. Calibrated stock is cut to a
  // standard millimetre and graded, so qty 2 of one SKU is consistent by
  // construction. The earring stone step is filtered on exactly that; see
  // `RB.pairable`.
  RB.KINDS = {
    ring:    { label: 'ring',    piece: 'Ring',    mount: 'setting',  sized: true,  pair: 1 },
    pendant: { label: 'pendant', piece: 'Pendant', mount: 'mounting', sized: false, pair: 1 },
    earring: { label: 'earrings', piece: 'Earring', mount: 'mounting', sized: false, pair: 2 }
  };

  // The wave the CURRENT PAGE declares (data-rb-kind), or null off-builder.
  // Read live rather than cached: pageReview and friends can run before and after
  // an async resolve, and a stale value would send step links to the wrong flow.
  RB.pageKind = function () {
    var el = document.querySelector('.rb[data-rb-kind]');
    var k = el && el.getAttribute('data-rb-kind');
    return RB.KINDS[k] ? k : null;
  };

  // PRECEDENCE: explicit ?kind= > the page's own declaration > ring.
  // The query param stays first so every pre-split link keeps its meaning after the
  // 301s (…/engagement-ring-builder?kind=pendant lands on the ring URL but is still
  // a pendant build). New links carry no ?kind= at all — the URL says which flow it is.
  RB.kind = function (build) {
    var q = new URLSearchParams(location.search).get('kind');
    var k = (build && build.kind) || q || RB.pageKind();
    return RB.KINDS[k] ? k : 'ring';
  };

  // A pendant has no finger size. Anything that asks for one must ask this first,
  // or the review step will block a pendant build on a size it can never have.
  RB.isSized = function (build) { return RB.KINDS[RB.kind(build)].sized; };

  /* ------------------------------------------- the shopper-facing name --- */
  // "Solitaire Engagement Ring Setting" -> "Solitaire Engagement Ring".
  // The word SETTING is a catalogue word — it exists on the product so nobody
  // mistakes a bare mounting for a finished ring (see finished-jewelry-variant-
  // rules.md). Inside the builder the shopper already knows they are choosing a
  // mounting, so the grid, the step bar and the detail heading drop it.
  //
  // 🚨 THIS WAS FOUR COPIES OF `/ Setting$/` AND THE ANCHOR BROKE ON 2026-08-05.
  // `fix_setting_duplicates.py` appends a size span, so the title became
  // "… Engagement Ring Setting (4–18 mm)" — "Setting" is no longer at the END and
  // every one of those four regexes silently stopped matching. The grid then read
  // "Solitaire Engagement Ring Setting (4–18 mm)" beside "Solitaire Engagement
  // Ring", which looks like two different KINDS of product rather than two sizes
  // of one. One helper now, so the next suffix cannot desynchronise four call sites.
  RB.settingName = function (title) {
    return String(title || '').replace(/ Setting(?= \(|$)/, '');
  };

  /* ------------------------------------------- which half comes first? --- */
  // Both benchmarks the owner cited do this, and do it with URLs rather than
  // state: Brilliant Earth serves "1 Choose Setting · 2 Choose Diamond" at
  // /engagement-rings/shop-all/ and "1 Choose Diamond · 2 Choose Setting" at
  // /engagement-rings/start-with-a-diamond/. Same builder, same three steps,
  // renumbered to match the way in.
  //
  // 🚨 THE DEEP LINK FROM A STONE PRODUCT PAGE IS THE COMMON CASE, and before
  // this it landed the shopper on "Step 1 — choose a mounting" with the stone
  // they had just picked filed under step 2. The order is what makes the page
  // agree with what the shopper did.
  //
  // PRECEDENCE: an explicit ?start= wins; otherwise a build that carries a stone
  // and no mounting is self-evidently stone-first; otherwise mounting-first,
  // which is what every pre-existing link means and must keep meaning.
  RB.START = { mount: 'mount', stone: 'stone' };
  RB.startedWith = function (build) {
    var s = build && build.start;
    if (RB.START[s]) return s;
    if (build && build.stone && !build.style) return 'stone';
    return 'mount';
  };
  RB.isStoneFirst = function (build) { return RB.startedWith(build) === 'stone'; };

  // BOTH flows share ONE page template, so the hero <h1> and subheading are
  // section settings rendered by Liquid BEFORE `kind` is known — a pendant build
  // read "Design your ring" and "Here is your ring from every angle" while its
  // own step 3 correctly said "Your pendant". Liquid cannot fix this (kind is a
  // client-side URL param), so the piece word is swapped here, the same way the
  // step bar already does it. Scoped to the phrase "your <piece>" so nothing
  // touches "engagement ring" or a style name that legitimately says ring.
  RB.pieceCopy = function (text, build) {
    var label = RB.KINDS[RB.kind(build)].label;
    if (label === 'ring') return text;
    return String(text).replace(/\byour ring\b/gi, function (m) {
      return m.charAt(0) === 'Y' ? 'Your ' + label : 'your ' + label;
    });
  };

  // Rewrite the static hero copy in place. Only nodes the builder owns.
  RB.applyPieceCopy = function (root, build) {
    if (RB.kind(build) === 'ring') return;
    var sel = 'h1, .rb-hero__sub, [data-rb-subheading]';
    (root || document).querySelectorAll(sel).forEach(function (n) {
      if (n.children.length) return;              // only leaf text nodes
      var out = RB.pieceCopy(n.textContent, build);
      if (out !== n.textContent) n.textContent = out;
    });
  };

  RB.read = function () {
    var q = new URLSearchParams(location.search), b = {};
    FIELDS.forEach(function (f) { if (q.get(f)) b[f] = q.get(f); });
    b.kind = RB.kind(b);
    return b;
  };

  RB.href = function (path, build, extra) {
    var q = new URLSearchParams();
    FIELDS.forEach(function (f) {
      // `kind` is no longer a query field — the PATH says which flow this is, and
      // every builder page declares its own wave. Emitting it too would put
      // ?kind=pendant on /pages/pendant-builder, and one stale copy of it on a
      // ring URL would silently drag the shopper into the wrong flow. `extra` can
      // still set it deliberately.
      if (f !== 'kind' && build[f]) q.set(f, build[f]);
    });
    Object.keys(extra || {}).forEach(function (k) {
      if (extra[k] === null) q.delete(k); else q.set(k, extra[k]);
    });
    var s = q.toString();
    return path + (s ? '?' + s : '');
  };

  RB.go = function (path, build, extra) { location.href = RB.href(path, build, extra); };

  // ONE URL SET PER WAVE (owner decision 2026-07-29). Each flow has its own five
  // pages, so a link never needs ?kind= to say what it is and no builder URL says
  // "engagement" any more. The PAGES themselves are separate; the section, the
  // templates' logic and this module are NOT duplicated — every page renders the
  // same `ring-builder` section and differs only by two settings (page + kind).
  //
  // 🚨 THE TWO ENTRIES NAME THEMSELVES (owner decision 2026-08-05). `settings` and
  // `stones` are the two ways INTO the builder, and they now say so:
  //     /pages/{wave}-builder-mounting     /pages/{wave}-builder-gemstone
  // `/pages/{wave}-builder` still resolves — it is a 301 onto -mounting, kept for
  // the links and indexed URLs that predate the split (see rename_builder_pages.py).
  //
  // 🚨 `stones` AND `stone` ARE ONE WORD APART AND BOTH ARE LIVE:
  //     stones -> /pages/{wave}-builder-gemstone    the GRID
  //     stone  -> /pages/{wave}-gemstone            ONE stone in full
  // The owner chose the singular grid name knowing this (2026-08-05). They are
  // distinct handles and nothing collides, but a careless edit here sends the grid
  // to the detail page or the reverse, and both return 200 — so the mistake would
  // render a plausible wrong page rather than an error. `verify_builder_urls.py`
  // asserts each answers 200 directly and carries the builder section.
  RB.PAGES = {
    ring: {
      settings: '/pages/ring-builder-mounting',
      stones: '/pages/ring-builder-gemstone',
      stonesLab: '/pages/ring-builder-gemstone-lab',
      stone: '/pages/ring-gemstone',
      review: '/pages/your-ring'
    },
    pendant: {
      settings: '/pages/pendant-builder-mounting',
      stones: '/pages/pendant-builder-gemstone',
      stonesLab: '/pages/pendant-builder-gemstone-lab',
      stone: '/pages/pendant-gemstone',
      review: '/pages/your-pendant'
    },
    earring: {
      settings: '/pages/earring-builder-mounting',
      stones: '/pages/earring-builder-gemstone',
      stonesLab: '/pages/earring-builder-gemstone-lab',
      stone: '/pages/earring-gemstone',
      review: '/pages/your-earrings'
    }
  };

  RB.pagesFor = function (kind) {
    return RB.PAGES[RB.KINDS[kind] ? kind : 'ring'];
  };

  // `RB.pages` stays a plain-looking object so every existing `RB.pages.review`
  // call site is untouched — it just resolves against the CURRENT wave now. A
  // getter, not a snapshot, because kind is known only once the page has rendered.
  try {
    Object.defineProperty(RB, 'pages', {
      get: function () { return RB.pagesFor(RB.kind(RB.read())); },
      configurable: true
    });
  } catch (e) {                      // pathological old engine: ring URLs, still usable
    RB.pages = RB.PAGES.ring;
  }

  /* ------------------------------------------------------------- catalog */
  // Scoped to the current `kind` by default: the shard now carries BOTH waves, and
  // a shopper building a pendant must never be shown a ring mounting. Settings
  // built before the pendant wave have no `kind` at all, so a missing value reads
  // as 'ring' rather than as "matches nothing" — the difference between an
  // untouched ring builder and an empty one.
  RB.settings = function (kind) {
    var all = (window.RB_SETTINGS && window.RB_SETTINGS.settings) || [];
    if (kind === '*') return all;
    var want = kind || RB.kind(RB.read());
    return all.filter(function (s) { return (s.kind || 'ring') === want; });
  };
  // Looking a style up by id must NOT be kind-scoped — the review step resolves a
  // style from the URL before it has decided anything about kind, and scoping here
  // would make a valid pendant build look like a broken one.
  RB.setting = function (style) {
    return RB.settings('*').filter(function (s) { return String(s.style) === String(style); })[0];
  };

  /* THE SIZES A STYLE IS OFFERED IN, AND THE SIZE IT IS STOCKED IN (ring sizing part 2, owner 2026-09-17). Per STYLE, from
     every SKU the store sells (ring-sizing/builder/census_mountings.py: 18,589 SKUs, all stocked in 7), written into the
     review step's section by the release that shipped this file -- RB_RING_SIZING, in sections/ring-builder.liquid.
     🚨 NOT FROM rb-ring-sizes.js ANY MORE (Codex review-mu5pguof-xcym9p): the nightly job rebuilds that file, and an engine
     whose charges depend on a file another job rewrites can be broken by that job's timing. This data changes only with a
     reviewed release. A style with no record has no size menu and cannot be ordered here (it fails closed). */
  RB.ringSizing = function (style) {
    var m = window.RB_RING_SIZING;
    var r = m && m[String(style)];
    return (r && r.sizes && r.sizes.length && r.stocked !== null && r.stocked !== undefined) ? r : null;
  };
  RB.ringSizes = function (style) {
    var r = RB.ringSizing(style);
    return r ? r.sizes : null;
  };

  RB.sizeLabel = function (v) {
    var n = parseFloat(v);
    return isNaN(n) ? String(v) : (n % 1 === 0 ? n.toFixed(0) : String(n));
  };

  /* ── RING SIZING CHARGES (owner 2026-09-17: "Charge the same sizing table in the ring builder") ─────────────────
     A ring made in any size but the one its setting is stocked in carries the supplier's sizing charge, collected by a
     Ring Sizing line in the cart: the same table, the same product and the same rule as a ring page
     (ring-sizing/menu-v2/rule.py; ring-sizing/builder/test_builder.cjs holds this copy to it, size by size).
       the stocked size       no charge
       any size smaller       one flat charge: $22 gold, $26 platinum
       a quarter size larger  $19 gold, $25 platinum
       larger                 $28 gold, $46 platinum, then +$9 / +$21 a size, up to 6 sizes larger
     Measured 2026-09-17 on every SKU the builder sells (ring-sizing/builder/research): every setting is stocked in 7,
     and every charge the supplier lists is this table.
     ⚠️ IT FAILS CLOSED. A size sells with a charge only when all of it is known: the size is one the setting is offered
     in and its stocked size is on record (RB.ringSizing), the metal is gold or platinum, the shopper is in the store's
     own currency, and a Ring Sizing variant is on sale whose title AND price are that charge (RB_SIZING_FEES,
     sections/ring-builder.liquid). Anything else is 'unavailable': never free, never sold. */
  RB.SIZING = {
    gold:     { down: 22, quarter: 19, base: 28, step: 9,  maxUp: 600 },
    platinum: { down: 26, quarter: 25, base: 46, step: 21, maxUp: 600 }
  };
  // Hundredths of a size (7.25 is 725), so sizes are compared as whole numbers, never as floats.
  function sizeHundredths(v) {
    var n = parseFloat(v);
    return isNaN(n) ? null : Math.round(n * 100);
  }
  // The table: the charge in dollars for moving `d` hundredths of a size from the stocked size, or null out of reach.
  RB.sizingCharge = function (metal, d) {
    var t = RB.SIZING[metal];
    if (!t || typeof d !== 'number' || isNaN(d)) return null;
    if (d === 0) return 0;
    if (d < 0) return t.down;
    if (d > t.maxUp) return null;
    if (d === 25) return t.quarter;
    return t.base + t.step * Math.floor((d - 50) / 100);
  };
  /* -> { size, kind: 'stock' | 'charge' | 'unavailable', charge (dollars, per ring), variantId (the Ring Sizing
     variant, a string) } for one size of this build. */
  RB.sizingFor = function (build, size) {
    var out = { size: size, kind: 'unavailable', charge: null, variantId: null };
    var want = sizeHundredths(size);
    var rec = build ? RB.ringSizing(build.style) : null;
    if (want === null || !rec) return out;
    if (!rec.sizes.some(function (s) { return sizeHundredths(s) === want; })) return out;
    var stocked = sizeHundredths(rec.stocked);
    if (stocked === null) return out;
    if (want === stocked) {
      out.kind = 'stock';
      out.charge = 0;
      return out;
    }
    var m = String(build.metal || '');
    var metal = m.indexOf('Platinum') >= 0 ? 'platinum' : (m.indexOf('Gold') >= 0 ? 'gold' : null);
    var charge = metal ? RB.sizingCharge(metal, want - stocked) : null;
    if (!charge) return out;
    /* ⚠️ ONLY IN THE STORE'S OWN CURRENCY (Codex review-mu5nuao0-2c20xf). The variant prices here are in the shopper's
       currency: in a converted one a Ring Sizing variant's price cannot be checked against its title, and every amount
       this page shows is in dollars. So a converted currency buys the stocked size only. (The store sells in USD alone,
       Shopify.currency.rate 1.0, measured 2026-09-17: this changes nothing today.) */
    var cur = window.Shopify && window.Shopify.currency;
    if (cur && cur.rate && Number(cur.rate) !== 1) return out;
    var fees = window.RB_SIZING_FEES || [];
    for (var i = 0; i < fees.length; i++) {
      var r = fees[i];
      if (r && r[0] === '$' + charge && r[2] === true && Number(r[3]) === charge * 100) {
        out.kind = 'charge';
        out.charge = charge;
        out.variantId = String(r[1]);
        return out;
      }
    }
    return out;
  };

  /* ---- ACCENT STONES (colored pavé, the full menu; owner option 1, 2026-11-03) --------------------------------------
     ONE calibrated stone line for every accent seat, halo and band, never per stone, chosen on its own step with the
     design's diamond accents preselected (the side-stone door's pattern). A build with accent stones sells as two parts:
     the setting line is the design's EMPTY mounting (its variant whose accent is RB.ACCENT_VALUE, priced at today's
     ladder like any setting), and an "Accent Stones" service line carries the rest of today's one-markup price of the
     whole ring (colored-pave/build_accent_shards.py: the ring at the rule minus the empty setting, a whole $10 step).
     Data, lazy, review and setting pages only: rb-accent-menu.js (the lines, their preview materials, the designs with
     a shard) and rb-accent-<style>.js ("<seat>|<metal>" -> [[line index, charge, stones, stock verified]]).
     ⚠️ IT FAILS CLOSED, like RB.sizingFor. A line is offered only where the empty mounting is on sale for this seat and
     metal, the design's shard lists the line there, the shopper is in the store's own currency, and an Accent Stones
     variant is on sale whose title AND price are that charge (RB_ACCENT_FEES, sections/ring-builder.liquid). Anything
     else offers nothing, so before the store items exist every design reads exactly as it does today. */
  RB.ACCENT_VALUE = 'Gemstone Accents';
  RB.loadAccentMenu = function (style) {
    if (!style) return Promise.resolve(null);
    return RB.loadAsset('rb-accent-menu.js', 'RB_ACCENT_MENU').then(function (menu) {
      if (!menu || (menu.styles || []).indexOf(String(style)) < 0) return null;
      return RB.loadAsset('rb-accent-' + style + '.js', 'RB_ACCENT_' + style).then(function (sh) {
        return sh ? { menu: menu, shard: sh } : null;
      });
    });
  };
  // The Accent Stones variant for a charge in dollars, or null (store currency only: RB.sizingFor's rule).
  RB.accentFee = function (charge) {
    var cur = window.Shopify && window.Shopify.currency;
    if (cur && cur.rate && Number(cur.rate) !== 1) return null;
    var fees = window.RB_ACCENT_FEES || [];
    for (var i = 0; i < fees.length; i++) {
      var r = fees[i];
      if (r && r[0] === '$' + charge && r[2] === true && Number(r[3]) === charge * 100) return String(r[1]);
    }
    return null;
  };
  // One menu line as the pages read it.
  RB.accentLine = function (menu, f) {
    return { id: f[0], label: f[1], origin: f[2], species: f[3], colour: f[4], cut: f[5], grade: f[6],
             curve: (menu.curves || {})[f[7]] || null, treatment: f[8] || '' };
  };
  // "Natural blue sapphire" / "Lab-grown green emerald": what a line is called on the page, the cart and the order.
  RB.accentName = function (o) {
    return (o.origin === 'lab' ? 'Lab-grown ' : 'Natural ') + String(o.colour + ' ' + o.species).toLowerCase();
  };
  /* -> [{ id, label, origin, species, colour, cut, grade, curve, treatment, charge (dollars, per ring), stones,
     variantId (the Accent Stones variant) }] for this build's seat and metal, or [] (data not loaded, no empty mounting,
     nothing on sale). Synchronous: callers load RB.loadAccentMenu(style) first. */
  RB.accentOptions = function (build) {
    var menu = window.RB_ACCENT_MENU, sh = build && window['RB_ACCENT_' + build.style];
    if (!menu || !sh || !build.seat || !build.metal) return [];
    if (!RB.settingVariant(Object.assign({}, build, { accentStone: 'x' }))) return [];
    var rows = (sh.cells || {})[build.seat + '|' + build.metal] || [];
    return rows.map(function (r) {
      var f = menu.families[r[0]];
      var vid = f ? RB.accentFee(r[1]) : null;
      if (!vid) return null;
      var o = RB.accentLine(menu, f);
      o.charge = r[1]; o.stones = r[2]; o.variantId = vid;
      return o;
    }).filter(Boolean);
  };
  // The build's chosen accent line, offered at its seat and metal, or null (none chosen, or not offered here).
  RB.accentFor = function (build) {
    if (!build || !build.accentStone) return null;
    return RB.accentOptions(build).filter(function (o) { return o.id === build.accentStone; })[0] || null;
  };
  // Does this design have accent stones to choose at this seat and metal? (data loaded first)
  RB.hasAccentMenu = function (build) {
    return RB.accentOptions(Object.assign({}, build, { accentStone: null })).length > 0;
  };

  /* 🚨 WHAT TO PRINT IN A "COLOR" FIELD, AND IT IS NOT THE SAME COLUMN FOR BOTH
     KINDS (owner, 2026-08-17: the stone spec table read *"Bl"*, "not good front
     facing").

     Two different fields live on every stone and only one is for a shopper:
       `color` = `custom.colour_grade` — for a DIAMOND the D-Z grade ("H", "G"),
                 exactly right; for a COLOURED stone the supplier's two-letter TRADE
                 CODE ("Bl", "Gr", "Co"), ~31% filled and unreadable.
       `hue`   = `custom.gem_color` — the store's own word ("Blue", "Padparadscha"),
                 written by write_gem_color.py and kept current nightly.
     `card-stone-row.liquid` has always printed the right one and says so in its own
     comment; the JS surfaces printed the raw column, which put the code on a spec
     table, a compare row, a list cell and a card spec line.

     🚨 IT LIVES ON THE ENGINE, NOT ON `RB.SCALES`, AND THAT IS THE WHOLE POINT. The
     first cut put it beside the grade ladders in ring-builder-stones.js — which
     `sections/ring-builder.liquid` loads ONLY for `page == 'stones'`. The stone
     DETAIL page (`page == 'stone'`) does not load it, so `RB.SCALES.colourLabel`
     was undefined there, the call threw inside `mount()` after it had already set
     the H1, and the page rendered a title over an EMPTY body. `ring-builder.js` is
     loaded by every builder step and by diamond-browse.js's own guard, so this is
     the one file all four consumers already have.

     ⚠️ NO FALLBACK TO THE TRADE CODE. A coloured stone with no recorded hue prints
     NOTHING — the row does not render, which is honest, where "Bl" is the defect.
     ⚠️ Keyed on the species word, not `_isD`: that flag is set by the grid's own
     pass and does not exist on a detail-page row. */
  RB.colourLabel = function (r) {
    if (!r) return null;
    return r.species === 'Diamond' ? (r.color || null) : (r.hue || null);
  };

  RB.seatKey = function (shape, l, w) { return shape + '|' + l + '|' + w; };

  RB.parseSeat = function (key) {
    var p = String(key).split('|');
    return { shape: p[0], l: parseFloat(p[1]), w: parseFloat(p[2]) };
  };

  RB.seatLabel = function (key) {
    var s = RB.parseSeat(key), t = s.shape.charAt(0) + s.shape.slice(1).toLowerCase();
    return s.l === s.w ? t + ' ' + s.l.toFixed(2) + ' mm'
                       : t + ' ' + s.l.toFixed(2) + ' × ' + s.w.toFixed(2) + ' mm';
  };

  // (style, seat, metal, accent) -> [variantId, price]. Lazy: only the detail and
  // review pages load rb-setting-variants.js.
  /* 🚨 A CARRIED PARAM MUST BE CHECKED AGAINST THE STYLE THAT IS NOW SELECTED
     (owner hit this 2026-08-17 on a real build: style 224129, seat ROUND|7.4|7.4,
     14K Yellow Gold, `accent=None` -> "That combination is not available", with both
     steps ticked).

     The style, the seat and the metal all existed. `accent=None` did not, and never
     had: 224129 is a hidden halo, so it HAS accent diamonds and its vocabulary is
     ['Lab-Grown', 'Natural']. The value came from a different style — **457 of the
     522 styles carry accents ['None']** against 47 that carry Lab-Grown/Natural — so
     configuring any ordinary solitaire and then switching to a halo carries a `None`
     the new style cannot honour. The commonest path in the builder.

     The detail page already did this for SEAT (`seatsFor.indexOf(seat) < 0` falls back
     to `RB.defaultSeat`) and simply did not for metal or accent — those read
     `q.get(...) || default`, so the URL won even when the style disagreed. Metal has
     not bitten because all 522 styles happen to offer the same four; that is luck, not
     a rule, so it is normalised here too.

     ⚠️ THIS IS NORMALISATION, NOT A SUPPRESSED ERROR. It only ever replaces a value
     the style does not offer with one it does. A genuinely missing variant — a real
     (style, seat, metal, accent) that Stuller does not make — still returns null and
     the review page still refuses, which is the guard that stops us quoting a price
     we cannot sell. */
  RB.normalizeToStyle = function (b, s) {
    if (!b || !s) return b;
    var out = Object.assign({}, b);
    if (s.metals && s.metals.length && s.metals.indexOf(out.metal) < 0) {
      out.metal = s.metals.indexOf('14K Yellow Gold') >= 0 ? '14K Yellow Gold'
                                                           : s.metals[0];
    }
    if (s.accents && s.accents.length && s.accents.indexOf(out.accent) < 0) {
      /* ⚠️ MATCH THE CENTRE STONE'S FORMATION WHERE THE STYLE OFFERS IT, rather than
         taking whatever sits first in the array. `s.accents[0]` is 'Lab-Grown' for
         style 224129 purely by build order, so a NATURAL centre stone was being given
         lab accents — a defensible ring, a different price, and not a choice the
         shopper made. Falling back to the array order stays the answer for a style
         that offers only one, or a build with no origin. */
      var want = out.origin === 'lab' ? 'Lab-Grown'
               : out.origin === 'nat' ? 'Natural' : null;
      out.accent = (want && s.accents.indexOf(want) >= 0) ? want : s.accents[0];
    }
    return out;
  };

  /* THE TABLE COMES IN TWO FORMS (2026-09-21). The full `rb-setting-variants.js`
     (~2 MB) took 8-20 s on a cold CDN fetch after every nightly rebuild, and the
     configure step waits for it. `rb-setvar-NN.js` are the same rows split by
     style (setting_variant_shards.py proves them equal, every row, every build);
     `RB.loadSettingVariants` fetches the chosen style's shard and falls back to
     the full table. The full table wins when both are here: it is what this
     function has always read. A shard row is the full row with "<style>|" taken
     off the key, so the value is the same [variant id, price, ...]. */
  RB.settingVariant = function (b) {
    var map = window.RB_SETTING_VARIANTS;
    // a build with accent stones is sold on the design's EMPTY mounting (RB.ACCENT_VALUE), never its diamond variant
    var accent = b.accentStone ? RB.ACCENT_VALUE : b.accent;
    if (map) return map[[b.style, b.seat, b.metal, accent].join('|')] || null;
    var rows = setvarRows(b.style);
    return (rows && rows[[b.seat, b.metal, accent].join('|')]) || null;
  };

  // 🚨 KEEP EQUAL TO setting_variant_shards.py (N and bucket()). The test runs this
  // function against every style. A mismatch is slow, never wrong: a shard that
  // lacks the style sends loadSettingVariants to the full table.
  RB.SETVAR_N = 40;
  RB.setvarBucket = function (style) {
    var h = 0, t = String(style);
    for (var i = 0; i < t.length; i++) h = (Math.imul(h, 31) + t.charCodeAt(i)) >>> 0;
    return h % RB.SETVAR_N;
  };
  function setvarId(style) {
    var b = RB.setvarBucket(style);
    return (b < 10 ? '0' : '') + b;
  }
  // 🚨 A SHARD THE NIGHTLY STOPPED REFRESHING IS NOT TRUSTED. Shards and the settings
  // index come out of one build, minutes apart. A shard more than six hours OLDER
  // than the index this page loaded means the build no longer writes shards (or
  // their upload keeps failing) while the full table is still fresh — so the full
  // table answers. A NEWER shard is fine: that is a cached page after a rebuild,
  // which is what the full table's own URL has always meant.
  function setvarFresh(sh) {
    var idx = window.RB_SETTINGS;
    var a = Date.parse((idx && idx.built) || ''), b = Date.parse(sh.built || '');
    if (isNaN(a)) return true;            // no index to compare with: nothing to judge by
    return !isNaN(b) && a - b <= 6 * 3600 * 1000;
  }
  function setvarRows(style) {
    var sh = window['RB_SETVAR_' + setvarId(style)];
    var st = sh && sh.n === RB.SETVAR_N && setvarFresh(sh) && sh.styles;
    return (st && Object.prototype.hasOwnProperty.call(st, style)) ? st[style] : null;
  }
  // True once `RB.settingVariant` can answer for this style: null then means "not
  // made", not "not loaded yet". The review page waits on this before it prices.
  RB.settingVariantsReady = function (style) {
    return !!window.RB_SETTING_VARIANTS || !style || !!setvarRows(style) ||
           !!setvarTried[style];
  };

  /* ONLY WHAT IS MADE AT THIS CENTER SIZE (September audit M147, 2026-09-25).
     `normalizeToStyle` checks a metal and an accent against the STYLE's lists, and
     those lists are the union of every seat. Not every seat is made in every pair:
     the halo 192448 is made for a 6 mm heart center only in 14K White Gold with
     natural accents, and at 23 of its 25 seats the page's own default (14K Yellow
     Gold) is not made at all. The setting page pressed a pair nobody can order and
     the shopper found out at review ("That combination is not available").
     Census of the 2026-09-24 build: 33 seats in 6 styles (rings 192448, 226272,
     220827; pendants 117988, 193386, 42567).

     Answers the pair to show at this seat, and the chips to gray:
       { metal, accent, changed, unmade, offMetals, offAccents }
     The order keeps what the shopper has: their pair; their metal with another
     accent; then the store's metal order (14K Yellow, White, Rose, Platinum), their
     accent first. offMetals: no accent is made in that metal at this seat.
     offAccents: not made with the answered metal at this seat.

     🚨 `keep` ({ metal, accent }, optional) NAMES THE VALUES THE SHOPPER CHOSE, AND
     THE ANSWER NEVER SWITCHES THEM (owner 2026-09-27, "3. Yes", after Codex
     review-mujrxdce-xj1obt: a halo in 14K Yellow Gold at Round 5.2 mm, then Heart
     6.0 mm, and the setting page sent 14K White Gold on to review without the
     shopper ever accepting it). Only the page's own values move. When no made pair
     keeps the shopper's values the answer is `unmade: true` with their values
     handed back (a value of the page's own still moves to one this seat is made
     in, so what is left unmade is the shopper's alone); the setting page says so
     and carries nothing forward. Without `keep` (the review step) there is always
     a made answer, exactly as before.

     ⚠️ NULL MEANS "NO OPINION", NEVER "NOTHING IS MADE". Before the variant table
     is ready, or when this seat has no row at all (a failed load), nothing is
     grayed and the caller keeps its own values: the page exactly as it was. A
     table that did not load must never gray every chip on the page.
     ⚠️ THIS FUNCTION IS ALSO THE VERSION MARK. ring-builder-pages.js calls it only
     when `typeof RB.validCombo === 'function'`, so a page that pairs one new file
     with a cached copy of the other behaves exactly as before. */
  RB.COMBO_METALS = ['14K Yellow Gold', '14K White Gold', '14K Rose Gold', 'Platinum'];
  RB.validCombo = function (style, seat, metal, accent, keep) {
    var s = RB.setting(style);
    if (!s || !seat || !RB.settingVariantsReady(style)) return null;
    keep = keep || {};
    var metals = (s.metals && s.metals.length) ? s.metals : [metal];
    var accents = (s.accents && s.accents.length) ? s.accents : [accent];
    var made = function (m, a) {
      return !!RB.settingVariant({ style: style, seat: seat, metal: m, accent: a });
    };
    var offMetals = metals.filter(function (m) {
      return !accents.some(function (a) { return made(m, a); });
    });
    if (offMetals.length === metals.length) return null;
    // `first` leads, then the rest of `pool` in its own order; nothing outside `pool`
    var order = function (first, pool) {
      return first.concat(pool).filter(function (v, i, all) {
        return pool.indexOf(v) >= 0 && all.indexOf(v) === i;
      });
    };
    var byMetal = order([metal].concat(RB.COMBO_METALS), metals);
    var pick = null;
    (keep.metal ? [metal] : byMetal).some(function (m) {
      return (keep.accent ? [accent] : order([accent], accents)).some(function (a) {
        if (made(m, a)) pick = [m, a];
        return !!pick;
      });
    });
    var unmade = !pick;
    if (unmade) {
      pick = [keep.metal ? metal : byMetal.filter(function (m) {
        return offMetals.indexOf(m) < 0;
      })[0], keep.accent ? accent : order([accent], accents).filter(function (a) {
        return metals.some(function (m) { return made(m, a); });
      })[0] || accent];
    }
    return {
      metal: pick[0], accent: pick[1],
      changed: pick[0] !== metal || pick[1] !== accent,
      unmade: unmade,
      offMetals: offMetals,
      offAccents: accents.filter(function (a) { return !made(pick[0], a); })
    };
  };

  // Approved fixed illustrations are tied to the exact physical mounting SKU.
  // They are independent of the parametric ring/pendant renderer.
  RB.earringPreview = function (build, stone) {
    if (RB.fixedPhotoRing(build)) return fixedRingPreview(build, stone);
    if (!build || RB.kind(build) !== 'earring') return null;
    var seat = RB.parseSeat(build.seat);
    if (!seat) return null;
    var key = [build.style, seat.shape, String(Number(seat.l)),
      String(Number(seat.w)), build.metal, build.accent || 'None'].join('|');
    var row = (window.RB_EARRING_IMAGES || {})[key];
    var variant = RB.settingVariant(build);
    if (!row || !variant || variant[3] !== row.sku) return null;
    var url = function (name) { return (window.RB_ASSET_BASE || '') + name; };
    if (!stone && build.stone) {
      return {frames: [{url: row.original, label: 'Original mounting'}],
        caption: 'Original mounting. Loading the selected stone.'};
    }
    if (stone && !RB.isColourlessDiamond(stone)) {
      if (row.sapphire && String(stone.vid) === row.sapphire.vid) {
        return {frames: [{url: url(row.sapphire.image), label: 'Front and side'}],
          caption: 'Sapphire illustration. Side view inferred.'};
      }
      return {frames: [{url: row.original, label: 'Original mounting'}],
        caption: 'Original mounting. A finished preview for this stone is not available.'};
    }
    return {frames: [{url: url(row.pair), label: 'Pair'},
                    {url: url(row.front_side), label: 'Front and side'}],
      caption: 'Design illustration with generic diamonds; not the selected stones.'};
  };

  /* ══ A RING WHOSE SUPPLIER PICTURE IS ONE FIXED PHOTOGRAPH (owner option A, 2026-09-28) ══════════════
     689651, the Bezel-Set Emerald-Cut Toi et Moi: its supplier picture is a flat photograph per metal with two
     generic diamonds, which nothing can recolor (pull_setting_renders.centre_of leaves it out). The owner chose the
     earring treatment for it: RB.earringPreview (above) answers for it with that photograph and the earring builder's
     own frame label and captions, never recolored, and the review page shows the shopper's own stone photograph beside
     it (ring-builder-pages.js). No new words.
     ⚠️ THE PAGE SCRIPT CALLS `RB.fixedPhotoRing` GUARDED: for ~20 minutes after a release a browser can pair it with
     this file's previous copy, and a missing function must read as "an ordinary ring". */
  RB.FIXED_PHOTO_RINGS = ['689651'];
  RB.fixedPhotoRing = function (build) {
    return !!build && RB.kind(build) === 'ring' && RB.FIXED_PHOTO_RINGS.indexOf(String(build.style)) >= 0;
  };
  // The setting's own photograph of the metal shown (s.imgByMetal, the metal RB.settingThumb defaults to).
  // 🚨 NEVER ANOTHER METAL'S PHOTOGRAPH (Codex review-muunbcfe-4za78s, finding 1). This ring has no render to draw a
  // missing metal with, so a metal with no photograph of its own (not taken yet, or still processing) gets the earring
  // stage's own "Preview unavailable." state, never the featured photo in another metal: the site's rule (brand_settings,
  // RB.settingThumb and the grid tiles for a two-tone metal). Only when the page script draws that state (its version
  // mark, RB.stageShowsUnavailable): an older copy reads `frames[0]`, so it gets no preview, as before this release.
  function fixedRingPreview(build, stone) {
    var s = RB.setting(build.style);
    if (!s) return null;
    var m = build.metal ||
      (s.metals && s.metals.indexOf('14K Yellow Gold') >= 0 ? '14K Yellow Gold' : (s.metals || [])[0]);
    var u = m && (s.imgByMetal || {})[m];
    if (!u) return RB.stageShowsUnavailable ? {frames: [], caption: 'Preview unavailable.'} : null;
    var frames = [{url: u, label: 'Original mounting'}];
    if (!stone && build.stone) return {frames: frames, caption: 'Original mounting. Loading the selected stone.'};
    if (stone && !RB.isColourlessDiamond(stone)) {
      return {frames: frames, caption: 'Original mounting. A finished preview for this stone is not available.'};
    }
    return {frames: frames, caption: 'Design illustration with generic diamonds; not the selected stones.'};
  }

  /* ---- lazy asset loading --------------------------------------------- */
  // The renders map and the 14,233-entry variant map are only needed once the
  // customer opens a setting or reaches review. Inlining them on the grid took
  // step 1 from 6 KB to 225 KB gzipped, so they load on demand instead.
  var assetCache = {};

  // 🚨 THE BUILD STAMP, FOR EVERYTHING FETCHED BY FILENAME.
  // `RB_ASSET_BASE` is built by STRIPPING Liquid's `?v=` hash off an asset_url, so
  // anything requested through it is a version-less URL the CDN may hold for a very
  // long time. loadShard already knew this and busts with `meta.built`; loadAsset
  // did not, and on 2026-08-04 the storefront served `rb-setting-variants.js` at
  // 1,688,216 bytes with ZERO earring keys while the Admin API confirmed the
  // 2,281,086-byte file on MAIN by checksum. A `?cb=` query did not shift it.
  // ⚠️ THE LESSON IS BIGGER THAN THE BUG: a checksum proof against MAIN says the
  // bytes are on the theme, NOT that the storefront is serving them.
  // Prefers the stones meta (present on step 2) and falls back to the settings
  // index, which every builder page loads eagerly WITH Liquid's hash — so its
  // `built` is always current. Both change on every rebuild, which is exactly the
  // moment these files need a new URL.
  function buildStamp() {
    var m = window.RB_STONES_META, s = window.RB_SETTINGS;
    return (m && m.built) || (s && s.built) || '1';
  }

  RB.loadAsset = function (file, globalName) {
    if (window[globalName]) return Promise.resolve(window[globalName]);
    if (assetCache[file]) return assetCache[file];
    assetCache[file] = new Promise(function (res) {
      var sc = document.createElement('script');
      sc.src = (window.RB_ASSET_BASE || '') + file +
               (file.indexOf('?') >= 0 ? '&' : '?') +
               'v=' + encodeURIComponent(buildStamp());
      sc.onload = function () { res(window[globalName] || null); };
      sc.onerror = function () { res(null); };
      document.head.appendChild(sc);
    });
    return assetCache[file];
  };
  /* 🚨 THIS HARDCODED THE RING MAP, SO THE PENDANT CONFIGURE VIEW COULD NEVER
     REDRAW (measured live 2026-09-02). The Liquid section already picks the wave's
     shard on the `setting`/`review` pages — but the mounting CONFIGURE view is
     `page == 'settings'` with `?configure=1`, and it lazy-loads its detail assets
     through THIS function, which fetched `rb-setting-renders.js` for every wave. On a
     pendant that map holds no pendant style, `RB.anglesFor` returned [] and the page
     fell back to the stored photograph — so the shopper pressed Cushion, then Emerald
     8×6, and kept looking at a ROUND brilliant. Metal still worked (per-metal photos),
     which is exactly what made shape and size read as dead controls: the same "facet
     that cannot change the picture" defect the owner reported on step 1 in August,
     fixed there for the grid tiles and never here. Rings were unaffected because the
     ring map is the one it loaded.
     `RB.rendersFor` already reads both globals, so loading the pendant map is all the
     detail view needs. Earrings fall to the ring map as before: their wave is flat
     photographs and `anglesFor` is meant to return [] there. */
  // The chosen style's variants: its shard, else the full table. Resolves either
  // way; `setvarTried` is what lets a style the store really does not have fall
  // through to the page's own "not available" instead of waiting forever.
  var setvarTried = {};
  RB.loadSettingVariants = function (style) {
    if (window.RB_SETTING_VARIANTS || !style) return Promise.resolve();
    var id = setvarId(style);
    return RB.loadAsset('rb-setvar-' + id + '.js', 'RB_SETVAR_' + id).then(function () {
      if (setvarRows(style)) return;
      return RB.loadAsset('rb-setting-variants.js', 'RB_SETTING_VARIANTS');
    }).then(function () { setvarTried[style] = true; });
  };

  RB.needSettingDetail = function (build) {
    var pendant = RB.kind(build) === 'pendant';
    return Promise.all([
      RB.loadSettingVariants(build && build.style),
      pendant ? RB.loadAsset('rb-setting-renders-pendant.js', 'RB_RENDERS_PENDANT')
              : RB.loadAsset('rb-setting-renders.js', 'RB_RENDERS')
    ]);
  };

  /* ---- stone shards -------------------------------------------------- */
  var shardCache = {};
  RB.loadShard = function (shape, origin) {
    var key = shape + '|' + origin;
    if (shardCache[key]) return shardCache[key];
    var meta = window.RB_STONES_META;
    var info = meta && meta.shards[key];
    if (!info) return (shardCache[key] = Promise.resolve(null));
    shardCache[key] = new Promise(function (res) {
      if (window[info.var]) return res(window[info.var]);
      var s = document.createElement('script');
      // CACHE-BUST WITH THE BUILD STAMP. Shards are injected at runtime, so they
      // never get Liquid's `asset_url` ?v= hash and the browser would otherwise
      // hold a stale copy forever — which after an image recut means every card
      // pointing at media that has been deleted. rb-stones-meta.js IS versioned
      // by Liquid, so its `built` value is always fresh and makes a sound key.
      s.src = (window.RB_ASSET_BASE || '') + info.file +
              '?v=' + encodeURIComponent((meta && meta.built) || '1');
      s.onload = function () { res(window[info.var] || null); };
      s.onerror = function () { res(null); };
      document.head.appendChild(s);
    });
    return shardCache[key];
  };

  // Columnar -> objects, decoding the shared dictionary.
  RB.rows = function (shard) {
    if (!shard) return [];
    var c = shard.cols, v = (window.RB_STONES_META || {}).vocab || {};
    var d = function (f, i) { var x = c[f] && c[f][i]; return x == null || x < 0 ? null : (v[f] || [])[x]; };
    var out = [];
    for (var i = 0; i < shard.n; i++) {
      out.push({
        vid: c.v[i], handle: c.h[i], price: c.p[i], title: c.t[i],
        carat: c.ct[i], l: c.L[i], w: c.W[i],
        species: d('sp', i), color: d('col', i), clarity: d('cl', i),
        cut: d('cg', i), lab: d('lab', i), cert: c.cn[i], certUrl: c.cu[i],
        wear: d('wear', i), video: c.vid[i], img: c.img[i],
        // ⚠️ BOTH OF THESE ARE DIAMOND-SHARD COLUMNS AND ARE ABSENT FROM THE
        // BUILDER'S OWN SHARDS — `d()` returns null for a column that is not there,
        // so this is a no-op on every stone the ring/pendant/earring builder loads.
        // They are decoded HERE rather than in diamond-browse.js because this is the
        // ONE decoder: a second one would have to re-implement the shared-dictionary
        // lookup, and the copy that drifts is always the one nobody is looking at.
        // `cutStyle` is Stuller's cut style; `setting` is "Ring", "Pendant" or
        // "Pendant+Ring" — the browse panel's "Can be set" facet.
        // `treatment` is colour enhancement, and ONLY that — "Color Enhanced" where
        // Stuller files the stone that way, "None" where the colour grade is a D-Z
        // letter (which enhancement cannot produce), null where the origin of the
        // colour is genuinely unstated. See build_diamond_assets.load_enhanced.
        cutStyle: d('cs', i), setting: d('st', i), treatment: d('tr', i),
        // `quality` is the supplier grade on a calibrated stone, or the
        // one-of-a-kind sentinel. Null on a shard built before 2026-09-04, which
        // `d()` already returns for a column that is not there.
        quality: d('ql', i),
        // ⚠️ `country` IS AN ALL-STONES-SHARD COLUMN, absent from the other two sets —
        // `d()` gives null for a column that is not there, so it is a no-op for the
        // builder and the diamond page, exactly like the three above.
        //
        // 🚨 `hue` IS NOT: THE BUILDER'S OWN SHARDS CARRY IT AS OF 2026-08-17, which
        // is what let the builder's coloured rail grow the Color row it had been
        // missing. That is the whole reason it is decoded HERE under this name —
        // naming the builder's column anything else would have meant a second
        // decoder for one field. Do not "tidy" it back in with `country`.
        //
        // `hue` is `custom.gem_color` and is the COLORED rail's primary facet. It
        // is deliberately NOT `color`: that one is `custom.colour_grade`, the
        // supplier's two-letter trade code ("Bl", "Gr"), ~31% filled and unreadable
        // to a shopper. Both exist on the same stone and they are not the same
        // field — reading the wrong one gives a facet blank 76% of the time.
        // `country` is `custom.origin`, named to avoid colliding with the shard's
        // own `origin` (which means lab vs natural, not where it was mined).
        hue: d('hue', i), country: d('org', i),
        // 🚨 DOMED (cabochon / sugarloaf / mabe), 1 or 0 — read RAW, not through
        // `d()`: it is a plain flag, not a dictionary index, so `d()` would look 1 up
        // in a vocab that has no such entry and hand back undefined. Same trap the
        // three sort columns below carry a note about.
        // Stuller ships no non-faceted stone material (17 object names probed, every
        // one a byte-identical no-op), so a domed centre cannot be RENDERED honestly —
        // the review page draws the stone's own photograph instead. Absent on a shard
        // built before 2026-08-17, and `!!undefined` is false, which is exactly the
        // old behaviour.
        domed: !!(c.dome && c.dome[i]),
        // ⚠️ THE THREE SORT COLUMNS ARE READ RAW, NOT THROUGH `d()`. They are plain
        // numbers, not dictionary indices — `d()` would look 1723 up in a vocab
        // that has no such entry and hand back undefined. `created` is epoch
        // SECONDS; `featOrder` / `bestOrder` are Shopify's own positions, and a
        // null means the harvest could not place that stone, which is a different
        // thing from position 0.
        created: c.cr ? c.cr[i] : null,
        featOrder: c.fo ? c.fo[i] : null,
        bestOrder: c.bo ? c.bo[i] : null,
        // `cal` is absent on a shard built before the earring wave; `!!undefined`
        // is false, which is the safe direction — see RB.pairable.
        calibrated: !!(c.cal && c.cal[i]),
        // Which builders may offer this stone, as a bitmask (1 ring / 2 pendant
        // / 4 earring) written from the stone's own three builder tags. A shard
        // built before this column reads 0, and RB.eligibleFor treats 0 as
        // "unknown, allow" — an older asset must not empty every grid.
        waves: (c.w && c.w[i]) || 0,
        // HOW THIS STONE IS RENDERED INTO A MOUNTING. An index into
        // RB_STONES_META.curves, resolved in PYTHON at build time from the
        // stone's own `custom.gem_type` + `custom.gem_color` (see
        // stone_curves.py) — never from `species` below, which is the title's
        // trailing word and is a treatment fragment ("(Heated)") on 17% of
        // coloured stones.
        //
        // null is the SAFE, EXPECTED answer, not a hole: it is what a diamond
        // gets, what a colourless sapphire correctly gets, and what a species
        // with no harvested curve gets. RB.renderUrl then draws the centre as
        // `stones/diamonds`, which is what the builder shipped before curves
        // existed — so a shard built by an older run simply keeps the old
        // behaviour instead of breaking.
        curve: (function () {
          var m = window.RB_STONES_META, ix = c.cv && c.cv[i];
          if (!m || !m.curves || ix == null || ix < 0) return null;
          return m.curves[ix] || null;
        })(),
        available: !!c.av[i], shape: shard.shape, origin: shard.origin
      });
    }
    return out;
  };

  /* ------------------------------------------------- can this be a PAIR? --- */
  // 🚨 THE ONLY HONEST ANSWER IS THE ONE THE SUPPLY CHAIN GIVES.
  // A serialized stone is one of one — that is the definition of Stuller's /v2/gem
  // feed, and it is why a sold stone LEAVES the feed rather than going to zero. You
  // cannot order a second. Calibrated stock is the opposite: cut to a standard
  // millimetre and sold to a colour/clarity BAND (Stuller grades this stock "Ghi",
  // "F-G", "Si2-Si3" — a range, not a stone), so qty 2 of one SKU is consistent by
  // construction and needs nobody to match anything by hand.
  //
  // ⚠️ That distinction is exactly the owner's condition for pairing diamonds —
  // "calibrated, close in color and clarity" — and it is ALREADY what calibrated
  // means. Do NOT extend this to two serialized stones that merely look alike:
  // trade-matched means colour-paired by eye, which this business does not do, and
  // CATALOG_STATE records that as a promise not to make.
  //
  // Reads the shard's `cal` column, which build_builder_assets.py writes from the
  // product's own `calibrated` tag. Absent (an older shard) reads as NOT pairable,
  // so a stale asset makes the earring grid empty rather than making it wrong.
  RB.pairable = function (stone) { return !!(stone && stone.calibrated); };

  /* ───────────────────────────────────────────────── SIDE STONES ───────────
     The seat a style's SIDE stones sit in, for the centre seat the shopper has
     chosen — `{n, spec:'SHAPE|L|W', set}` or null.

     🚨 KEYED BY THE CENTRE SEAT, NEVER BY THE STYLE. One design is sold at many
     centre sizes and a bigger centre takes bigger sides: series 127205 runs its
     sides from Oval 4.0 mm to Oval 8.0 mm across its own SKUs. A per-style answer
     would put a 4 mm side on an 8 mm build — a stone that does not fit the seat
     being bought, and one the fit test could not catch because it would be
     comparing against the wrong number.

     🚨 null IS THE ANSWER FOR EVERY STYLE WE SELL TODAY, AND IT IS CORRECT.
     Measured 2026-08-19 over 28,260 onboarded SKUs across all three waves: **0**
     expose a Side location. Every three-stone design in the builder is the
     SEMI-SET SKU, whose sides are welded into the mounting — the empty-seat
     MOUNTING SKU of the same series is orderable from Stuller but none is
     onboarded. So this returns null, no step appears, and the builder behaves
     exactly as it does now. It lights up on DATA, with no code change. */
  RB.sideSeat = function (style, seatKey) {
    var s = RB.setting(style);
    if (!s || !s.sides || !seatKey) return null;
    return s.sides[seatKey] || null;
  };

  /* ══ A SIDE SEAT ALSO TAKES THE SHAPES THE SUPPLIER SETS IN IT (owner 2026-10-06) ══════════════════════════════
     Owner report on 127354 (Asscher three-stone): the supplier's own builder offers a Radiant 5.00 x 3.00 mm beside
     the Emerald for its Emerald side seats, and ours offered the Emerald alone, because every fit test matched the
     seat's one shape word. The table is side_shapes.py's, written into rb-stones-meta.js as `sideAlso` by the
     nightly build and READ HERE, never copied: absent or {} (the switch off, or a meta older than this) means the
     seat's own shape alone, exactly as before.
     ⚠️ SIDE SEATS ONLY. `RB.fits` and `RB.bestSeat` (the centre stone) still match one shape. */
  RB.sideShapes = function (side, style) {
    var s = RB.parseSeat(side && side.spec || side), shapes = [s.shape];
    var table = (window.RB_STONES_META || {}).sideAlso;
    // Legacy shape-wide tables carry no verified design context: fail closed.
    if (!side || !side.spec || !style || !Array.isArray(table)) return shapes;
    table.forEach(function (row) {
      if (row && String(style) === row.style && s.shape === row.shape && s.l === row.l && s.w === row.w &&
          side.set === row.set && Array.isArray(row.also)) {
        row.also.forEach(function (sh) { if (sh && shapes.indexOf(sh) < 0) shapes.push(sh); });
      }
    });
    return shapes;
  };

  /* The words for a side seat. With no stone, every shape it takes: "Emerald or Radiant 5.00 × 3.00 mm". With a
     stone, that stone's own shape: "Radiant 5.00 × 3.00 mm", so a line never names one shape beside a stone of
     another. A seat that takes one shape reads exactly RB.seatLabel, as it always has. */
  RB.sideSeatLabel = function (spec, stone, style, setting) {
    var s = RB.parseSeat(spec), shapes = RB.sideShapes({ spec: spec, set: setting }, style);
    if (stone && stone.shape && shapes.indexOf(stone.shape) >= 0) shapes = [stone.shape];
    var label = RB.seatLabel(RB.seatKey(shapes[0], s.l, s.w));
    if (shapes.length === 1) return label;
    var names = shapes.map(function (x) { return x.charAt(0) + x.slice(1).toLowerCase(); });
    return names.slice(0, -1).join(', ') + ' or ' + names[names.length - 1] + label.slice(names[0].length);
  };

  /* Every stone row of every shape a side seat takes, for one formation, the seat's own shape first. The side
     step's grid, its starting prices and its default all read the pool through here, so they cannot disagree
     about which stones a seat takes. */
  RB.loadSideRows = function (side, origin, style) {
    var shapes = RB.sideShapes(side, style);
    return Promise.all(shapes.map(function (sh) { return RB.loadShard(sh, origin); }))
      .then(function (shards) {
        return [].concat.apply([], shards.map(function (sh) { return RB.rows(sh); }));
      });
  };

  /* ══ ON FIVE DESIGNS THE ONE SIDE SEAT IS "THE SECOND STONE" (owner Q6, 2026-09-25) ══════════════
     "The step heading changes from 'Choose your side stones' to 'Choose your second stone', the cart
     line from 'Side stones × 1 (3 of 3)' to 'Second stone (3 of 3)', and 'matched set' is dropped,
     since it's one stone. Everything else stays as today's side-stone step" -- for these rings only:
     the toi et moi designs whose partner the shopper chooses (126978, 126751) and the two-centre
     designs (127348, 123099, 689651), each sold through this step with a one-stone seat
     (docs/handoffs/ring-builder-second-stone.md). Every other build keeps the side-stone words.
     ⚠️ TRUE ONLY FOR A SEAT OF ONE. A listed style whose seat took more stones would be named as one
     stone, so it keeps the side-stone words, which at least count right.
     ⚠️ THE OTHER BUILDER FILES CALL THIS GUARDED (`RB.secondStone && ...`): for ~20 minutes after a
     release a browser can pair a new page script with this file's previous copy, and a missing
     function must read as "side stones", never as an error. */
  RB.SECOND_STONE_STYLES = ['126978', '126751', '127348', '123099', '689651'];
  RB.secondStone = function (style, side) {
    return !!(side && side.n === 1) && RB.SECOND_STONE_STYLES.indexOf(String(style)) >= 0;
  };

  /* Does this stone fit the SIDE seat?
     ⚠️ `RB.fits(stone, seatKey, style)` takes a STYLE as its third argument, not a
     setting word — it resolves the tolerance through `RB.tolFor(style)`. Passing
     `side.set` there would hand "Prong" to a style lookup, `RB.setting('Prong')`
     returns undefined, and every side seat would silently grade on the DEFAULT row
     — the exact defect owner report 2026-08-18 was about, reintroduced one seat
     over. The style is passed, so a style whose sides are bezel-set grades on the
     bezel row for the same reason its centre does. */
  RB.fitsSide = function (stone, side, style) {
    if (!stone || !side) return false;
    /* A stone of a shape this seat also takes (RB.sideShapes, owner 2026-10-06) is measured against the seat's own
       millimetres and tolerance, exactly as a stone of the seat's own shape is. */
    var s = RB.parseSeat(side.spec);
    if (stone.shape && stone.shape !== s.shape && RB.sideShapes(side, style).indexOf(stone.shape) >= 0) {
      return RB.fits(stone, RB.seatKey(stone.shape, s.l, s.w), style);
    }
    return RB.fits(stone, side.spec, style);
  };

  /* 🚨 A SIDE STONE MUST BE CALIBRATED, AND ON THE RING WAVE THAT IS A NEW RULE.
     The sides of one mounting are identical to each other — 7,916 of 7,916 two-side
     SKUs in the cohort — so the builder orders ONE stone N times. A one-of-one
     serialized stone cannot be supplied twice: Stuller confirms the order and it
     arrives short. `RB.pairable` already encodes this for the earring wave, where
     `pairQty` is 2; here it has to fire on a RING, where `pairQty` is 1 and that
     guard has therefore never run. */
  /* 🚨 A DEFAULT DIAMOND MUST BE COLOURLESS, AND "IS IT A DIAMOND" DOES NOT ANSWER
     THAT. Owner rule 2026-08-20: the side stones default to diamonds. The cheapest
     diamond that fits the smallest side seat is a **BLACK OPAQUE** one — Stuller
     sells colour-enhanced blacks in exactly these melee sizes, and they undercut
     every white stone at every seat (measured: $65 against $105 at 4x3, $75 against
     $325 at 5x3). So a plain `species === 'Diamond'` + cheapest rule would have
     defaulted 47 of 135 centre sizes to a black three-stone ring, which is a design
     statement nobody chose.
     The tell is the COLOUR GRADE, not the title: a graded stone reads "GHI" or
     "F-G", a black one reads "Black" with clarity "Opaque". So the test is that
     every letter sits on the D-Z ladder and there are at most three of them —
     "GHI" passes, "F-G" passes (the hyphen is stripped), "Black" fails on both
     counts (B is below D, and it is five letters).
     ⚠️ IT LIVES IN THE ENGINE, NOT IN ring-builder-stones.js. That file has
     `colIndex`/`isFancy` and would be the obvious home — but it is loaded only on
     the two STONE steps, and the caller here is the REVIEW page. The same boundary
     that took the page down when `colourLabel` was exported from the wrong file. */
  RB.isColourlessDiamond = function (r) {
    if (!r || r.species !== 'Diamond') return false;
    var L = String(r.color || '').replace(/[^A-Za-z]/g, '').toUpperCase();
    if (!L || L.length > 3) return false;
    for (var i = 0; i < L.length; i++) {
      if (L[i] < 'D' || L[i] > 'Z') return false;
    }
    return true;
  };

  /* The side stone a build gets when the shopper has not chosen one.
     Cheapest colourless diamond that fits the seat, preferring the build's own
     formation.
     ⚠️ IT FALLS BACK ACROSS FORMATION, AND SAYS SO RATHER THAN HIDING IT. Natural
     has no colourless diamond at OVAL 7x5 (9 of 135 centre sizes) — only the black
     one — while lab covers all five sizes at $55-$395. Refusing to cross would leave
     those 9 with no default at all; crossing is safe because the review line and the
     cart both print the stone's full title, which begins "Lab-Grown". A substitution
     the shopper can read is not a silent one.
     ⚠️ CHEAPEST, NOT BEST. A default is the base the price is quoted from, and the
     shopper can trade up in one click. Picking the nicest stone would quote a ring
     dearer than the one they get by doing nothing. */
  RB.defaultSideStone = function (build, side) {
    if (!build || !side || !build.style) return Promise.resolve(null);
    /* ⚠️ THE SEAT'S OWN SHAPE FIRST, IN BOTH FORMATIONS, AND ONLY THEN ANOTHER SHAPE IT TAKES (owner 2026-10-06).
       The ring picture draws the seat's own cut, and every build that has a default today keeps that default and
       that price; another shape (a Radiant in an Emerald seat) is the default only where the seat's own shape has
       no colourless diamond in either formation. */
    var shapes = RB.sideShapes(side, build.style);
    var mine = build.origin === 'lab' ? 'lab' : 'nat';
    var other = mine === 'lab' ? 'nat' : 'lab';
    var pick = function (shard) {
      var rows = RB.rows(shard).filter(function (r) { return r.available; });
      var c = RB.sideCandidates(rows, side, build.style)
                .filter(RB.isColourlessDiamond);
      c.sort(function (a, b) { return a.price - b.price; });
      return c[0] || null;
    };
    var search = function (shape, origin) {
      return RB.loadShard(shape, origin).then(function (shard) {
        if (shard) return { loaded: true, stone: pick(shard) };
        var meta = window.RB_STONES_META || {}, key = shape + '|' + origin;
        // rb-stones-meta.js IS the shard census: the nightly writes a shard only for a (shape, formation) pair that
        // has stones, so a pair absent from meta.shards is a COMPLETED empty answer and the search goes on to the
        // other formation, exactly as it did before this release. A shard that is LISTED but did not load is a
        // failed load and never authorizes substitution. No meta census at all means unknown. (Claude review of
        // codex/integrate-consumers-1009, finding 2: a missing sideShardCounts key used to read as "failed" and
        // builds ended with no side stones, Add to cart disabled.)
        var empty = meta.shards && !meta.shards[key];
        return { loaded: !!empty, stone: null };
      });
    };
    var at = function (i) {
      if (i >= shapes.length) return Promise.resolve(null);
      return search(shapes[i], mine).then(function (own) {
        if (!own.loaded || own.stone) return own.stone;
        return search(shapes[i], other).then(function (otherResult) {
          if (!otherResult.loaded || otherResult.stone) return otherResult.stone;
          return at(i + 1);
        });
      });
    };
    return at(0);
  };

  /* Fill an unchosen side seat with the default, in `build` and in the URL.
     Resolves to the stone it chose, or null if it changed nothing.

     🚨 ONE APPLIER, CALLED FROM TWO PAGES, BECAUSE THE DEFAULT HAS TO EXIST BEFORE
     THE SHOPPER SEES EITHER OF THEM. Review needs it so a deep link prices a whole
     ring; step 3 needs it so the grid opens with the default already ticked and
     "Continue" means something. Two copies would drift into two different defaults
     for one build — the `chain_fit` failure, one screen apart.
     ⚠️ THE URL IS THE STORE. `RB.addToCart` refuses an unfilled mounting and every
     link re-reads the query string, so a default held in a variable prices a ring the
     cart then refuses and vanishes on reload. `replaceState`, never a navigation: the
     shopper did not ask to move, and a reload would restart the resolve. */
  RB.ensureDefaultSide = function (build) {
    if (!build || build.side || !build.style || !build.seat) return Promise.resolve(null);
    var seat = RB.sideSeat(build.style, build.seat);
    if (!seat) return Promise.resolve(null);
    return RB.defaultSideStone(build, seat).then(function (st) {
      if (!st || build.side) return null;
      build.side = String(st.vid);
      /* 🚨 PATCH THE URL, NEVER MINT IT. This wrote `RB.href(path, build)`, which
         serialises FIELDS — and `for` is deliberately NOT a field, so applying the
         default silently STRIPPED `for=side` from the address bar. The page kept
         working (`forSide` was already resolved) and everything built from
         `location.search` afterwards did not: the Lab-Grown link on step 3 lost side
         mode and led to the ordinary centre grid, and a refresh left side mode
         entirely. Same defect as the facet links that minted their own href and
         destroyed every param they did not name (2026-08-17) — one screen over.
         Setting the one key we own leaves `for`, and every filter, untouched. */
      try {
        var q = new URLSearchParams(location.search);
        q.set('side', build.side);
        history.replaceState(null, '', location.pathname + '?' + q.toString()
                                       + location.hash);
      } catch (e) { /* a blocked replaceState must not cost the default */ }
      return st;
    });
  };

  /* ══ A SIDE STONE IS ONLY VALID FOR THE SEAT IT WAS CHOSEN FOR (2026-09-02) ═══
     Codex review, verified live: change the centre stone after picking side stones
     and the old `side` rides along in the URL. The review page then printed the NEW
     seat's dimensions beside the OLD stone's title ("0.31 ct … Emerald Cut · 2 ×
     Emerald 3.75 × 2.50 mm" over a 5.00 × 3.00 stone), Add to cart was live, and
     `addToCart` tested only that the side was calibrated — so a shopper could pay
     for a ring the bench cannot make. This is "A CARRIED PARAM IS ONLY VALID FOR
     THE STYLE IT CAME FROM" (2026-08-17) one level down: the seat.
     ⚠️ `style` IS PASSED SO THE TOLERANCE CLASS MATCHES THE GRID'S OWN GATE —
     `sideCandidates` calls `fits(r, side, style)`; a bare `fits(r, spec)` would
     judge a claw-prong seat by the tight default row and refuse stones the grid
     had just offered. */
  RB.sideFits = function (build, stone) {
    if (!build || !stone) return false;
    var seat = RB.sideSeat(build.style, build.seat);
    if (!seat) return true;                       // no side seat: nothing to fit
    // RB.fitsSide, the grid's own test: a Radiant offered for an Emerald seat must not be dropped here
    return !!RB.fitsSide(stone, seat, build.style);
  };

  /* Drop a side stone that no longer fits the build's own seat — from `build` AND
     from the URL, patched not minted (the `ensureDefaultSide` rule). Returns true
     if it dropped one. Callers then re-run `ensureDefaultSide`, so the seat the
     shopper actually has gets its own named default rather than a stale one.
     ⚠️ ONLY ON A RESOLVED STONE. An unresolvable vid (sold, stale link) is not
     proof of a misfit — "cannot tell" must not become "drop", the same `is not
     True` discipline the hover-frame repair records. */
  RB.dropSide = function (build) {
    if (!build || !build.side) return false;
    build.side = null;
    try {
      var q = new URLSearchParams(location.search);
      q.delete('side');
      history.replaceState(null, '', location.pathname + (q.toString() ? '?' + q.toString() : '')
                                     + location.hash);
    } catch (e) { /* a blocked replaceState must not keep a stone that is not there */ }
    return true;
  };
  RB.normalizeSide = function (build, stone) {
    if (!build || !build.side || !stone) return false;
    if (RB.sideFits(build, stone)) return false;
    return RB.dropSide(build);
  };
  /* 🚨 A SIDE THAT RESOLVES TO NOTHING IS NOT A SIDE. `normalizeSide` deliberately
     refuses to judge FIT on an unresolved row — but a vid that `resolveStone` cannot
     find after trying every shard is a stone that has LEFT the feed (sold; a stale
     link), and that is a fact, not a "cannot tell". Left in `build`, the truthy vid
     satisfied every "is a side chosen" test while no stone stood behind it: review
     enabled Add to cart over nothing and step 3 kept Continue live (Codex, third
     pass, 2026-09-02). Callers use this after a resolve: drop on null, normalise on a
     row — then re-run `ensureDefaultSide` either way. */
  RB.settleSide = function (build, stone) {
    if (!build || !build.side) return false;
    return stone ? RB.normalizeSide(build, stone) : RB.dropSide(build);
  };

  /* ══ THE TWO DOORS OUT OF STEP 2 (owner, 2026-08-20) ═══════════════════════════
     "On step 2, there should be two options before proceeding: proceed with default
     side stones, or select side stones."

     🚨 THIS SUPERSEDES "STEP 3 IS NOT SKIPPABLE", AND THE OLD REASONING STILL HOLDS
     — it is the PREMISE that moved. That rule was written because choosing the centre
     stone landed on REVIEW, which then quietly filled the sides: "a step you are shown
     and can pass through in one click is a choice; one that happens without you is
     not." Correct, and it argued against a SILENT default. The default is not silent
     here — it is named, priced and declined on the screen before it applies, which is
     the same test passed one step earlier. What is no longer true is that the only way
     to show it is to route through the step.
     ⚠️ SO THE DEFAULT MUST BE RESOLVED BEFORE THE DOOR IS DRAWN. Offering "proceed
     with default side stones" without knowing there IS one is how the shopper reaches
     review with two empty seats and an Add to cart that refuses — `defaultSideStone`
     returns null wherever no colourless diamond fits the seat, and on those builds
     this returns null and the caller must fall back to the single "choose" CTA.
     ⚠️ FORMATION FOLLOWS THE CENTRE STONE and is not asserted here: that is
     `defaultSideStone`'s rule (prefer `build.origin`, cross only where the formation
     has no colourless diamond at all). Restating it would be a second copy of it. */
  /* WHICH GRID THE SIDE PICKER OPENS ON — the lab page for a lab build.
     🚨 `origin` IS THE ONE PARAM THAT DOES NOT TRAVEL. The target page sets it from
     its own `data-rb-origin` section setting, so `?origin=lab` on the NATURAL page
     resolves to natural — and the side step then re-defaults the build to a natural
     stone. Measured on style 127354 at Emerald 5.00 x 3.00: the lab default is $210
     the set and the natural one is $1,210, so "Select side stones myself" quietly
     multiplied the price by 5.7 and showed a grid the named default was not even in.
     ⚠️ ONE HELPER, EVERY ENTRY. The step bar, the review "Change" link, the mounting
     page and step 2 all open this step; four copies of the choice would drift, and
     three of them landing on the natural grid is what this is fixing.
     ⚠️ It follows `build.origin` and NOT the default stone's own formation, which may
     have crossed (`defaultSideStone` falls back where a formation has no colourless
     diamond at all). The shopper asked for a lab ring; the grid should open where
     they can act on that, and the crossed default stays ticked either way. */
  /* WHICH CENTRE-STONE GRID A LINK OPENS. Six sites spelled this inline as
     `RB.stonesPage(build)` and a seventh —
     the mounting page's "Choose a gemstone" CTA — hardcoded the NATURAL grid, so a
     lab shopper pressing it landed on natural stones while the step bar beside it
     went to lab (Codex, 2026-09-02). One helper; the copies are gone. */
  /* ══ A CLOSED DRAWER IS OUT OF THE TAB ORDER — ON BOTH GRIDS, AND ACROSS A RESIZE ═
     The filter rail is translated off-screen when closed and was still fully
     tabbable (Codex, 2026-09-02). Drawer mode is a fact about the CSS breakpoint
     (`position: fixed` under 64rem), not the viewport width, so it is read off the
     element — a desktop sidebar must never be inerted. ONE helper for the settings
     grid and the stone grid: the first fix lived in the stone grid alone, and a
     resize across the breakpoint left the state stale in both directions (a widened
     sidebar still inert; a narrowed drawer still tabbable) — hence the watcher. */
  RB.syncDrawerA11y = function (rail, open) {
    if (!rail) return;
    var drawer = getComputedStyle(rail).position === 'fixed';
    var hide = drawer && !open;
    rail.inert = hide;
    if (hide) rail.setAttribute('aria-hidden', 'true'); else rail.removeAttribute('aria-hidden');
  };
  RB.watchDrawerA11y = function (rail, isOpen, onLeaveDrawer) {
    if (!rail || rail.__rbA11yWatched) return;
    rail.__rbA11yWatched = true;
    var t = null;
    var resync = function () {
      clearTimeout(t);
      t = setTimeout(function () {
        /* 🚨 AN OPEN DRAWER THAT BECOMES A SIDEBAR MUST BE CLOSED PROPERLY, not just
           un-inerted: `setOpen(true)` also hid the scrim, locked `body` scroll and
           trapped focus, and a resync of inert alone left the desktop page
           scroll-locked (Codex, 2026-09-02). The caller's own `setOpen(false)` is the
           one thing that knows how to undo all of it. */
        if (getComputedStyle(rail).position !== 'fixed' && isOpen() && onLeaveDrawer) onLeaveDrawer();
        RB.syncDrawerA11y(rail, isOpen());
      }, 120);
    };
    window.addEventListener('resize', resync);
    /* ⚠️ AND THE BREAKPOINT ITSELF. `resize` is the event a real window fires; the
       drawer/sidebar split is a media query (`width < 64rem`), and `matchMedia`'s
       `change` fires exactly when that flips — including on an orientation change
       some browsers deliver without a resize. Two triggers, one idempotent sync. */
    try {
      var mq = window.matchMedia('(max-width: 1023.98px)');
      if (mq && mq.addEventListener) mq.addEventListener('change', resync);
      else if (mq && mq.addListener) mq.addListener(resync);
    } catch (e) { /* no matchMedia: resize alone */ }
  };

  RB.stonesPage = function (build) {
    if (RB.kind(build) === 'earring' && !(build && build.origin)) return RB.pages.stonesLab;
    return (build && build.origin === 'lab') ? RB.pages.stonesLab : RB.pages.stones;
  };

  RB.sidePage = function (build) {
    return (build && build.origin === 'lab') ? RB.pages.stonesLab : RB.pages.stones;
  };

  RB.sideChoice = function (build, seat) {
    if (!build || !seat) return Promise.resolve(null);
    return RB.defaultSideStone(build, seat).then(function (st) {
      if (!st) return null;
      /* 🚨 `n` IS NOT ALWAYS 2 AND THE QTY IS NOT ALWAYS 1. The seat takes `n`
         (1 on ten designs, 4 on two) and an earring build orders `pairQty` of the
         whole thing, so a hardcoded "pair" misprices twelve designs — the same
         arithmetic the cart and the review line already run. */
      var n = (seat.n || 2) * RB.pairQty(build);
      return { stone: st, n: n, seat: seat,
               seatLabel: RB.sideSeatLabel(seat.spec, st, build.style, seat.set), total: st.price * n };
    });
  };

  /* The block both doors are drawn in. ONE renderer, because the stone detail page
     and the mounting detail page are two entries to the same decision — two copies
     would drift into two different descriptions of one default, which is the
     `chain_fit` failure with copy instead of arithmetic.
     `extra` carries the per-page overrides the href needs (the snapped seat, the
     stone just chosen); everything else rides on `build`. */
  RB.renderSideChoice = function (build, choice, extra) {
    extra = extra || {};
    var withSide = {}, k;
    for (k in extra) if (extra.hasOwnProperty(k)) withSide[k] = extra[k];
    /* `side` IS in FIELDS, so RB.href serialises it and the whole ring survives a
       share, a reload and the back button. A default held in a variable would price
       a ring `RB.addToCart` then refuses — the rule `ensureDefaultSide` states. */
    withSide.side = String(choice.stone.vid);
    withSide['for'] = null;
    var pickExtra = {}, j;
    for (j in extra) if (extra.hasOwnProperty(j)) pickExtra[j] = extra[j];
    // `for` is a MODE and lives outside FIELDS, so the side grid has to be named.
    pickExtra['for'] = 'side';

    // ONE stone on a second-stone design (RB.secondStone): named as one, and never "the set"
    var one = RB.secondStone(build.style, choice.seat) && choice.n === 1;
    return el('div', { class: 'rb-sidepick' }, [
      el('p', { class: 'rb-sidepick__k', text: one ? 'Second stone' : 'Side stones' }),
      el('p', { class: 'rb-sidepick__t', text: choice.stone.title }),
      el('p', { class: 'rb-sidepick__s',
        text: one ? [choice.seatLabel, money(choice.total)].join(' · ')
                  : [choice.n + ' × ' + choice.seatLabel,
                     money(choice.total) + ' the set'].join(' · ') }),
      el('a', { class: 'btn btn-primary rb-sidepick__go',
                href: RB.href(RB.pages.review, build, withSide),
                text: one ? 'Proceed with this second stone →' : 'Proceed with these side stones →' }),
      el('a', { class: 'rb-sidepick__a',
                href: RB.href(RB.sidePage(build), build, pickExtra),
                text: one ? 'Select the second stone myself' : 'Select side stones myself' })
    ]);
  };

  /* What a set of side stones STARTS at, per route the shopper can take.
     -> {natDia, labDia, colour} in dollars for the whole set, any of them null.

     🚨 THE PAIR, NOT THE STONE. The seat takes `n` and the cart charges for `n`, so a
     per-stone floor halves the number they meet on the next screen.
     🚨 BOTH FORMATIONS, BECAUSE THE GAP IS THE WHOLE POINT (owner, 2026-08-20: "as
     long as there is a starting price and the user sees the option to select lab").
     A natural colourless oval costs what it costs — $575 each at 6x4 — while the lab
     equivalent is $85. Quoting only the formation currently loaded would print
     "Diamonds from $1,150" over a page where $170 is one click away, which is true
     and reads as a lie. */
  RB.sideFloors = function (build, side) {
    var n = side.n || 2;
    var cheapest = function (rows, pred) {
      var c = RB.sideCandidates(rows, side, build.style).filter(pred);
      c.sort(function (a, b) { return a.price - b.price; });
      return c[0] ? c[0].price * n : null;
    };
    var notDia = function (r) { return r.species !== 'Diamond'; };
    var mine = build.origin === 'lab' ? 'lab' : 'nat';
    // every shape the seat takes (RB.loadSideRows), so the floor is the cheapest stone the grid below offers
    return Promise.all([RB.loadSideRows(side, 'nat', build.style), RB.loadSideRows(side, 'lab', build.style)])
      .then(function (s) {
        var nat = s[0].filter(function (r) { return r.available; });
        var lab = s[1].filter(function (r) { return r.available; });
        return {
          natDia: cheapest(nat, RB.isColourlessDiamond),
          labDia: cheapest(lab, RB.isColourlessDiamond),
          // colour is quoted for the formation on screen, because that is the grid
          // the shopper is looking at
          colour: cheapest(mine === 'lab' ? lab : nat, notDia)
        };
      });
  };

  RB.sideCandidates = function (rows, side, style) {
    if (!side) return [];
    return (rows || []).filter(function (r) {
      /* 🚨 THE SIDE BIT IS THE PAIRABILITY GATE, AND `RB.pairable` IS NOT.
         `RB.pairable(stone)` returns `stone.calibrated` — "is this a calibrated
         line?", not "do they have two?" — and 137 of 5,492 calibrated stones (2.5%)
         have fewer than 2 on hand. Every one of those would be offered as a matched
         pair and Stuller would confirm the order and ship it short.

         `tag_side_stones.py` reads `on_hand` out of the calibrated caches and only
         tags a stone it can supply TWICE at a live side seat, so the bit carries
         that fact and is refreshed nightly. That is why the fix is a tag and not a
         shard column: stock is the one thing that must never be cached (drilling is
         a property of the object and caches fine — stock goes stale into an order
         that arrives short). `pairable` stays as a belt-and-braces second gate for
         a shard built before the bit existed, where `waves` is 0 and eligibleFor
         waves everything through. */
      return r.available !== false && RB.eligibleFor(r, 'side') && RB.pairable(r) &&
             RB.fitsSide(r, side, style);
    });
  };

  /* -------------------------------------- may THIS wave offer this stone? --- */
  // 🚨 THE POOL AND THE BUTTON MUST READ THE SAME THREE TAGS.
  // `stone-set-into.liquid` decides which builder buttons a stone product page
  // shows from `ring-` / `pendant-` / `earring-builder-stone`. Until 2026-08-05
  // the shard was built from the RING tag alone, so 320 stones advertised a
  // builder that could not find them: `resolveStone` came back null and the step
  // bar printed a bare "Selected". The shard now carries all three cohorts and
  // this is the filter that keeps each flow to its own.
  //
  // ⚠️ THIS IS NOT A FIT TEST AND DOES NOT REPLACE ONE. With a mounting chosen,
  // `RB.bestSeat` is still what decides whether the stone physically sits in it.
  // This answers the question a STONE-FIRST grid has to answer before any
  // mounting exists: which stones can become one of these at all.
  //
  // A 0 mask means the shard predates the column. Allow it rather than filtering
  // it out — a stale asset should look like the old builder, not like an empty one.
  RB.eligibleFor = function (stone, kind) {
    if (!stone) return false;
    if (kind === 'earring') {
      var releaseIds = (window.RB_SETTINGS || {}).earringStoneIds || [];
      if (releaseIds.indexOf(String(stone.vid)) < 0) return false;
    }
    // `side` (8) is a ROLE, not a wave: it says "we can supply two of these for a
    // live side seat", and a stone carrying only that bit is deliberately invisible
    // to every centre grid.
    var bit = { ring: 1, pendant: 2, earring: 4, side: 8 }[kind || 'ring'] || 1;
    return !stone.waves || !!(stone.waves & bit);
  };

  /* -------------------------------------------------- resolve a stone --- */
  // The build carries only the stone's VARIANT ID (that is all the cart needs).
  // Everything shown about it — title, price, cert, spin video, durability — has
  // to be looked up in its shard, which is keyed by shape + origin. Both are in
  // the build, so this is one shard fetch, not a search across all twenty.
  //
  // 🚨 A SHAPE IN THE BUILD IS A HINT, NOT A KEY. It used to be treated as one —
  // `shape ? [[shape, origin]] : []` — so a single wrong guess ended the search and
  // the stone came back null. Both ways of being wrong are ordinary, not exotic:
  //   - `origin` is a separate URL field, so `?stone=…&shape=EMERALD` with no
  //     `origin` looks in EMERALD|nat for a LAB stone and misses;
  //   - `shape` is ALSO the setting's centre-stone shape, and step 1b writes its
  //     own into the URL, so a build that picked a Round mounting then looked its
  //     Emerald stone up in the ROUND shard.
  // A null here does not read as "lookup failed" anywhere downstream — it reads as
  // "no stone chosen", which is how the review page came to quote a total with a
  // $3,065 diamond missing from it. So the hint is tried FIRST and the remaining
  // shards are a fallback, in order: stated origin, then the other origin of the
  // same shape (one more fetch, same shape), then everything else.
  //
  // The full scan is up to 2 MB, which is why `stone-set-into.liquid` emits shape
  // AND origin — with both correct this stops at the first fetch and the tail is
  // never walked. It is paid only by a legacy link, a hand-edited URL, or a stone
  // that has left the shard entirely (sold), and paying it beats a silent null.
  RB.resolveStone = function (build) {
    if (!build.stone) return Promise.resolve(null);
    var meta = window.RB_STONES_META || { shards: {} };
    var tries = [];
    var push = function (shape, origin) {
      if (!shape || !meta.shards[shape + '|' + origin]) return;
      if (tries.some(function (t) { return t[0] === shape && t[1] === origin; })) return;
      tries.push([shape, origin]);
    };
    var stated = build.origin === 'lab' ? 'lab' : 'nat';
    push(build.shape, stated);
    push(build.shape, stated === 'lab' ? 'nat' : 'lab');
    Object.keys(meta.shards).forEach(function (k) {
      var p = k.split('|');
      push(p[0], p[1]);
    });
    var want = String(build.stone);
    var i = 0;
    function next() {
      if (i >= tries.length) return Promise.resolve(null);
      var t = tries[i++];
      return RB.loadShard(t[0], t[1]).then(function (shard) {
        var hit = RB.rows(shard).filter(function (r) { return String(r.vid) === want; })[0];
        return hit || next();
      });
    }
    return next();
  };

  /* ------------------------------------------------- seat follows stone --- */
  // THE SEAT IS DERIVED FROM THE STONE, NOT CHOSEN BEFORE IT.
  // The customer picks a seat in step 1b, long before they know which stone they
  // want. If they then change shape in step 2 — which the shape chips allow,
  // because a style offers many shapes — the seat stayed on the old shape and
  // the build went to the cart as, literally, an Emerald 10x7 mounting holding
  // an Asscher stone. That is an order Stuller cannot make. So the moment a
  // stone is chosen, snap the seat to the best seat of THAT stone's shape.
  RB.seatsByShape = function (style) {
    var s = RB.setting(style);
    if (!s) return {};
    var by = {};
    s.seats.forEach(function (k) { (by[RB.parseSeat(k).shape] = by[RB.parseSeat(k).shape] || []).push(k); });
    return by;
  };

  RB.bestSeat = function (style, stone) {
    if (!stone) return null;
    var cands = (RB.seatsByShape(style)[stone.shape] || []).filter(function (k) {
      return RB.fits(stone, k, style);
    });
    if (!cands.length) return null;
    // tightest fit wins: the seat whose length is closest to the stone's
    return cands.sort(function (a, b) {
      return Math.abs(RB.parseSeat(a).l - stone.l) - Math.abs(RB.parseSeat(b).l - stone.l);
    })[0];
  };

  // A stone is compatible with a STYLE if any seat of that style can hold it.
  RB.fitsStyle = function (style, stone) { return !!RB.bestSeat(style, stone); };

  // THE SEAT A STYLE IS SHOWN AT WHEN NOBODY HAS PICKED ONE — the size the grid
  // tile draws and the size the setting page opens on. It was written inline in
  // pageSettingDetail and is now here because a SECOND consumer arrived: the
  // step-1 tile, whose das ids are baked per (style, shape) by
  // build_builder_assets.py using this same rule. Two copies of it would show one
  // ring in the grid and a different one on the screen the tile clicks into, and
  // nothing would error — so verify_setting_tiles.py executes this function
  // against the shard and fails on any disagreement.
  //
  // ⚠️ NUMERIC sort. The index ships seats string-sorted, which puts 10/11/13/15
  // mm before 4.1 mm; the middle of that order is not a mid-range stone.
  // ⚠️ MID-RANGE, NOT THE SMALLEST. The smallest seat on most styles is a ~4 mm
  // accent size nobody centres a ring on, and a grid of 51 rings wearing 4 mm
  // stones reads as a catalogue of cheap ones.
  RB.defaultSeat = function (style, shape) {
    var s = RB.setting(style);
    if (!s) return null;
    var ks = s.seats.filter(function (k) { return RB.parseSeat(k).shape === shape; })
      .sort(function (a, b) {
        var pa = RB.parseSeat(a), pb = RB.parseSeat(b);
        return (pa.l - pb.l) || (pa.w - pb.w);
      });
    return ks[Math.floor(ks.length / 2)] || ks[0] || null;
  };

  /* --------------------------------------------------- compatibility ---- */
  // FAITHFUL MIRROR OF stuller_settings.TOLERANCE_CLASS. Undersize is more
  // forgiving than oversize because a stone larger than the seat physically will
  // not sit; a smaller one the bench closes the metal onto.
  //
  // 🚨 THIS USED TO BE ONE FLAT PAIR — the table's DEFAULT row — applied to all 51
  // ring styles, while its comment claimed to mirror a three-row table. It could
  // not do better: the index shipped seats as bare SHAPE|L|W with nothing saying
  // how they grip. So a PRONG seat, which Python grades at 0.25mm undersize, was
  // graded at 0.15 in the browser, and the two halves of the same rule disagreed:
  // `builder_routes.evaluate` tagged a stone `ring-builder-stone` and its page
  // said "Can be set into a Ring", and then the grid refused to show it on any
  // mounting. 1,713 of 5,873 ring-wave stones were in that state — $2.7M of list
  // price — and the owner found it on a 3.53ct that missed a 10.0mm seat by
  // 0.16mm against a 0.15mm allowance. One hundredth of a millimetre.
  //
  // ⚠️ `_` IS NOT A CLASS, IT IS THE ABSENCE OF ONE, and it must stay the TIGHT
  // default. A style with no tolerance class (the pendant and earring waves, whose
  // harvests carry no setting word) keeps exactly today's behaviour. Defaulting to
  // `prong` instead would silently loosen two whole waves nobody measured.
  // ⚠️ Change these numbers in stuller_settings.py, never here —
  // verify_builder_fit.py executes this function against that module and fails on
  // any drift, including a call site that forgets to pass `style`.
  var TOL = {
    prong: { under: 0.25, over: 0.10 },
    bezel: { under: 0.10, over: 0.05 },
    _:     { under: 0.15, over: 0.08 }
  };

  RB.tolFor = function (style) {
    var s = (style == null) ? null : RB.setting(style);
    return (s && TOL[s.tol]) || TOL._;
  };

  RB.fits = function (stone, seatKey, style) {
    var s = RB.parseSeat(seatKey), t = RB.tolFor(style);
    if (!stone.l || stone.shape !== s.shape) return false;
    var sw = stone.w || stone.l;
    return (stone.l - s.l) <= t.over && (s.l - stone.l) <= t.under &&
           (sw - s.w) <= t.over && (s.w - sw) <= t.under;
  };

  /* --------------------------------------------------------- 360 spin ---- */
  // Frame sequences are generated by stone_spin.py. A stone with no video (all
  // calibrated lab color, plus 2 serialized stones with no media at Stuller)
  // falls back to its still — the viewer must never render an empty box.
  RB.mountSpin = function (stage, frames, opts) {
    opts = opts || {};
    if (!frames || frames.length < 2) return null;
    var imgs = frames.map(function (src, i) {
      var im = el('img', { src: src, alt: '', draggable: 'false' });
      if (i === 0) im.className = 'on';
      stage.appendChild(im);
      return im;
    });
    var N = frames.length, cur = 0, dragging = false, x0 = 0, f0 = 0, touched = false;

    function show(i) {
      var n = ((i % N) + N) % N;
      if (n === cur) return;
      imgs[cur].classList.remove('on');
      imgs[n].classList.add('on');
      cur = n;
    }
    function touch() { if (!touched) { touched = true; stage.classList.add('is-touched'); } }

    stage.addEventListener('pointerdown', function (e) {
      dragging = true; x0 = e.clientX; f0 = cur; touch();
      stage.classList.add('is-drag'); stage.setPointerCapture(e.pointerId);
    });
    stage.addEventListener('pointermove', function (e) {
      if (!dragging) return;
      show(f0 - Math.round((e.clientX - x0) / (stage.clientWidth * 0.85) * N));
    });
    ['pointerup', 'pointercancel'].forEach(function (ev) {
      stage.addEventListener(ev, function (e) {
        dragging = false; stage.classList.remove('is-drag');
        if (e.pointerId != null && stage.hasPointerCapture(e.pointerId)) stage.releasePointerCapture(e.pointerId);
      });
    });
    // keyboard parity: the spin must be operable without a pointer
    stage.setAttribute('tabindex', '0');
    stage.setAttribute('role', 'img');
    stage.setAttribute('aria-label', opts.label || 'Drag or use arrow keys to rotate');
    stage.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowRight') { touch(); show(cur + 1); e.preventDefault(); }
      if (e.key === 'ArrowLeft') { touch(); show(cur - 1); e.preventDefault(); }
    });
    return { show: show, frames: N };
  };

  /* ------------------------------------------------------------- 5 angles */
  // THE URL HAS TO NAME EVERY OBJECT TO DRAW. The old fixed template asked for
  // metal + `g_Center` only, so every halo / three-stone / accented setting was
  // drawn WITHOUT its melee — a plainer ring than the customer is buying, on the
  // last screen before Add to cart. The obj list is Stuller's own, harvested from
  // FullySetImages by pull_setting_renders.py.
  //
  // These names are NOT guessable and must never be hardcoded here: the accent on
  // a three-stone is `g_Side`, and `g_Accent` — the obvious guess — returns a
  // valid 200 JPEG identical to the centre-only render, so a wrong name fails
  // silently and looks fixed. Change the harvest, not this file.
  //
  // Falls back to the fixed template when there is no list, which is what lets
  // this ship before the regenerated shard does.
  // 🚨 SPACES MUST GO OUT AS %20, NEVER AS '+'. Four object names contain a space
  // (`g_Accent 1` … `g_Accent 4`). `%20` and a raw space both render correctly;
  // '+' returns a valid 200 JPEG with the accents SILENTLY MISSING. So the space is
  // encoded here explicitly rather than left to whatever touches the URL next —
  // anything form-encoding it (URLSearchParams, a query builder) would produce '+'
  // and quietly reintroduce this exact bug. Only ' ' is encoded: the names also
  // contain '/', which must stay literal.
  // THE CENTRE STONE'S OWN COLOUR (2026-08-12). `curve` is the stone's entry
  // from RB_STONES_META.curves — a list of [key, value] pairs harvested from
  // Stuller's own coloured-stone products by stone_curves.py. Given one, the
  // CENTRE object is swapped from `stones/diamonds/g_Center` to
  // `stones/faceted/g_Center` and the curve pairs are emitted IMMEDIATELY after
  // it; every other object in the list is left as `stones/diamonds`, so the
  // halo and accents stay white.
  //
  // 🚨 THE PAIRS MUST FOLLOW THE CENTRE OBJECT AND NOTHING ELSE. `obj.<prop>`
  // binds to the obj immediately before it — the same adjacency rule
  // `obj=metals&obj.recipe=` already depends on. Emitting the curve anywhere
  // else in the string binds it to whatever object happens to precede it, which
  // is how the melee turns green.
  //
  // 🚨 NEVER ADDRESS THE GROUP. `obj=stones/faceted` without `/g_Center` is a
  // GROUP and recolours the accents too (3,616 changed pixels against 2,821 for
  // the per-object form, measured on das 73290430). The object path is not
  // optional shorthand.
  //
  // No curve — a diamond, a colourless stone, or a species with none harvested —
  // falls through to the original all-diamond list, which is what shipped before.
  // TWO SHARDS, ONE COMPOSER. `RB_RENDERS` is the full map (7,089 seats x 5
  // angles, 160 KB gz) and loads on setting-detail and review only; `RB_TILE_RENDERS`
  // is the step-1 subset (one seat per shape, two frames). They carry the same
  // top-level keys on purpose — `urls`, `pattern`, `recipes`, `objsets` — and are
  // written by one block of build_builder_assets.py, so this function composes a
  // grid tile and a hero from the same template rather than from two copies of it.
  // The full shard wins when both are somehow present.
  /* `sideCurve` recolours the SIDE stones, exactly as `curve` recolours the centre,
     and it is a separate argument because the two are separate choices. Verified
     against Stuller: a curve emitted after `stones/faceted/g_Side` binds to that
     object alone — a green centre with red sides renders correctly, and the md5s
     differ from the sides-diamond render. Absent (the common case, and every build
     today) the sides stay `stones/diamonds` and nothing changes. */
  /* COLORED PAVÉ (owner 2026-09-25, option B; docs/handoffs/ring-builder-two-tone-pave.md). A design sold with lab-grown
     coloured accents ("Ruby", "Blue Sapphire", "Pink Sapphire", "Emerald") draws its halo and accents in that stone's
     own material: the curve stone_curves.py holds for it, shipped per accent label in the settings index
     (build_builder_assets.accent_curves). A diamond accent, or an index with no coloured accents, answers null and the
     melee stays white, exactly as before. ring-builder-pages.js calls this only when it exists (a cached older copy
     of this file draws white accents and the old facet words). */
  RB.accentCurve = function (accent) {
    // an accent stone line (a menu id, RB.accentFor) draws in its own material from the accent menu
    if (accent && String(accent).indexOf('menu:') === 0) {
      var menu = window.RB_ACCENT_MENU, fs = (menu && menu.families) || [];
      for (var i = 0; i < fs.length; i++) {
        if (fs[i][0] === accent) {
          var mc = (menu.curves || {})[fs[i][7]];
          return mc && mc.length ? mc : null;
        }
      }
      return null;
    }
    var m = window.RB_SETTINGS && window.RB_SETTINGS.accentCurves;
    var c = m && accent && Object.prototype.hasOwnProperty.call(m, accent) ? m[accent] : null;
    return c && c.length ? c : null;
  };

  /* `accentCurve` (colored pavé) recolours the HALO and ACCENT melee — every `g_Halo`, `g_Accent` and `g_Accent N`
     object — the way `curve` recolours the centre: a separate choice, so a separate argument. Absent (a diamond
     accent, and every build before coloured accents existed) the melee stays `stones/diamonds`. */
  RB.renderUrl = function (das, metal, rendition, objs, curve, sideCurve, accentCurve) {
    /* ⚠️ `RB_RENDERS_PENDANT` JOINS THE CHAIN FOR THE SAME REASON `RB_TILE_RENDERS`
       IS IN IT: the maps carry identical top-level keys (`pattern`, `recipes`,
       `urls`) and NEVER load on the same page — the section gates the wave map, and
       the tile shard is step-1 only. This function is handed a das and not a style,
       so it cannot ask `RB.rendersFor`; the chain is what makes one composer serve
       every wave.
       🚨 WITHOUT IT A PENDANT SILENTLY RETURNED null: `anglesFor` still produced its
       three angles, the viewer still mounted three <img>, and every src was the
       string "null" — a page that looks like it is loading forever rather than one
       that errors. Found the same day the pendant map shipped. */
    var R = window.RB_RENDERS || window.RB_RENDERS_PENDANT || window.RB_TILE_RENDERS;
    if (!R) return null;
    var enc = function (o) { return o.replace(/ /g, '%20'); };
    // `R.urls` is the pre-objsets fallback template with
    // `obj=stones/diamonds/g_Center` FROZEN INTO THE STRING, so it cannot carry
    // a colour. Every one of the 7,089 live seats has an objset, making this
    // unreachable today — but if a seat ever arrives without one, synthesise the
    // minimal list rather than silently handing back a white diamond for a stone
    // we do have a curve for.
    if (curve && curve.length && !(objs && objs.length) && R.pattern) {
      objs = ['stones/diamonds/g_Center'];
    }
    var tpl = (objs && objs.length && R.pattern)
      ? R.pattern.replace('{objs}', objs.map(function (o) {
          /* TWO CENTRE SEATS (owner 2A, 2026-09-25): the wrap designs 127348 and 123099 name their
             stones `g_Center 1` (the shopper's centre) and `g_Center 2` (the second stone, chosen at
             the side step), so neither test below matches them. Same per-object form, the number
             kept: measured on the supplier's renders 2026-09-27 (das 73315577 and 73310382), each
             recolours its own stone and nothing else. Only an object already in the harvested list is
             rewritten, exactly like the two below. */
          var two = /g_center(?: |%20)([12])$/i.exec(o);
          var twoCurve = two && (two[1] === '1' ? curve : sideCurve);
          if (twoCurve && twoCurve.length) {
            return '&obj=stones/faceted/g_Center%20' + two[1] +
              twoCurve.map(function (kv) { return '&' + kv[0] + '=' + kv[1]; }).join('');
          }
          if (sideCurve && sideCurve.length && /g_side$/i.test(o)) {
            // Same per-object form as the centre — never the bare `stones/faceted`
            // group, which recolours the melee too (3,616 changed pixels against
            // 2,821, measured).
            return '&obj=stones/faceted/g_Side' +
              sideCurve.map(function (kv) { return '&' + kv[0] + '=' + kv[1]; }).join('');
          }
          if (curve && curve.length && /g_center$/i.test(o)) {
            return '&obj=stones/faceted/g_Center' +
              curve.map(function (kv) {
                // The VALUES are raw here on purpose: a curve is digits, commas
                // and dots, none of which need escaping, and running them
                // through a form encoder is what turns the ' ' in an object
                // name into '+' elsewhere in this file. Nothing is interpolated
                // that did not come out of the harvest.
                return '&' + kv[0] + '=' + kv[1];
              }).join('');
          }
          if (accentCurve && accentCurve.length && /\/g_(halo|accent( \d+)?)$/i.test(o)) {
            // Each halo / accent object in the stone's own material, the per-object form again: the bare
            // `stones/faceted` group would recolour the centre too. Measured 2026-10-04 (das 163585953, style 225246):
            // the halo and the band's accents draw in the colour, the centre stays white.
            return '&obj=' + enc(o.replace(/^stones\/diamonds\//, 'stones/faceted/')) +
              accentCurve.map(function (kv) { return '&' + kv[0] + '=' + kv[1]; }).join('');
          }
          return '&obj=' + enc(o);          // every other object, exactly as before
        }).join(''))
      : R.urls;
    // A two-tone metal is TWO metal objects on the same asset (band, then head), so the pattern's single metal
    // part is replaced whole; a single metal keeps the `{recipe}` path below exactly as before.
    var tt = RB.twoTone(metal);
    if (tt) {
      tpl = tpl.replace('obj=metals&obj.recipe={recipe}',
                        'obj=metals/m1&obj.recipe=' + tt[0] + '&obj=metals/m2&obj.recipe=' + tt[1]);
    }
    return tpl.replace('{das}', das)
              .replace('{recipe}', R.recipes[metal] || 'white')
              .replace('{rendition}', rendition || 'standard');
  };

  /* TWO-TONE METALS (owner 2026-09-25). "14K Yellow & White Gold" -> ['yellow', 'white'], null for a single metal.
     The FIRST colour is the BAND (the supplier's `metals/m1`) and the second the HEAD (`metals/m2`): the supplier's
     own order, which also names a different ring — "14K White & Yellow Gold" is a white band under a yellow head.
     Measured 2026-09-27 over every planned two-tone option: this composition is the supplier's own picture of that
     SKU (ring-builder-two-tone/verify_renders.py). builder_metals.py is canonical; test_two_tone.py holds this to it. */
  RB.TWO_TONE_RECIPE = { Yellow: 'yellow', White: 'white', Rose: 'rose' };
  RB.twoTone = function (metal) {
    var m = /^\d{2}K (Yellow|White|Rose) & (Yellow|White|Rose) Gold$/.exec(String(metal || ''));
    return m ? [RB.TWO_TONE_RECIPE[m[1]], RB.TWO_TONE_RECIPE[m[2]]] : null;
  };

  // THE LIST BELONGS TO THE DAS ID, NOT TO THE STYLE. One style spans several
  // series, and the same (style, seat) can be served by different das ids with
  // different objects — 27 of 51 styles do. So the seat's own `o` (an index into
  // R.objsets, written by the same harvest row that produced these das ids) is
  // authoritative; R.objs[style] is only a coarse fallback for a seat without one.
  /* 🚨 THE RENDERS MAP IS PER WAVE (owner, 2026-08-17: *"The preview doesn't work
     for pendants like it does for rings"*). `RB_RENDERS` is the RING map; a pendant
     style is simply not in it, so `anglesFor` returned [] and the review page fell
     back to a stored photograph — which is why a green tsavorite in white gold drew a
     white diamond in yellow gold. Neither the metal recipe nor the stone curve can
     reach a photograph.

     ⚠️ THERE IS NO EARRING MAP AND THAT IS MEASURED. `probe_wave_parametric.py`:
     pendant 12/12 SKUs parametric, earring 0/12 — 12/12 FLAT PHOTOGRAPHS. An earring
     render would be a fixed picture that ignores metal and stone, i.e. exactly the
     bug being fixed here. Falling through to null keeps today's behaviour for that
     wave rather than inventing a render that cannot exist.
     ⚠️ Keyed on the STYLE, not on `RB.kind(build)`: the review page resolves a style
     before it has a wave in hand, and a style id is unambiguous across the waves. */
  RB.rendersFor = function (style) {
    var st = String(style);
    var ring = window.RB_RENDERS;
    if (ring && ring.renders && ring.renders[st]) return ring;
    var pend = window.RB_RENDERS_PENDANT;
    if (pend && pend.renders && pend.renders[st]) return pend;
    return ring || null;
  };

  RB.objsFor = function (style, seatKey) {
    var R = RB.rendersFor(style);
    if (!R) return null;
    var set = (R.renders[String(style)] || {})[seatKey];
    if (set && R.objsets && set.o != null && R.objsets[set.o]) return R.objsets[set.o];
    return (R.objs && R.objs[String(style)]) || null;
  };

  /* ------------------------------------------------- step-1 grid tiles ---- */
  // THE TILE FOR ONE (style, shape, metal), OR NULL.
  //
  // 🚨 A STORED TILE IS KEYED BY METAL AND BY NOTHING ELSE, so before this the
  // shape rail could not change the picture. All 51 ring styles take ROUND, OVAL
  // and EMERALD, so those three cannot change the row COUNT either — click Oval
  // and every pixel of the grid stayed put, which is indistinguishable from a
  // dead filter. brand_settings.py stores four images per product because that is
  // what the cart, the checkout and the admin read; the ~1,200 (shape x metal)
  // renders were always meant to be composed here instead of uploaded.
  //
  // Returns null — and the caller keeps the branded photo — whenever there is no
  // harvested render for the pair. That is the whole pendant and earring
  // catalogue today (pull_setting_renders.py has only ever run over the ring
  // wave), so those grids degrade to exactly today's behaviour rather than to a
  // broken image. The count is printed at build time so "no tiles" can never be
  // mistaken for "no shapes".
  //
  // 🚨 THE RENDITION IS PER WAVE, BECAUSE A PENDANT IS CLIPPED BY THE SMALL ONE.
  // Measured 2026-08-13 over 10 styles per wave, subject bbox against the frame:
  //     ring     $xlarge$ 640px   fill 0.851 sd 0.010   clipped  0/10
  //     pendant  $xlarge$ 640px   fill 1.000 sd 0.000   clipped 10/10   <-- cut off
  //     pendant  $large$ 1200px   fill 1.000 sd 0.000   clipped 10/10   <-- cut off
  //     pendant  $zoom$  1500px   fill 0.800 sd 0.000   clipped  0/10
  // ⚠️ `fill 1.000, sd 0.000` IS THE TELL AND IT READS AS PRECISION. A saturated
  // measurement is a FLOOR — "1.000 or more, unknown" — because the bbox has
  // become the frame. It is the same trap RB.ANGLE_FIT documents, and here it
  // would have shipped a visibly cropped pendant in every filtered tile.
  // Pendant $zoom$ costs 85 KB against the 72 KB stored tile it replaces.
  // ⚠️ An unknown rendition name does NOT error: `$medium$` and `$small$` both
  // return the 1200 px frame, the same silent fallback `$spin$` gives.
  RB.TILE_RENDITION = { ring: 'xlarge', pendant: 'zoom' };

  RB.tileFrames = function (style, shape, metal) {
    var T = window.RB_TILE_RENDERS;
    if (!T || !T.tiles) return null;
    var e = (T.tiles[String(style)] || {})[shape];
    if (!e || !e.s) return null;
    var st = RB.setting(style);
    var rend = RB.TILE_RENDITION[(st && st.kind) || 'ring'] || 'xlarge';
    // The objset is the seat's own, exactly as RB.objsFor resolves it for the
    // hero — a tile drawn without it is the same under-drawn halo the review page
    // used to ship, and it fails as a valid 200 JPEG.
    var objs = (e.o != null && T.objsets && T.objsets[e.o]) ? T.objsets[e.o] : null;
    return {
      seat: e.k,
      wave: (st && st.kind) || 'ring',
      img: RB.renderUrl(e.s, metal, rend, objs, null),
      // No hover frame rather than a fallback to the stored 3/4: that one is a
      // different SHAPE, so hovering an oval tile would turn it round. Same rule
      // as the metal filter's — never show one facet of the build under another's.
      hover: e.h ? RB.renderUrl(e.h, metal, rend, objs, null) : null
    };
  };

  // WHICH ANGLE LEADS IS A MERCHANDISING DECISION, NOT A HARVEST ONE. The shard's
  // `angles` is the order pull_setting_renders.py asked Stuller for, and it opens
  // on ViewA — a 3/4 lying on its side, which foreshortens the centre stone into an
  // ellipse. OWNER RULE 2026-08-09: every mounting opens on ViewS, the top-down
  // face-up view, because that is the seat the shopper is choosing.
  //
  // The order lives HERE, not in WANT_ANGLES, for two reasons: a
  // `pull_setting_renders.py --refresh` would silently revert it, and the harvest
  // order is also the shard's key order, which the verifier and the audits read.
  // Presentation belongs to the client; the harvest just says what exists.
  //
  // The whole strip is reordered, not merely the selected index — both consumers
  // (setting detail, review) light index 0, and a strip whose first thumbnail is
  // not the frame on the stage reads as a mis-click rather than as the default.
  // An angle missing from this list sorts to the end in shard order rather than
  // disappearing, so adding one to WANT_ANGLES still renders without a JS change.
  // 🚨 THIS ORDER IS NOW PHYSICAL, NOT ARBITRARY (2026-08-13). It was ViewS
  // followed by harvest order; everything after ViewS was whatever
  // `WANT_ANGLES` happened to list. Measured across 12 random styles, the five
  // cameras are one FIXED RIG applied catalogue-wide — mean silhouette aspect
  // ViewE 0.48, ViewA 0.76, ViewB_4 0.80, View5 1.15, ViewS 1.35, and every
  // style agrees to sd <= 0.02. Read from ViewS down it is the ring TIPPING UP
  // and TURNING AWAY from the camera:
  //
  //     ViewS (lying, face-up) -> View5 -> ViewA -> ViewB_4 (upright, front-on)
  //                                                  -> ViewE (upright, edge-on)
  //
  // so dragging along it reads as one continuous movement instead of five
  // unrelated product shots. ViewS still LEADS, which is owner rule 2026-08-09
  // and the only part of this order that was ever a merchandising decision.
  //
  // ⚠️ ASPECT ORDERS THE ENDS, NOT THE MIDDLE, AND SORTING BY IT IS WRONG.
  // ViewS (1.35, widest) and ViewE (0.48, narrowest) are pinned by it, but
  // ViewA 0.76 and ViewB_4 0.80 are within 0.04 of each other — a tilted 3/4 and
  // an upright circle simply project to similar boxes. Sorting the five by
  // aspect swaps that pair and gives ViewS -> View5 -> ViewB_4 -> ViewA ->
  // ViewE, which stands the ring fully up and then lays it back down again. The
  // middle pair is ordered by POSE. verify_angle_viewer.py therefore asserts the
  // ends by measurement and the pair by name, rather than pretending one metric
  // decides all five.
  //
  // ⚠️ THE STRIP AND THE DRAG MUST SHARE ONE ORDER. Both consumers render the
  // thumbnails straight off this array, so a separate "drag order" would make
  // one step of the drag land on a thumbnail that is not the neighbouring one
  // in the strip — which reads as a mis-click, the same reason the leading
  // frame and thumbnail 0 were made to agree in the first place.
  RB.ANGLE_ORDER = ['ViewS', 'View5', 'ViewA', 'ViewB_4', 'ViewE'];

  // ---------------------------------------------------------------- fit ----
  // THE RING'S APPARENT DIAMETER, PER ANGLE, AS A FRACTION OF THE FRAME.
  //
  // Each das is framed to FILL its own frame, so the same ring is drawn at
  // different sizes in different views — ViewB_4 and ViewE run edge to edge
  // (1.000) while View5 sits at 0.854. Clicking thumbnails hides that; DRAGGING
  // does not, and the ring visibly breathes as it turns. Measured pulse within
  // a single seat: 1.18x mean, 1.23x worst.
  //
  // 🚨 THESE ARE FIVE CONSTANTS AND NOT A HARVEST, WHICH IS A MEASUREMENT AND
  // NOT AN ASSUMPTION. A per-das table would be 35,445 fetches. It is not
  // needed: the framing is a property of the CAMERA, not of the ring in it.
  // Across 20 random styles the per-angle spread is sd <= 0.024, and across 8
  // seats within one style it is <= 0.009. So one global table takes the pulse
  // from 1.19x to ~1.03x, which is under the threshold of visibility.
  //
  // 🚨 MEASURED AT `$zoom$`, WHICH IS THE RENDITION THE VIEWER DRAWS. The first
  // version of this table was measured at `$xlarge$` and read
  // {0.863, 0.854, 0.895, 1.000, 1.000} — the same trap the hand visualiser
  // already documents ("harvest and draw at the same rendition"). Two things
  // were wrong with it and only one was harmless:
  //   * HARMLESS: the renditions differ by a near-uniform 0.794-0.800, and
  //     `angleScale` divides by the table's own minimum, so the RATIOS survived
  //     and the shipped correction was right to within 0.8%.
  //   * NOT HARMLESS: at `$xlarge$` the ring is CLIPPED by the frame in ViewB_4
  //     and ViewE, so their bbox was the frame and the value SATURATED at
  //     exactly 1.000. ⚠️ `sd = 0.000` is the tell, and it reads as precision.
  //     A saturated entry is a FLOOR, not a measurement: the true value is
  //     "1.000 or more, unknown", and it hides the day a seat starts clipping.
  // At `$zoom$` nothing clips (0 of 14 seats on every angle) and ViewB_4/ViewE
  // sit at a deterministic 0.800 — still sd 0.000, but now because the renderer
  // genuinely frames those two views to a fixed fraction, which is a fact rather
  // than a ceiling. verify_angle_viewer.py measures at `$zoom$` and FAILS on any
  // clipped sample for exactly this reason.
  //
  // ⚠️ NORMALISE DOWNWARD ONLY. Every factor derived below is <= 1 because the
  // target is the SMALLEST entry here — scaling a frame up would crop the band.
  RB.ANGLE_FIT = {
    ViewS: 0.681, View5: 0.674, ViewA: 0.708, ViewB_4: 0.800, ViewE: 0.800
  };

  // ------------------------------------------------------- per-style fit ----
  // 🚨 THE RING WAVE VARIES BY CAMERA; THE PENDANT WAVE VARIES BY STYLE.
  // Measured 2026-08-18 (measure_setting_fit.py --wave=pendant): all three
  // pendant cameras sit at 0.800 with sd 0.000 within a style, so the
  // angle-to-angle pulse is 1.000x and an ANGLE_FIT table for pendants would be
  // a no-op — that open follow-up is answered "measured, not needed".
  // One level up it is bimodal: 47 styles frame at 0.800 and 10 at 0.389, so the
  // same 16x12 oval drew at half size depending only on its mounting, floating in
  // whitespace. The correction is per style, harvested and shipped in the shard.
  //
  // ⚠️ THIS IS THE ONE PLACE WE NORMALISE **UPWARD**, WHICH RB.ANGLE_FIT FORBIDS.
  // That rule exists because the largest RING frames already run to the frame
  // edge, so scaling up crops the band. These outliers are small and centred to
  // the pixel, and measure_setting_fit.py refuses per style any correction that
  // would put the subject past the frame — the guard is in the measurement, so
  // nothing here has to re-derive it.
  RB.styleScale = function (style) {
    var R = RB.rendersFor(style);
    var m = R && R.scale;
    var k = m && m[String(style)];
    return k > 0 ? k : 1;
  };

  // The scale that makes every angle draw the ring at one physical size.
  RB.angleScale = function (angle) {
    var f = RB.ANGLE_FIT[angle];
    if (!f) return 1;                       // an angle we have not measured is left alone
    var lo = Infinity;
    for (var k in RB.ANGLE_FIT) {
      if (RB.ANGLE_FIT[k] < lo) lo = RB.ANGLE_FIT[k];
    }
    return lo / f;
  };

  // `stone` is OPTIONAL and is the resolved stone row, not an id — passing it is
  // what makes the mounting draw the chosen centre in its own colour. Omitting
  // it (or passing one with no curve) renders the all-diamond version, which is
  // the behaviour every caller had before 2026-08-12.
  // `sideStone` is optional and absent on every build today; when present the five
  // frames draw the sides in their own colour, so the review stage and the cart
  // thumbnail cannot disagree about what the customer is buying.
  // `accent` (optional) is the build's accent value: a coloured one draws the halo and accents in its colour
  // (RB.accentCurve); a diamond accent, or none, draws them white as before.
  RB.anglesFor = function (style, seatKey, metal, stone, sideStone, accent) {
    var R = RB.rendersFor(style);
    if (!R) return [];
    var byStyle = R.renders[String(style)] || {};
    var set = byStyle[seatKey];
    if (!set) return [];
    var objs = RB.objsFor(style, seatKey);
    var curve = (stone && stone.curve) || null;
    // A domed side is refused for the same reason a domed centre is: Stuller ships
    // no cabochon material, so a curve there draws facets the customer is not buying.
    var sideCurve = (sideStone && !sideStone.domed && sideStone.curve) || null;
    var accentCurve = RB.accentCurve(accent);
    var rank = function (a) {
      var i = RB.ANGLE_ORDER.indexOf(a);
      return i < 0 ? RB.ANGLE_ORDER.length + R.angles.indexOf(a) : i;
    };
    return R.angles.filter(function (a) { return set[a]; })
      .sort(function (x, y) { return rank(x) - rank(y); })
      .map(function (a) {
        return { angle: a, url: RB.renderUrl(set[a], metal, 'zoom', objs, curve, sideCurve, accentCurve) };
      });
  };

  /* 🚨 THE CART SHOWED A STOCK PHOTO OF A BARE MOUNTING (owner, 2026-08-18:
     *"in the cart, they don't show the final product"*).

     `rb-cart-pair.js` already merges the two line items into one card with one
     image — and took `setting.item.image`, which is the stored branded photo of
     the MOUNTING: a white diamond, in whatever metal that photo happens to be
     shot in, for a shopper who just configured a green tsavorite in white gold.
     The correct picture is the one the review page draws, and it is composed,
     not stored.

     🚨 THIS RETURNS A URL, AND THE CALLER STORES IT ON THE LINE ITEM — the cart
     does NOT recompute it. That is the whole design. Recomputing on the cart
     would mean loading ring-builder.js (39.9 KB gz) + the renders shard
     (159.5 KB) + rb-stones-meta.js (12.5 KB) + a stone shard just to resolve one
     `cv` index into a curve, on the page where the customer keeps their money.
     Worse, it would be a SECOND implementation of the same picture — the
     `chain_fit.py` bug, whose two copies agree until somebody edits one. Composed
     once here, the cart is guaranteed to show the exact image the shopper
     approved, and stays a 5.9 KB file with no shard dependencies.

     ⚠️ `xlarge` (640 px / 31 KB), not `standard` (300 px). The thumbnail is 104 px
     on desktop but `width:100%;height:180px` under 749 px — ~360 CSS px on a phone,
     which is 720 DEVICE px at 2x. `standard` would be visibly soft there, the same
     device-pixel-ratio trap the hand plate paid for.

     ⚠️ A DOMED CENTRE PASSES `null`, exactly as pageReview does — Stuller ships no
     cabochon material, so a curve here would draw a faceted stone the shopper does
     not own. Keep the two in step; this is the same decision, not a copy of it.

     Approved fixed earring illustrations resolve by exact SKU. Other earring
     builds return null and the cart falls back to its stored product photograph. */
  RB.buildThumb = function (build, stone, rendition, sideStone) {
    if (!build || !build.style || !build.seat) return null;
    if (RB.kind(build) === 'earring') {
      var preview = RB.earringPreview(build, stone);
      return preview ? preview.frames[0].url.replace(/\$zoom\$/, '$' + (rendition || 'xlarge') + '$') : null;
    }
    var R = RB.rendersFor(build.style);
    if (!R || !R.renders) return null;
    var set = (R.renders[String(build.style)] || {})[build.seat];
    if (!set) return null;
    // ⚠️ ANGLE_ORDER FIRST, THEN THE MAP'S OWN ORDER — the identical rank rule
    // `anglesFor` uses, so the cart shows the same camera that leads the review
    // strip (ViewS on rings, owner rule 2026-08-09). A pendant matches nothing in
    // ANGLE_ORDER, which is exactly why the fallback exists and why it is not a
    // bug that it fires: AngleA leads that wave.
    var lead = RB.ANGLE_ORDER.filter(function (a) { return set[a]; })[0] ||
               (R.angles || []).filter(function (a) { return set[a]; })[0];
    if (!lead) return null;
    var domed = !!(stone && stone.domed);
    // A domed SIDE is refused on the same ground as a domed centre: Stuller ships
    // no cabochon material, so a curve there would draw facets the customer is not
    // buying. Judged per stone, not per build — a faceted centre beside cabochon
    // sides is a real combination.
    var sideDomed = !!(sideStone && sideStone.domed);
    return RB.renderUrl(set[lead], build.metal, rendition || 'xlarge',
                        RB.objsFor(build.style, build.seat),
                        domed ? null : ((stone && stone.curve) || null),
                        sideDomed ? null : ((sideStone && sideStone.curve) || null),
                        RB.accentCurve(build.accentStone || build.accent));
  };

  // The five camera names are Stuller's internal ones and mean nothing to a
  // shopper — "ViewB_4" was being read out to screen readers and printed under
  // every thumbnail. What each one actually shows was measured, not guessed.
  //
  // ⚠️ DECLARED ABOVE ITS CALLER ON PURPOSE. `RB.mountAngleViewer` calls this
  // while building the alt text and the aria label. Property assignment on RB
  // is not hoisted the way a function declaration is, and this file has already
  // shipped one bug of exactly that shape — a `var stone` sitting below the
  // `RB.anglesFor` call that consumed it, which passed `undefined`, threw
  // nothing, and drew a white diamond on a fully resolved build. Keep
  // definitions above their first use here rather than relying on call order.
  RB.ANGLE_LABELS = {
    ViewS: 'Top', View5: 'Tilted', ViewA: 'Angled', ViewB_4: 'Front', ViewE: 'Profile'
  };
  RB.angleLabel = function (a) { return RB.ANGLE_LABELS[a] || a; };

  /* ------------------------------------------------- the angle viewer ---- */
  // Drag / swipe / arrow through the five angles, scale-normalised, with zoom.
  //
  // 🚨 THIS IS NOT A 360 AND MUST NEVER BE LABELLED ONE. Stuller ships five
  // fixed cameras per mounting and nothing else: `$spin$` / `$360$` / `$video$`
  // all fall back to `$xlarge$` byte for byte, the das ids surrounding a seat's
  // five are OTHER PRODUCTS (238 of 240 sampled render, none of them this ring),
  // and `FullySetImages`' second row of five is `AllComponents_*` — the same
  // five cameras with the matching wedding band composited in, not five more.
  // There is no frame sequence to be found; the copy says "Drag to rotate" and
  // the label says how many views there are.
  //
  // WHY THE STEP IS DISCRETE AND THE FADE IS SHORT. Blending two frames
  // continuously is what a real turntable viewer does, and it is wrong here: the
  // widest gap in this sequence is a 90 degree yaw (ViewB_4 -> ViewE), so a 50/50
  // blend is a legible double exposure rather than motion. The drag therefore
  // SNAPS to the nearest frame and the CSS fade only softens the cut.
  //
  // The frames are already `$zoom$` (1500px) and were being displayed at roughly
  // a third of that, so the magnification below costs no extra bytes — it spends
  // detail that was already downloaded and thrown away.
  // ⚠️ 1500 IS THE CEILING AND `scl=` DOES NOT RAISE IT. `scl=0.25` returns a
  // 2560px frame and `scl=0.125` a 5120px one, both HTTP 200 and both pure
  // upscales — energy above the 1500 master's Nyquist measures 0.34 and 0.26
  // against a 0.79 noise floor. Asking for one would quadruple the bytes for no
  // detail, which is the same silent fallback as `$spin$`.
  RB.NATIVE_PX = 1500;          // the `$zoom$` rendition, measured — see above
  RB.ANGLE_FADE_MS = 160;

  // POINTER TRAVEL PER FRAME, in CSS pixels (owner 2026-08-13: "dragging the
  // mouse less should start the turn"). This was `clientWidth / (N - 1)` — a
  // QUARTER of the stage per frame, so on a 632px stage the ring did not move
  // until the pointer had travelled 79px, and a full turn took most of the
  // stage. At 48 the first frame lands at 24px and a full turn takes 240px.
  //
  // ⚠️ FLAT PIXELS, NOT A FRACTION OF THE STAGE, AND THAT IS THE POINT. Tying it
  // to width made the same gesture mean different things on different pages —
  // the review stage is nearly twice the setting-detail one, so the identical
  // drag turned the ring half as far there. Travel is a property of the hand,
  // not of the layout.
  RB.ANGLE_DRAG_PX = 48;

  // Above this many frames the viewer loads a coarse subset first instead of
  // every frame at once. Five renders are ~200KB and have always loaded eagerly;
  // a 36-frame turntable is ~2.9MB and must not. 12 sits well clear of both.
  RB.ANGLE_EAGER_MAX = 12;
  // How many frames the coarse pass loads — spread across the whole revolution,
  // so the first usable state is a complete (if steppy) turn.
  RB.ANGLE_COARSE = 6;

  // Rings share the product gallery's PhotoSwipe window: arrows, counter, swipe,
  // pinch zoom, Escape and focus return all belong to that existing viewer.
  RB.mountAngleExpansion = function (stage, imgs, show, index) {
    var active = null, observer = null, disposed = false;
    var button = el('button', {
      class: 'rb-view__expand product__media-icon', type: 'button', hidden: true,
      'aria-label': 'Open ring photos full screen', 'aria-haspopup': 'dialog'
    });
    button.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="var(--icon-stroke-width, 1)" stroke="currentColor" class="icon" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="M3.75 3.75v4.5m0-4.5h4.5m-4.5 0L9 9M3.75 20.25v-4.5m0 4.5h4.5m-4.5 0L9 15M20.25 3.75h-4.5m4.5 0v4.5m0-4.5L15 9m5.25 11.25h-4.5m4.5 0v-4.5m0 4.5L15 15"/></svg>';
    // A button press must not also reach the stage's click-to-zoom handlers.
    ['pointerdown', 'pointerup', 'keydown'].forEach(function (name) {
      button.addEventListener(name, function (e) { e.stopPropagation(); });
    });
    function ready() {
      var lb = window.SRLightbox;
      if (lb) document.removeEventListener('sr:lightbox', ready);
      button.hidden = disposed || !lb || typeof lb.loadAndOpen !== 'function' ||
        !imgs.every(function (im) { return im.complete && im.naturalWidth; });
    }
    function detach() {
      if (!active) return;
      var lb = active.lb;
      lb.off('beforeOpen', opened);
      lb.off('change', changed);
      lb.off('contentAppend', framed);
      lb.off('destroy', detach);
      if (observer) observer.disconnect();
      observer = null;
      active = null;
    }
    function owned() {
      return active && active.lb.pswp && active.lb.pswp.options.dataSource === active.photos;
    }
    function closeOwned() {
      if (!owned() || active.closing) return;
      active.closing = true;
      var pswp = active.lb.pswp;
      // PhotoSwipe ignores close() during its opening animation. Its public
      // completion event also covers a rebuild immediately after a tap.
      pswp.on('openingAnimationEnd', function () { pswp.close(); });
      pswp.close();
    }
    function changed() {
      if (owned() && !disposed) show(active.lb.pswp.currIndex);
    }
    function framed(e) {
      var data = e.content && e.content.data;
      if (active && data && data.srRingOwner === button && e.content.element) {
        // Keep the builder's measured camera framing; PhotoSwipe zooms its parent.
        e.content.element.style.transform = data.srRingFrame;
      }
    }
    function opened() {
      if (!owned()) return;
      if (disposed || !stage.isConnected) { closeOwned(); return; }
      // A metal change or builder re-render removes this exact stage. Never
      // leave the previous choice's photos open after its page viewer goes away.
      observer = new MutationObserver(function () {
        if (owned() && !stage.isConnected) closeOwned();
      });
      observer.observe(document.body, {childList: true, subtree: true});
    }
    button.addEventListener('click', function (e) {
      e.stopPropagation();
      ready();
      var lb = window.SRLightbox;
      if (button.hidden || window.pswp || active) return;
      var photos = imgs.map(function (im) {
        return {src: im.src, msrc: im.src, width: im.naturalWidth, height: im.naturalHeight,
                alt: im.alt, srRingOwner: button, srRingFrame: im.style.transform};
      });
      active = {lb: lb, photos: photos};
      lb.on('beforeOpen', opened);
      lb.on('change', changed);
      lb.on('contentAppend', framed);
      lb.on('destroy', detach);
      if (!lb.loadAndOpen(index(), photos)) detach();
    });
    stage.appendChild(button);
    document.addEventListener('sr:lightbox', ready);
    imgs.forEach(function (im) { im.addEventListener('load', ready); });
    ready();
    return {destroy: function () {
      disposed = true;
      document.removeEventListener('sr:lightbox', ready);
      imgs.forEach(function (im) { im.removeEventListener('load', ready); });
      if (owned()) closeOwned();
      // Keep pending/opening hooks until destroy so a removed viewer cannot
      // leave an asynchronous PhotoSwipe opening behind.
      else if (active && active.lb.pswp) detach();
    }};
  };

  RB.mountAngleViewer = function (stage, angles, opts) {
    opts = opts || {};
    if (!stage || !angles || !angles.length) return null;
    if (stage.__rbViewer) stage.__rbViewer.destroy();
    var N = angles.length;

    // A PHOTOGRAPHED TURNTABLE IS THE SAME VIEWER WITH THREE THINGS TURNED OFF.
    // Stuller's five renders need a per-angle scale correction, carry named
    // cameras, and are few enough to load at once. A commissioned 36-frame spin
    // (TURNTABLE_SPEC.md) is the opposite on all three counts, and every default
    // below keeps the five-angle path byte-identical to what already ships.
    //   opts.normalise === false  frames are already framed consistently, so
    //                             applying RB.ANGLE_FIT would INTRODUCE a pulse
    //   opts.labels === false     "Top"/"Profile" describe five cameras and mean
    //                             nothing across 36; fall back to "view N of M"
    //   opts.dragPx               48px/frame would need 1,728px for a 36-frame
    //                             turn; ~14 gives the same feel per revolution
    var normalise = opts.normalise !== false;
    var named = opts.labels !== false;
    var dragPx = opts.dragPx || RB.ANGLE_DRAG_PX;

    stage.innerHTML = '';
    stage.classList.add('rb-stage--viewer');
    var view = el('div', { class: 'rb-view' });
    // 🚨 PROGRESSIVE ONLY WHEN IT IS NEEDED. Five frames at `$zoom$` are ~200KB
    // and load eagerly today; 36 are ~2.9MB, and eagerly loading those would
    // stall the page the viewer sits on. Above the threshold a coarse subset
    // loads first — a usable low-density spin within one round trip — and the
    // rest fill in behind it. At or below it, every frame is assigned its src
    // immediately, exactly as before, so the live path cannot regress.
    var progressive = N > RB.ANGLE_EAGER_MAX;
    var imgs = angles.map(function (a, i) {
      var im = el('img', {
        alt: (opts.label || 'Ring') + ' — ' +
             (named ? RB.angleLabel(a.angle) : 'view ' + (i + 1) + ' of ' + N),
        draggable: 'false',
        class: i === 0 ? 'on' : ''
      });
      im.setAttribute('data-src', a.url);
      // the per-angle correction, on the IMAGE; zoom/pan lives on the wrapper
      // above it, so neither transform can cancel the other.
      // Two independent corrections, composed: per CAMERA (rings) and per STYLE
      // (pendants). A wave only ever has one of them — angleScale returns 1 for
      // an unmeasured angle name and styleScale returns 1 for a style with no
      // entry — so the product is the right answer for both without branching
      // on the wave, which is what would go stale when a third wave lands.
      var s = normalise ? RB.angleScale(a.angle) * RB.styleScale(opts.style) : 1;
      if (s !== 1) im.style.transform = 'scale(' + s.toFixed(4) + ')';
      view.appendChild(im);
      return im;
    });
    stage.appendChild(view);

    function hydrate(i) {
      var im = imgs[((i % N) + N) % N];
      if (im && !im.src) im.src = im.getAttribute('data-src');
    }
    if (!progressive) {
      imgs.forEach(function (_im, i) { hydrate(i); });
    } else {
      // every Kth frame, so the first thing that works is a whole revolution
      // rather than a contiguous arc that dead-ends mid-turn
      var K = Math.ceil(N / RB.ANGLE_COARSE);
      for (var q = 0; q < N; q += K) hydrate(q);
      hydrate(0);
      var fill = function () { imgs.forEach(function (_im, i) { hydrate(i); }); };
      if (window.requestIdleCallback) requestIdleCallback(fill, { timeout: 2500 });
      else setTimeout(fill, 600);
    }

    /* ---- WHILE STULLER IS RENDERING ----------------------------------------
       A frame the CDN has not served before is GENERATED on demand. Measured
       2026-08-21 against meteor.stullercloud.com: a COLD frame is ~2.4s and a
       WARM one ~150ms — a 16x step — and it is Stuller's RENDERER, not our
       bytes, because a 10 KB `$standard$` frame costs the same ~2.2s as a 93 KB
       `$zoom$` one. `Cache-Control: max-age=259200` (3 days) on the answer, so
       the warm path is durable once anything has asked for that exact URL.

       🚨 NOTHING CLIENT-SIDE MAKES A COLD FRAME FASTER, AND THAT IS MEASURED,
       NOT ASSUMED. Loading the visible frame ALONE and deferring the other four
       — the obvious fix, and the exact shape `progressive` above already has —
       moved frame 0 from 2,461ms to 2,581ms over 8 trials on the live
       storefront, i.e. nothing, while pushing all five from 4,953ms to
       7,595ms. Stuller renders the five concurrently and the queueing cost is
       not ours to win back, so the five still load together and the only thing
       that changes here is that the stage says what it is doing. The lever that
       DOES work is asking earlier (a warm URL is 150ms), never asking harder.

       Before this the stage was `--rb-plate` — WHITE — with no class on it at
       all, so the shopper watched a blank square for ~3.5s (measured on the live
       setting-detail page, navigation start to frame 0 landing). That is
       indistinguishable from a page that has broken.

       ⚠️ ONLY WHEN THE FRAME IS ACTUALLY COMING. A warm frame decodes from cache
       and `complete` is already true on the line below, so announcing a render
       there would put a skeleton flash on the FAST path and make it read as
       slower than the slow one.
       ⚠️ AND IT MUST CLEAR ON `error`, NOT ONLY ON `load`. A frame that 500s
       would otherwise leave a permanent "rendering…" over an empty plate, which
       is a worse lie than the blank plate this replaces. */
    var leadImg = imgs[0];
    stage.classList.remove('is-rendering');
    stage.removeAttribute('aria-busy');
    if (leadImg && !(leadImg.complete && leadImg.naturalWidth)) {
      /* 🚨 A TOKEN, BECAUSE `pageReview` RE-RENDERS IN PLACE. `stage.innerHTML`
         is cleared at the top of this function but the CLASS lives on `stage`
         itself, so a second mount starting while the first frame is still in
         flight would have the OLD handler clear the NEW mount's state — a stage
         that goes quiet while it is still blank. Only the current mount clears. */
      var token = (RB._stageMount = (RB._stageMount || 0) + 1);
      stage.__rbMount = token;
      stage.classList.add('is-rendering');
      stage.setAttribute('aria-busy', 'true');
      /* The wave the PAGE declares, not one threaded through opts: `data-rb-kind`
         is on the section and cannot disagree with the flow the shopper is in. */
      var piece = (RB.KINDS[RB.pageKind() || 'ring'] || RB.KINDS.ring).label;
      var note = el('div', { class: 'rb-render', 'aria-hidden': 'true' }, [
        el('span', { class: 'rb-render__spin' }),
        el('span', { class: 'rb-render__txt', text: 'Rendering your ' + piece + '…' })
      ]);
      stage.appendChild(note);
      var settled = function () {
        if (stage.__rbMount !== token) return;
        stage.classList.remove('is-rendering');
        stage.removeAttribute('aria-busy');
        if (note.parentNode) note.parentNode.removeChild(note);
      };
      leadImg.addEventListener('load', settled, { once: true });
      leadImg.addEventListener('error', settled, { once: true });
      /* A frame that neither loads nor errors — a hung connection — must not pin
         the message on for ever. 20s is far outside the measured cold tail. */
      setTimeout(settled, 20000);
    }

    var hint = el('div', { class: 'rb-view__hint', 'aria-hidden': 'true',
                           text: N > 1 ? 'Drag to rotate' : '' });
    if (N > 1) stage.appendChild(hint);

    var cur = 0, zoom = 1, tx = 0, ty = 0, touched = false;

    // 🚨 NO FLOOR. This read `Math.max(1.5, Math.min(3.2, NATIVE/box))` and the
    // floor was the bug — on the REVIEW page the stage measures 1232px CSS, so a
    // 1500px master carries only 1.22x of real detail and the floor handed out
    // 1.50x anyway. That is a magnifier that invents 23% of what it shows, which
    // is the same defect as `scl=` returning a 2560px upscale: a bigger picture
    // and not one more pixel of ring. Caught in a browser, not by any test —
    // the setting-detail stage is 632px, where the floor never binds.
    function maxZoom() {
      var box = stage.clientWidth || RB.NATIVE_PX;
      return Math.min(3.2, Math.max(1, RB.NATIVE_PX / box));
    }
    // Below this there is nothing worth revealing, so the control is not drawn
    // at all rather than drawn and disappointing — a route advertised and not
    // honoured is the failure this repo keeps paying for.
    var ZOOM_WORTH_IT = 1.25;
    function canZoom() { return maxZoom() >= ZOOM_WORTH_IT; }
    function clampPan() {
      var lim = Math.max(0, (zoom - 1) / 2);
      tx = Math.min(lim, Math.max(-lim, tx));
      ty = Math.min(lim, Math.max(-lim, ty));
    }
    function paint() {
      clampPan();
      view.style.transform = zoom === 1 ? ''
        : 'translate(' + (tx * 100).toFixed(3) + '%,' + (ty * 100).toFixed(3) + '%) scale(' + zoom.toFixed(3) + ')';
      stage.classList.toggle('is-zoomed', zoom > 1);
    }
    function touch() {
      if (touched) return;
      touched = true;
      stage.classList.add('is-touched');
    }
    function show(i) {
      // WRAPS (owner 2026-08-13: "make it continuous"). It clamped at first, on
      // the reasoning that these five are a SWEEP with two ends rather than an
      // orbit — which is true of the geometry and wrong for the interaction: a
      // drag that stops dead reads as the viewer being broken, and every
      // turntable a shopper has used loops. Same modulo form RB.mountSpin
      // already uses for stones.
      // ⚠️ The seam is real and is ViewE -> ViewS, upright edge-on back to
      // lying flat — the widest jump in the set. It is one transition in five
      // and the alternative was a dead end at both extremes.
      var n = ((i % N) + N) % N;
      if (n === cur) return;
      // A progressive load may not have reached this frame yet — fetch it now
      // rather than revealing an empty box mid-drag. No-op below the threshold,
      // where every frame already has its src.
      hydrate(n);
      imgs[cur].classList.remove('on');
      imgs[n].classList.add('on');
      cur = n;
      stage.setAttribute('aria-label', label());
      // 🚨 THE STRIP HAS TO HEAR ABOUT A DRAG. The thumbnails are the only
      // indication of which of the five is on the stage, and the drag and the
      // arrow keys move the stage without going anywhere near them — so without
      // this the shopper drags to the profile view while the strip still shows
      // the top view selected, which reads as the strip being broken.
      if (opts.onChange) opts.onChange(cur);
    }
    function label() {
      return (opts.label || 'Ring') +
             (named ? ', ' + RB.angleLabel(angles[cur].angle) : '') +
             ' — view ' + (cur + 1) + ' of ' + N +
             (N > 1 ? '. Drag or use arrow keys to rotate.' : '');
    }
    function setZoom(z, ox, oy) {
      var prev = zoom;
      zoom = Math.min(maxZoom(), Math.max(1, z));
      if (zoom === 1) { tx = ty = 0; }
      else if (ox != null && prev !== zoom) {
        // keep the point under the cursor roughly put while scaling
        tx += (0.5 - ox) * (zoom - prev) / zoom;
        ty += (0.5 - oy) * (zoom - prev) / zoom;
      }
      paint();
    }

    /* ---- pointer: rotate at 1x, pan when zoomed ---- */
    var dragging = false, x0 = 0, y0 = 0, f0 = 0, pan0x = 0, pan0y = 0, moved = 0;
    var pointers = {}, pinch0 = 0, zoom0 = 1;

    stage.addEventListener('pointerdown', function (e) {
      pointers[e.pointerId] = { x: e.clientX, y: e.clientY };
      if (Object.keys(pointers).length === 2) {
        var p = Object.keys(pointers).map(function (k) { return pointers[k]; });
        pinch0 = Math.hypot(p[0].x - p[1].x, p[0].y - p[1].y) || 1;
        zoom0 = zoom;
        dragging = false;
        return;
      }
      dragging = true; moved = 0;
      x0 = e.clientX; y0 = e.clientY; f0 = cur; pan0x = tx; pan0y = ty;
      touch();
      stage.classList.add('is-drag');
      try { stage.setPointerCapture(e.pointerId); } catch (err) {}
    });

    stage.addEventListener('pointermove', function (e) {
      if (pointers[e.pointerId]) { pointers[e.pointerId] = { x: e.clientX, y: e.clientY }; }
      var ids = Object.keys(pointers);
      if (ids.length === 2 && pinch0) {
        var p = ids.map(function (k) { return pointers[k]; });
        var d = Math.hypot(p[0].x - p[1].x, p[0].y - p[1].y);
        setZoom(zoom0 * (d / pinch0), 0.5, 0.5);
        e.preventDefault();
        return;
      }
      if (!dragging) return;
      var dx = e.clientX - x0, dy = e.clientY - y0;
      moved = Math.max(moved, Math.abs(dx), Math.abs(dy));
      if (zoom > 1) {
        var box = stage.clientWidth || 1;
        tx = pan0x + dx / box; ty = pan0y + dy / box;
        paint();
        e.preventDefault();
        return;
      }
      if (N < 2) return;
      show(f0 - Math.round(dx / dragPx));
      e.preventDefault();
    });

    ['pointerup', 'pointercancel', 'pointerleave'].forEach(function (ev) {
      stage.addEventListener(ev, function (e) {
        delete pointers[e.pointerId];
        if (Object.keys(pointers).length < 2) pinch0 = 0;
        if (!dragging) return;
        dragging = false;
        stage.classList.remove('is-drag');
        try {
          if (e.pointerId != null && stage.hasPointerCapture(e.pointerId)) {
            stage.releasePointerCapture(e.pointerId);
          }
        } catch (err) {}
        // a press that never travelled is a click, and a click toggles zoom —
        // but only where zooming reveals something (see canZoom)
        if (ev === 'pointerup' && moved < 6 && (zoom > 1 || canZoom())) {
          setZoom(zoom > 1 ? 1 : maxZoom(), 0.5, 0.5);
        }
      });
    });

    // ⚠️ WHEEL ONLY WHILE ALREADY ZOOMED. A stage that swallows the wheel at 1x
    // traps the page scroll on mobile and on a trackpad — the shopper scrolls
    // past a ring, not into it.
    stage.addEventListener('wheel', function (e) {
      if (zoom === 1) return;
      var r = stage.getBoundingClientRect();
      setZoom(zoom * (e.deltaY < 0 ? 1.12 : 1 / 1.12),
              (e.clientX - r.left) / r.width, (e.clientY - r.top) / r.height);
      e.preventDefault();
    }, { passive: false });

    /* ---- keyboard parity: everything the pointer does ---- */
    stage.setAttribute('tabindex', '0');
    stage.setAttribute('role', RB.pageKind() === 'ring' ? 'group' : 'img');
    stage.setAttribute('aria-label', label());
    stage.addEventListener('keydown', function (e) {
      var k = e.key;
      if (k === 'ArrowRight' || k === 'ArrowDown') { touch(); show(cur + 1); e.preventDefault(); }
      else if (k === 'ArrowLeft' || k === 'ArrowUp') { touch(); show(cur - 1); e.preventDefault(); }
      else if (k === 'Home') { touch(); show(0); e.preventDefault(); }
      else if (k === 'End') { touch(); show(N - 1); e.preventDefault(); }
      else if (k === '+' || k === '=') { setZoom(zoom * 1.25, 0.5, 0.5); e.preventDefault(); }
      else if (k === '-') { setZoom(zoom / 1.25, 0.5, 0.5); e.preventDefault(); }
      else if (k === 'Escape' && zoom > 1) { setZoom(1); e.preventDefault(); }
    });

    // ⚠️ HIDDEN UNTIL PROVEN WORTHWHILE, not shown until proven pointless. Both
    // defaults are wrong for a moment while layout settles; only one of them
    // looks broken. A control that fades in a frame late reads as normal, while
    // one that VANISHES as the shopper reaches for it reads as a bug — and that
    // is exactly what the review page did, offering the button from a mid-layout
    // measurement and withdrawing it on hover.
    var expansion = RB.pageKind() === 'ring';
    var zoomBtn = el('button', {
      class: 'rb-view__zoom', type: 'button', hidden: true,
      'aria-label': 'Zoom in',
      onclick: function (e) {
        e.stopPropagation();
        setZoom(zoom > 1 ? 1 : maxZoom(), 0.5, 0.5);
        zoomBtn.setAttribute('aria-label', zoom > 1 ? 'Zoom out' : 'Zoom in');
      }
    });
    if (!expansion) stage.appendChild(zoomBtn);

    // The stage is fluid, so whether zooming is worth offering depends on the
    // viewport — the review stage measures 1232px in one column and 652px in
    // another, which is the difference between 1.22x (not worth a control) and
    // 2.30x (worth one).
    //
    // BOTH listeners, deliberately. ResizeObserver catches a stage that reflows
    // without the window changing (a column breakpoint, the hand panel opening);
    // the window event covers a browser without it. `syncZoomAffordance` is
    // idempotent, so firing twice costs nothing.
    //
    // 🚨 THE STAGE IS USUALLY DETACHED WHEN THIS RUNS, SO clientWidth IS 0. The
    // setting-detail page builds `stage`, mounts the viewer into it, and only
    // THEN appends the whole column to the document — so measuring at mount asks
    // a detached element how wide it is, gets 0, and concludes there is no
    // magnification worth offering. The control then stays hidden forever, on
    // the page with the most room to zoom.
    // ⚠️ ITS SYMPTOM IS SPLIT, WHICH IS WHY IT SURVIVED A ROUND OF TESTING:
    // click-to-zoom calls canZoom() LIVE and worked perfectly at 2.37x while the
    // button advertising it was decided once, early, and was wrong. Two
    // behaviours reading one predicate at different times looked like a
    // ResizeObserver failure and was not — the REVIEW page, whose stage is
    // already attached at mount, was correct throughout.
    // Deferred to after layout, then re-checked on any later resize.
    function syncZoomAffordance() {
      var worth = canZoom();
      zoomBtn.hidden = !worth;
      if (!worth && zoom > 1) setZoom(1);
    }
    // ⚠️ RE-TESTING THE RESPONSIVE PATH: driving the viewport from automation
    // changes clientWidth WITHOUT dispatching `resize` or a ResizeObserver
    // callback — measured with a hand-installed probe, 0 callbacks for a
    // 1232 -> 652px change it definitely saw. Reload after resizing or the
    // reading is a lie; the same trap `<picture>` set on the homepage.
    if (window.requestAnimationFrame) requestAnimationFrame(syncZoomAffordance);
    else setTimeout(syncZoomAffordance, 0);
    var observer = window.ResizeObserver ? new ResizeObserver(syncZoomAffordance) : null;
    if (observer) observer.observe(stage);
    window.addEventListener('resize', syncZoomAffordance);
    // 🚨 AND AGAIN THE MOMENT IT MATTERS. Even deferred a frame, the initial
    // measurement can land mid-layout — the review page settles at 1232px but
    // was measured narrower and offered a control it should have withheld. Every
    // scheduled callback here is a GUESS about when layout is done; a shopper
    // reaching for the stage is not. Re-syncing on hover and focus makes the
    // control correct at the instant it can be used, whatever the layout did
    // before, and it costs one comparison. The click path already reads
    // canZoom() live for the same reason — this is the button catching up.
    ['pointerenter', 'focusin'].forEach(function (ev) {
      stage.addEventListener(ev, syncZoomAffordance);
    });

    var enlarged = expansion ? RB.mountAngleExpansion(stage, imgs, function (i) {
      touch(); setZoom(1); show(i);
    }, function () { return cur; }) : null;
    paint();
    return (stage.__rbViewer = {
      show: function (i) { touch(); setZoom(1); show(i); },
      index: function () { return cur; },
      count: function () { return N; },
      reset: function () { setZoom(1); },
      destroy: function () {
        if (observer) observer.disconnect();
        window.removeEventListener('resize', syncZoomAffordance);
        if (enlarged) enlarged.destroy();
        stage.__rbMount = null;
        stage.__rbViewer = null;
      }
    });
  };

  /* ------------------------------------------------------------ add to cart */
  // TWO line items joined by a shared _build_id. Underscore-prefixed properties
  // are hidden by Edge's cart templates; the visible ones must be
  // SELF-DESCRIBING because the cart DRAWER renders values with the label
  // visually-hidden, so a bare "1 of 2" would read as nonsense there.
  RB.buildId = function () {
    return (Date.now().toString(36) + Math.random().toString(36).slice(2, 6)).toUpperCase();
  };

  // How many of EACH half this build orders. 1 for a ring or a pendant, 2 for a
  // pair of earrings — see the `pair` note on RB.KINDS.
  RB.pairQty = function (build) { return RB.KINDS[RB.kind(build)].pair || 1; };

  // Customer copy for the final-sale note on a build's own parts; see RB.addToCart.
  RB.FINAL_SALE = 'Final sale \u2014 made and set to order, not returnable';

  /* ---- ENGRAVING (owner 2026-10-06) ----------------------------------------
     The supplier's inside-band engraving, offered FREE on the review step (owner: "1. Yes 2. Yes": free; the shopper
     chooses the text, the font and the color fill, as on the supplier's own screen). The supplier publishes it per
     SKU, and only bare mountings carry it (never a Semi-Set, never a pendant or earring setting), so the answer is
     per VARIANT, from `rb-ring-engrave.js` (builder-engraving/census.py -> build.py), never per style or title.
     `RB.ENGRAVE_V` is this engine's mark: the review step shows the box only beside an engine that has it, so a
     page newer than a cached engine never takes an engraving the engine would drop. */
  RB.ENGRAVE_V = 1;
  // Carries only an opaque edit/draft identity across the builder's steps, never engraving words.
  FIELDS.push('engrave_flow');
  // The variant's engraving spec ({ loc, max, fonts: [{ id, label, max, img }], colors: [...] }), or null: not a
  // ring, data not loaded, variant not known, or the supplier does not engrave this SKU.
  RB.engraveSpec = function (build) {
    var E = window.RB_ENGRAVE;
    if (!E || !E.styles || !E.specs || !build || RB.kind(build) !== 'ring') return null;
    var sv = RB.settingVariant(build);
    if (!sv) return null;
    var s = E.styles[String(build.style)];
    if (!s) return null;
    return engraveIds(s)[String(sv[0])] ? (E.specs[s.spec] || null) : null;
  };
  /* 🚨 EVERY ENGRAVABLE VARIANT IS LISTED; NOTHING IS ENGRAVABLE BY STYLE (Codex review-muwn6qol-q5s3ih, high). A
     style once answered "all", so a variant the builder data added after the census was offered engraving the
     supplier never confirmed. Now `ids` names each confirmed variant (sorted, base-36, each after the first as the gap
     from the one before: about 4 characters a variant), decoded once per style; an unknown id is not engravable. */
  function engraveIds(s) {
    if (s._set) return s._set;
    var set = {}, parts = String(s.ids || '').split(','), n = 0;
    for (var i = 0; i < parts.length; i++) {
      if (!parts[i]) continue;
      n += parseInt(parts[i], 36);
      set[String(n)] = 1;
    }
    s._set = set;
    return set;
  }
  /* What the laser is asked to cut: letters, numbers, spaces and simple punctuation. Typographic quotes become
     straight ones (a phone keyboard types them), runs of spaces become one, the ends are trimmed. Anything else is
     dropped, so what the shopper sees in the box is exactly what reaches the order. */
  RB.engraveClean = function (text) {
    return String(text == null ? '' : text)
      .replace(/[‘’‛′]/g, "'").replace(/[“”‟″]/g, '"')
      .replace(/[–—]/g, '-')
      .replace(/[^A-Za-z0-9 .,'"&+!?:;\/()#@-]/g, '')
      .replace(/\s+/g, ' ').replace(/^ /, '');
  };
  RB.ENGRAVE_NONE = 'None';

  RB.addToCart = function (build, opts) {
    var sv = RB.settingVariant(build);
    if (!sv) return Promise.reject(new Error('setting variant not found'));
    if (!opts.stoneVariantId) return Promise.reject(new Error('no stone chosen'));
    var qty = RB.pairQty(build);
    if (RB.kind(build) === 'earring') {
      if (!RB.eligibleFor(opts.stone, 'earring') ||
          String(opts.stone.vid) !== String(opts.stoneVariantId)) {
        return Promise.reject(new Error('Please choose a gemstone from the current earring collection.'));
      }
    }
    // 🚨 A PAIRED BUILD MUST NOT REACH THE CART FROM A SERIALIZED STONE. The grid
    // filters to calibrated stock, but this is the last gate before money, and a
    // shared or hand-edited URL bypasses every grid. Ordering qty 2 of a one-of-one
    // stone is an order Stuller cannot fill: it would confirm, then arrive short,
    // and the customer would have paid for two.
    if (qty > 1 && opts.stone && !RB.pairable(opts.stone)) {
      return Promise.reject(new Error('that gemstone is one of a kind, so it cannot be made as a pair'));
    }
    /* 🚨 A MOUNTING WITH EMPTY SIDE SEATS MAY NOT BE SOLD WITHOUT THEM. This is the
       last gate before money and the only one a shared or hand-edited URL cannot
       walk past — the step bar, the review gate and the CTA are all UI, and every
       one of them is bypassed by pasting a link. Without it we send Stuller a
       Mounting SKU and one centre stone, they set the centre, and the customer
       receives a ring with two visible holes in it. Nothing in the order would look
       wrong: the SKU is real, the price is real, and Stuller fulfils exactly what
       was asked for.
       ⚠️ GATED ON THE SEAT, NOT ON THE STYLE. A style is only ever "a mounting with
       empty sides" AT a given centre seat — `RB.sideSeat` keys on the seat, and the
       other 521 styles return null here and are unaffected. */
    var needSides = (build.style && build.seat)
      ? RB.sideSeat(build.style, build.seat) : null;
    // the owner's words for a one-stone seat on the five second-stone designs (RB.secondStone)
    var secondStone = RB.secondStone(build.style, needSides);
    if (needSides && !(opts.side && opts.side.variantId)) {
      return Promise.reject(new Error(secondStone
        ? 'this setting is made with a second stone, so please choose it before adding to cart'
        : 'this setting is made with two side stones, so please choose them before adding to cart'));
    }
    /* 🚨 A SIZED RING GOES IN WITH ITS SIZING CHARGE, OR NOT AT ALL (owner 2026-09-17; RB.sizingFor has the rule). The last
       gate before money, like the side-stone gate above: a pasted link or a stale page reaches here too. It refuses a
       ring with no size, a size nothing can charge for, and a charge other than the one the review step showed
       (`opts.sizing`): a review step older than this file shows none, and no shopper is charged what they were not
       shown. */
    var sizing = null;
    if (RB.isSized(build)) {
      if (!build.size) return Promise.reject(new Error('please choose a ring size first'));
      var sz = RB.sizingFor(build, build.size);
      if (sz.kind === 'unavailable') {
        return Promise.reject(new Error('size ' + RB.sizeLabel(build.size) +
          ' cannot be ordered online for this setting, so please contact us'));
      }
      if (sz.kind === 'charge') {
        if (!opts.sizing || String(opts.sizing.variantId) !== sz.variantId || Number(opts.sizing.charge) !== sz.charge) {
          var stale = new Error('the ring sizing charge on this page is out of date, so please refresh the page');
          stale.code = 'sizing-stale';
          return Promise.reject(stale);
        }
        sizing = sz;
      }
    }
    /* 🚨 ACCENT STONES GO IN AT THE CHARGE THE SHOPPER WAS SHOWN, OR NOT AT ALL (colored pavé, owner option 1,
       2026-11-03; RB.accentFor has the rule). `sv` above is already the EMPTY mounting for such a build, so a ring with
       empty accent seats must never leave here without its Accent Stones line: a line not offered at this seat and
       metal, and a charge other than the one the review step showed (`opts.accent`), are both refused. */
    var accentLine = null;
    if (build.accentStone) {
      var ac = RB.accentFor(build);
      if (!ac) {
        return Promise.reject(new Error('those accent stones are not offered for this setting, so please choose ' +
                                        'your accent stones again'));
      }
      if (!opts.accent || String(opts.accent.variantId) !== ac.variantId || Number(opts.accent.charge) !== ac.charge) {
        var staleA = new Error('the accent stones price on this page is out of date, so please refresh the page');
        staleA.code = 'accent-stale';
        return Promise.reject(staleA);
      }
      accentLine = ac;
    }
    /* 🚨 AN ENGRAVING GOES IN EXACTLY AS THE SHOPPER SAW IT, OR NOT AT ALL (owner 2026-10-06). The last gate before
       money, like the sizing gate above: a stale page or a pasted link reaches here too. It refuses an engraving on a
       ring the supplier does not engrave, a font or color fill the supplier does not offer, and text the box would
       not have allowed -- never silently trimmed, recoloured or dropped, because the order is what we relay. */
    var engrave = null;
    if (opts.engravePending) return Promise.reject(new Error('please refresh the engraving options before adding'));
    if (opts.engrave && String(opts.engrave.text == null ? '' : opts.engrave.text).trim()) {
      // A cached older page cannot submit a global, unbound engraving through this engine.
      if (!opts.engraveFlow || opts.engraveFlow !== build.engrave_flow) {
        return Promise.reject(new Error('please refresh this page to confirm the engraving for this build'));
      }
      var es = RB.engraveSpec(build);
      if (!es) return Promise.reject(new Error('this setting cannot be engraved, so please clear the engraving'));
      var etxt = String(opts.engrave.text).trim();
      var efont = (es.fonts || []).filter(function (f) { return String(f.id) === String(opts.engrave.font); })[0];
      if (!efont) return Promise.reject(new Error('please choose a font for the engraving'));
      if (RB.engraveClean(etxt).trim() !== etxt || etxt.length > (efont.max || es.max)) {
        return Promise.reject(new Error('the engraving can be up to ' + (efont.max || es.max) +
          ' letters, numbers, spaces and simple punctuation'));
      }
      var ecolor = String(opts.engrave.color || RB.ENGRAVE_NONE);
      if (ecolor !== RB.ENGRAVE_NONE && (es.colors || []).indexOf(ecolor) < 0) {
        return Promise.reject(new Error('please choose a color fill from the list'));
      }
      engrave = { text: etxt, font: efont.label, color: ecolor };
    }
    var id = RB.buildId();
    var shared = {
      '_build_id': id,
      '_rb_style': String(build.style),
      '_rb_seat': build.seat,
      '_rb_kind': RB.kind(build),
      // THE WHOLE BUILD, so the cart can offer "Change" on either half the way
      // Blue Nile does. Without it the cart knows the style and the seat but not
      // the metal, accent, stone or origin, and a "Change setting" link would
      // drop the shopper into a half-empty builder — the same partial-copy
      // mistake that made step 1b lose the stone. `RB.href('')` emits exactly
      // RB.FIELDS, so this stays correct when a field is added.
      '_rb_q': RB.href('', build).replace(/^\?/, ''),
      // The build's code stays in `_build_id` above: checkout hides it, the order page in Shopify admin shows it. The
      // visible label names only what the piece belongs to (owner 2026-10-08: the code means nothing to a customer).
      'Part of': 'Custom ' + RB.KINDS[RB.kind(build)].label
    };
    /* THE PICTURE OF THE THING THEY JUST BOUGHT. Composed here, at the one moment
       every input is resolved and on screen, and carried on the line item so the
       cart needs no shards — see RB.buildThumb for why it is not recomputed there.
       ⚠️ Set for composed renders or approved fixed earring images. An absent property is what a legacy earring build and
       an older cart both look like, and `rb-cart-pair.js` falls back to the stored
       image for both — i.e. exactly today's behaviour, never a broken <img>. */
    var thumb = RB.buildThumb(build, opts.stone || null, null,
                              (opts.side && opts.side.stone) || null);
    if (thumb) shared['_rb_img'] = thumb;
    // Only properties the VARIANT does not already state. Shopify prints the
    // variant's own options above these, so repeating Metal and the seat put
    // "Metal: 14K Yellow Gold" and the centre-stone size on screen twice — and
    // "Stone: <title>" restated the stone line's own title verbatim.
    // The component labels have to state the PAIR, because the cart drawer renders
    // a property's label visually-hidden and shows only its value. "Setting (1 of
    // 2)" beside a line of quantity 2 reads as a contradiction; "Both mountings"
    // reads correctly on its own, which is the standing rule for these strings.
    var half = qty > 1 ? ' (pair)' : '';
    /* 🚨 THE COUNTER COUNTED TO TWO ON A THREE-PART BUILD. These labels were
       hardcoded "(1 of 2)" and "(2 of 2)" from the days when a build WAS exactly a
       setting plus a stone — so a three-stone order reached the cart reading
       "Setting (1 of 2)", "Center stone (2 of 2)" and a third line that was
       apparently not part of it. Found in a real cart 2026-08-19, after the side
       line was already pricing correctly: the same "built for exactly two lines"
       family as the total that omitted the sides, one layer further out, and this
       half is CUSTOMER-VISIBLE — it prints in the cart drawer and on the packing
       slip Stuller works from.
       ⚠️ COUNTED FROM THE PARTS THIS BUILD ACTUALLY POSTS, never from a flag. The
       side line is conditional on a variant id AND on the pairable gate below it,
       so anything that decides the denominator earlier can disagree with the array
       that is finally sent. */
    /* ⚠️ THE DENOMINATOR IS DECIDED ONCE, HERE, AND THE PUSHES BELOW REUSE THESE
       BOOLEANS. It used to be re-derived inline as `(opts.side && …) ? 3 : 2`, which
       mirrored the later `if` rather than sharing it — two expressions that have to
       agree, which is the shape the counter got wrong the first time. Now a part
       cannot be posted without being counted, or counted without being posted.
       ⚠️ A CHAIN IS A PENDANT-WAVE PART ONLY. Nothing can carry one into another wave
       today — the picker is rendered only on the pendant review step — but a ring
       line item reading "Matching chain (4 of 4)" would be a real order sent to
       Stuller, so the wave is asserted here rather than assumed from the caller. */
    var willSide = !!(opts.side && opts.side.variantId);
    var willChain = !!(opts.chain && opts.chain.i && RB.kind(build) === 'pendant');
    var nParts = 2 + (willSide ? 1 : 0) + (willChain ? 1 : 0) + (sizing ? 1 : 0) + (accentLine ? 1 : 0);
    var partOf = function (i) { return ' (' + i + ' of ' + nParts + ')'; };
    // `_rb_part` is the STABLE machine-readable half-marker. The cart guard needs
    // to tell the metal half from the stone half, and `Component` is customer copy
    // that changes with the wave ("Setting" / "Both mountings") — matching on it
    // would break the guard the next time the wording is edited. Underscore
    // prefix, so Edge's cart templates never print it.
    var settingProps = Object.assign({}, shared, {
      '_rb_part': 'setting',
      'Component': (qty > 1 ? 'Both mountings' : 'Setting') + partOf(1)
    });
    // A pendant has no finger size, and neither does an earring. Sending
    // "Ring size: To be confirmed" on either would put a line on the packing slip
    // that Stuller cannot act on and the customer never chose.
    if (RB.isSized(build)) settingProps['Ring size'] = build.size || 'To be confirmed';
    // the empty mounting names the accent line it is made with, so the order sheet re-plans the set from the setting
    if (accentLine) settingProps['_rb_accent'] = accentLine.id;
    /* THE ENGRAVING RIDES ON THE SETTING LINE (owner 2026-10-06), in three visible properties so checkout, the order
       email, the admin order and the packing slip each say what to ask the supplier for: the text, the font and the
       fill. "No fill" is the supplier's default and writes no color line. Set before `Returns`, which stays last. */
    if (engrave) {
      settingProps['Inside engraving'] = engrave.text;
      settingProps['Engraving font'] = engrave.font;
      if (engrave.color !== RB.ENGRAVE_NONE) settingProps['Engraving color'] = engrave.color;
    }
    // 🚨 THE BACKS RIDE ON THE SETTING LINE (owner rule 2026-08-04: backs are
    // INCLUDED with every pair). Their wholesale cost is already inside the
    // mounting price — sync_settings folds ONE back into each mounting, and a pair
    // is two mountings — so this is NOT a charge. It is the SKU, because Stuller
    // ships the parcel and will not put a back in it on the strength of our
    // pricing. `_rb_back` is underscore-prefixed so Edge never prints it; the
    // visible line is the plain-English one beneath.
    //
    // ⚠️ `sv[2]` is per VARIANT, not per design: post type is a property of the
    // SKU and 30 of 331 designs stock more than one, so a design-level answer
    // would send a friction back to a threaded post. Absent (an ear cuff, a
    // seed-pearl post) means the mounting genuinely takes no back, and saying
    // nothing is then correct — do not substitute a default here.
    if (sv[2]) {
      settingProps['_rb_back'] = sv[2];
      settingProps['_rb_back_qty'] = String(qty);
      settingProps['Earring backs'] = qty > 1 ? 'Included (pair)' : 'Included';
    }
    var stoneProps = Object.assign({}, shared, {
      '_rb_part': 'stone',
      'Component': (qty > 1 ? 'Both center stones' : 'Center stone') + partOf(2)
    });
    // ⚠️ TWO LINE ITEMS AT QUANTITY 2, NOT FOUR LINE ITEMS. Shopify merges lines
    // with identical id AND identical properties, so posting the same variant
    // twice would collapse to one line of quantity 2 anyway — but only by
    // accident, and only while the properties happen to match. Ask for what we
    // mean. It also keeps the cart readable: a pair is two lines, like every
    // other build.
    if (half) { settingProps['Quantity'] = 'Pair'; stoneProps['Quantity'] = 'Pair'; }
    /* FINAL SALE (owner 2026-09-15, in line with the supplier's returns policy: custom
       stone setting and sizing are special orders and cannot be returned). A visible
       property, so Shopify prints it under the piece at checkout, in the order
       confirmation email and on the order in admin. Set LAST so it prints after the
       piece's own details. The cart card reads it back off the line (rb-cart-pair.js)
       and never retypes it.
       ⚠️ ON THE PIECE'S OWN PARTS ONLY — setting, centre and side stones. NOT on a
       matching chain: it is an unmodified finished chain we also sell on its own,
       and it stays returnable. The value leads with "Final sale" because the cart
       drawer shows a property's value with its label visually hidden. */
    settingProps['Returns'] = RB.FINAL_SALE;
    stoneProps['Returns'] = RB.FINAL_SALE;

    var items = [
      { id: sv[0], quantity: qty, properties: settingProps },
      { id: opts.stoneVariantId, quantity: qty, properties: stoneProps }
    ];

    /* ---- SIDE STONES -------------------------------------------------------
       🚨 ONE SKU AT QUANTITY N, NOT N LINES — and that is a measurement, not a
       convenience. Across the mounting cohort, **7,916 of 7,916** two-side base
       SKUs carry two IDENTICAL side seats (same shape, same millimetres, same
       setting type). Not most: all of them. So a pair of sides is one calibrated
       stone ordered twice, which is exactly the shape of the earring pair the
       `RB.pairable` gate already guards.

       ⚠️ `seats` IS NOT ALWAYS 2. The cohort holds 111 designs at 2 side seats,
       10 at 1 and 2 at 4. Defaulting to 2 would under-order the four-seat designs
       by half — the same arithmetic the earring back got right by counting
       mountings rather than pieces.

       🚨 A SIDE PAIR MUST BE CALIBRATED. A one-of-one serialized stone cannot be
       supplied twice: Stuller would confirm the order and it would arrive short.
       This is the same gate the earring wave applies, applied for the same reason
       — and unlike the earring case it fires on the RING wave too, where
       `pairQty` is 1 and the old guard therefore never ran. */
    if (willSide) {
      var seats = opts.side.seats || 2;
      if (!RB.pairable(opts.side.stone)) {
        return Promise.reject(new Error(secondStone
          ? 'that gemstone is one of a kind, so it cannot be chosen as the second stone'
          : 'that side gemstone is one of a kind, so it cannot be supplied as a matched set'));
      }
      /* 🚨 AND IT MUST FIT THE SEAT THIS BUILD ACTUALLY HAS. Pairable says Stuller
         can supply two; it says nothing about whether they fit. A side chosen for a
         5.00 x 3.00 seat carried onto a 3.75 x 2.50 one passed every gate above and
         reached the cart. This is the load-bearing check — the step bar, the review
         gate and the CTA are all bypassable by a pasted URL. */
      if (!RB.sideFits(build, opts.side.stone)) {
        return Promise.reject(new Error(secondStone
          ? 'that second stone does not fit this setting \u2014 choose a second stone for the center stone you have now'
          : 'those side stones do not fit this setting\u2019s side seats \u2014 choose side stones for the center stone you have now'));
      }
      items.push({
        id: opts.side.variantId,
        quantity: seats * qty,
        properties: Object.assign({}, shared, {
          '_rb_part': 'side',
          // ⚠️ The word "Side" must lead: rb-cart-pair.js's legacy role fallback
          // reads this copy, and "Side stones" CONTAINS "stone" — tested in the
          // wrong order it classifies a side as the centre, and a centre-less
          // mounting then survives repair().
          /* ⚠️ THIS ONE STATES THE SEAT COUNT AS WELL AS THE POSITION, because
             `seats` is 1, 2 or 4 across the cohort and the quantity on the line is
             `seats * qty` — a customer reading "Side stones (3 of 3)" beside a
             quantity of 4 has no way to tell an intended set of four from a
             mistake. */
          /* A SECOND STONE IS ONE STONE AND SAYS SO IN WORDS (owner Q6, 2026-09-25): "Second stone
             (3 of 3)". rb-cart-pair.js reads it as ONE seat (seatsOf) and, for a line older than
             `_rb_part`, as a side (its 'second stone' rung sits above 'stone'). */
          'Component': (secondStone && seats === 1 ? 'Second stone' : 'Side stones × ' + seats) + partOf(3),
          'Returns': RB.FINAL_SALE
        })
      });
    }

    /* ---- ACCENT STONES -----------------------------------------------------
       The charge on its own Accent Stones line at the ring's quantity, after the side stones and before a sizing
       charge. NOT optional the way a chain is: the setting line is the empty mounting, so rb-cart-pair.js removes the
       ring with this line and this line with the ring (role 'accent'). The Component names what is set, in words the
       order sheet and the supplier order read: the line, its grade and the stone count. */
    if (accentLine) {
      items.push({
        id: Number(accentLine.variantId),
        quantity: qty,
        properties: Object.assign({}, shared, {
          '_rb_part': 'accent',
          '_rb_accent': accentLine.id,
          'Component': 'Accent stones: ' + RB.accentName(accentLine) + ', ' + accentLine.grade + ', ' +
                       accentLine.stones + ' stones' + partOf(3 + (willSide ? 1 : 0)),
          'Returns': RB.FINAL_SALE
        })
      });
    }

    /* ---- A MATCHING CHAIN --------------------------------------------------
       An OPTIONAL fourth part, and the only one on a build that is not part of the
       piece itself: a pendant without a chain is a finished pendant, which is why
       this is a panel on the review step and not a step of its own. The gate that
       decided it fits ran in `matching-chain.js` — the storefront's one copy of
       `chain_fit.fits()` — and is deliberately NOT repeated here.

       ⚠️ IT IS NOT LOAD-BEARING, AND `rb-cart-pair.js` MUST AGREE. Removing the chain
       from the cart leaves a whole pendant; removing the pendant leaves a chain we
       sell on its own as a necklace. So it is exactly as removable as a side stone
       and must never be treated the way the SETTING is — the mounting is the one half
       that cannot stand alone, and that asymmetry is the whole point of the orphan
       guard.

       ⚠️ QUANTITY IS `qty`, WHICH IS 1 ON THE ONLY WAVE THAT REACHES HERE. Written as
       `qty` rather than a literal 1 so it cannot silently under-order if a paired wave
       ever gains a chain — the same arithmetic the earring back got right by counting
       mountings rather than pieces. */
    if (willChain) {
      items.push({
        id: Number(opts.chain.i),
        quantity: qty,
        properties: Object.assign({}, shared, {
          '_rb_part': 'chain',
          // ⚠️ "Matching chain" contains none of the words the legacy role fallback
          // in rb-cart-pair.js matches on ('side', 'stone', 'setting', 'mounting'),
          // which is why 'chain' is tested FIRST there rather than relied upon to
          // miss. Self-describing on its own line, because the cart drawer renders
          // the property's label visually-hidden and shows only this value.
          'Component': 'Matching chain' + partOf(nParts)
        })
      });
    }

    /* ---- RING SIZING -------------------------------------------------------
       The charge on its own Ring Sizing line, at the ring's quantity, carrying the build's properties -- so the cart
       draws it inside the ring ("Ring sizing, size 6 · $22", rb-cart-pair.js) and moves and removes it with the ring.
       ⚠️ `_sr_pair`, ON THE SETTING AND THE CHARGE: the token a ring page's charge carries. A cart script older than this
       release keeps a Ring Sizing line only while another line shares its token, and would take this one out of the
       cart as an orphan. The cart guard (main-cart-items.liquid) holds checkout unless this is the charge the size
       costs, on this build, at the setting's quantity. */
    if (sizing) {
      settingProps['_sr_pair'] = id;
      items.push({
        id: Number(sizing.variantId),
        quantity: qty,
        properties: Object.assign({}, shared, {
          '_rb_part': 'sizing',
          '_sr_pair': id,
          // Last of the parts: a chain (pendant) and a sizing charge (ring) are never on the same build.
          'Component': 'Ring sizing, size ' + RB.sizeLabel(build.size) + partOf(nParts)
        })
      });
    }

    /* CHECKOUT ORDER (owner 2026-09-21): Shopify lists the newest line first, so the parts are SENT in reverse --
       checkout, the order email and the order in admin then read setting, centre, sides, and the chain or the Ring
       Sizing charge last, the order of the "part 1 of N" labels. The cart groups by property, never by position. */
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: items.slice().reverse() })
    }).then(function (r) {
      return r.json().then(function (j) {
        if (!r.ok) throw new Error(j.description || j.message || 'Could not add to cart');
        return j;
      });
    });
  };

  /* ------------------------------------------------- sort + filter drawer */
  // Shared by step 1 and step 2 so the two surfaces cannot drift apart. Both
  // benchmarks put a plain dropdown at the right of the results bar and a
  // Filters control at the left; on a phone both collapse into one sticky bar.
  RB.mountSort = function (wrap, options, current, onPick) {
    if (!wrap) return;
    wrap.innerHTML = '';
    var label = (options.filter(function (o) { return o.value === current; })[0] || options[0]).label;
    var btn = el('button', {
      class: 'rb-sort__btn', type: 'button', 'aria-expanded': 'false',
      text: 'Sort by: ' + label
    });
    var menu = el('div', { class: 'rb-sort__menu', hidden: 'hidden' });
    options.forEach(function (o) {
      menu.appendChild(el('button', {
        class: 'rb-sort__opt', type: 'button',
        'aria-selected': String(o.value === current),
        text: o.label,
        onclick: function () { onPick(o.value); }
      }));
    });
    btn.addEventListener('click', function () {
      var open = !menu.hidden;
      menu.hidden = open;
      btn.setAttribute('aria-expanded', String(!open));
    });
    /* ⚠️ ONE CLOSER PER CONTROL, NOT ONE PER DRAW (2026-09-17). The pickers now redraw in place on every filter
       (assets/sr-client-filters.js), and each call used to add another document listener holding the menu of
       that draw. The closer reads the menu that is in `wrap` now. */
    if (!wrap.__rbSortCloser) {
      wrap.__rbSortCloser = true;
      document.addEventListener('click', function (e) {
        if (wrap.contains(e.target)) return;
        var m = wrap.querySelector('.rb-sort__menu'), b = wrap.querySelector('.rb-sort__btn');
        if (m) m.hidden = true;
        if (b) b.setAttribute('aria-expanded', 'false');
      });
    }
    wrap.appendChild(btn);
    wrap.appendChild(menu);
    return { close: function () { menu.hidden = true; } };
  };

  /* =================================================== THE FILTER PANEL ====
     🚨 OWNER RULE 2026-08-12: the builder's filters must BE the gemstone panel,
     not a lookalike. *"The look needs to be consistent and also shapes, colors,
     etc. should be consistent as well."*

     So these emit `stone-browse.css`'s OWN classes — `sb__row`, `sb__label`,
     `sb__scroll`, `sb-shape` — and the section loads that stylesheet. Nothing is
     re-implemented here, which is the only version of "consistent" that survives
     someone restyling the species pages later: change it there and the builder
     follows, because it is literally the same rule set.

     ⚠️ A PANEL ROW IS NOT COLLAPSIBLE. The old `.rb-facet` had a chevron and a
     `data-open` state; the gemstone panel has none — every axis is always visible
     with its label on the left. Dropping the collapse is what makes the two
     surfaces read as one control, and it removes a state that could disagree
     between them. */
  RB.panelRow = function (label, sub, body, mod) {
    return el('div', { class: 'sb__row' + (mod ? ' ' + mod : '') }, [
      el('span', { class: 'sb__label' },
        [document.createTextNode(label)].concat(sub ? [el('em', { text: sub })] : [])),
      el('div', { style: 'min-width:0' }, body)
    ]);
  };

  // Two axes side by side on a wide screen, stacked below — the panel's own
  // `.sb__cols`. Either half may legitimately be absent, and stone-browse.css
  // already spans a lone survivor across both tracks.
  RB.panelCols = function (rows) {
    return el('div', { class: 'sb__cols' }, rows.filter(Boolean));
  };

  /* ======================================================= FACET HELP ========
     ONE MAP, EVERY RAIL (owner, 2026-09-03; widened 2026-09-14). The gemstone
     search's filter rail and the builders' step-2 rail each drew a sub-line under
     the label, and the two copies had ALREADY drifted — "graded on hue, not the D–Z
     scale" on one, "graded on hue and intensity, not the D–Z scale" on the other.
     The sub-lines are gone (they set the row height, and a rail that has to match
     the reference row for row cannot carry a six-line note); the copy lives once,
     in assets/sr-facet-help.js, and each rail draws it as a tap-to-open panel beside
     the label through RB.helpButton(key). A row names WHICH help it wants and never
     the wording — the chain_fit.py / matching-chain.js lesson, one layer over.

     ⚠️ `RB.panelRow` still takes a `sub` (the `<em>` sub-line): the settings step
     draws "center stone" under Shape through it and stone-browse.liquid's Origin
     row renders "where known" the same way, so `.sb__label em` stays in
     stone-browse.css. */
  /* ================================================= FACET DROPDOWNS ========
     ONE `.sb-dd` IMPLEMENTATION, TWO RAILS. The gemstone search built these
     locally in diamond-browse.js; the builders' rail had none at all, which is why
     its Quality facet first shipped as a full-width chip row that did not match
     the row beside it (owner, 2026-09-06: "we should just have all the options
     there like in the gem search ... we can match that").

     🚨 IT LIVES HERE RATHER THAN BEING COPIED, for the reason this repo keeps
     paying for elsewhere: two implementations of one control agree until somebody
     edits one. The markup, the "Any" / "N selected" summary and the zero-count
     rule are stated once.

     ⚠️ THE TWO RAILS NAVIGATE DIFFERENTLY AND THAT IS THE ONE REAL DIFFERENCE.
     The browse panel is URL-driven, so its chips must be real `<a href>` — they
     are shareable and crawlable. The builder applies state through a callback and
     rewrites the URL itself, so its chips are `<button>`. Pass EITHER `hrefFor`
     or `onToggle`; passing neither is a control that cannot be operated, so it
     throws rather than rendering something dead.

     ⚠️ ZERO-COUNT VALUES ARE SKIPPED UNLESS ACTIVE — hiding the number would not
     make an empty facet worth offering, and an ACTIVE one has to stay so it can
     be switched off. Same rule `sb-dd-chips.liquid` states for the Liquid panel. */
  RB.ddValue = function (applied) {
    if (!applied.length) return 'Any';
    return applied.length === 1 ? applied[0] : applied.length + ' selected';
  };

  RB.dropdown = function (label, valueText, active, kids, help) {
    var sum = RB.el('summary', { class: 'sb-dd__btn' }, [
      RB.el('span', { class: 'sb-dd__k', text: label }),
      RB.el('span', { class: 'sb-dd__v', text: valueText })
    ]);
    var chev = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    chev.setAttribute('class', 'sb-dd__chev');
    chev.setAttribute('viewBox', '0 0 12 12');
    chev.setAttribute('fill', 'none');
    chev.setAttribute('stroke', 'currentColor');
    chev.setAttribute('stroke-width', '1.5');
    chev.setAttribute('aria-hidden', 'true');
    chev.innerHTML = '<path d="M2 4.5L6 8.5l4-4"></path>';
    sum.appendChild(chev);
    // 🚨 `name` MAKES THE ROW AN EXCLUSIVE ACCORDION (owner, 2026-09-06: several
    // panels could be open at once, covering each other). Every `.sb-dd` shares one
    // group, so opening any panel closes whichever was open — the one-at-a-time rule
    // `RB.helpButton` already enforces for tooltips. The Liquid snippets that render
    // this same control carry the identical attribute; it is the platform feature
    // rather than a script precisely because those pages share no JS file with this
    // one, so a scripted closer would have had to exist two or three times.
    var dd = RB.el('details', { name: 'sb-dd',
                                class: 'sb-dd' + (active ? ' is-active' : '') },
      [sum, RB.el('div', { class: 'sb-dd__panel' }, kids)]);
    // THE "?" SITS BESIDE THE TRIGGER, NOT INSIDE IT (owner 2026-09-14: Origin,
    // Treatment and Quality had copy and guide pages but no button). A <button>
    // inside <summary> is invalid interactive content — the tap would toggle the
    // dropdown too — so the two are siblings in `.sb-dd-h`, which is also the
    // help panel's positioned box. `help` is a sr-facet-help.js key; none, or an
    // unknown one, returns the bare <details> exactly as before.
    var hb = help ? RB.helpButton(help) : null;
    return hb ? RB.el('span', { class: 'sb-dd-h' }, [dd, hb]) : dd;
  };

  /* o = { label, sub, help (sr-facet-help.js key), values:[[key, label, count], ...], applied:[key],
           skip:[lowercase key], order:[key] (leading, in THIS order),
           hrefFor:fn(nextKeys)->url  |  onToggle:fn(nextKeys) } */
  RB.ddChips = function (o) {
    var applied = o.applied || [], skip = o.skip || [], order = o.order || [];
    var live = (o.values || []).filter(function (v) {
      if (skip.indexOf(String(v[0]).toLowerCase()) >= 0) return false;
      return v[2] > 0 || applied.indexOf(v[0]) >= 0;
    });
    if (!live.length) return null;
    // ⚠️ ORDER COMES FROM `order`, NOT FROM THE INPUT. A value it does not name
    // keeps its place at the END rather than being dropped, so a grade the
    // supplier adds still shows.
    if (order.length) {
      live.sort(function (a, b) {
        var ia = order.indexOf(a[0]), ib = order.indexOf(b[0]);
        if (ia < 0) ia = order.length;
        if (ib < 0) ib = order.length;
        return ia - ib;
      });
    }
    if (!o.hrefFor && !o.onToggle) {
      throw new Error('RB.ddChips: needs hrefFor or onToggle');
    }
    var chips = live.map(function (v) {
      var on = applied.indexOf(v[0]) >= 0;
      var next = on ? applied.filter(function (x) { return x !== v[0]; })
                    : applied.concat([v[0]]);
      var n = v[2].toLocaleString();
      // ⚠️ THE TRAILING SPACE IS PART OF THE ACCESSIBLE NAME, not decoration:
      // without it the chip announces as "One Of A Kind160".
      var kids = [document.createTextNode(v[1] + ' '),
                  RB.el('span', { class: 'sb-chip__n', text: n })];
      var attrs = { class: 'sb-chip' + (on ? ' is-active' : ''), role: 'listitem',
                    title: v[1] };
      if (o.hrefFor) {
        attrs.href = o.hrefFor(next);
        attrs['aria-pressed'] = on ? 'true' : null;
        return RB.el('a', attrs, kids);
      }
      attrs.type = 'button';
      attrs['aria-pressed'] = on ? 'true' : 'false';
      attrs.onclick = function () { o.onToggle(next); };
      return RB.el('button', attrs, kids);
    });
    var kids = [];
    if (o.sub) kids.push(RB.el('p', { class: 'sb-dd__sub', text: o.sub }));
    kids.push(RB.el('div', { class: 'sb-dd__chips', role: 'list' }, chips));
    var appliedLabels = live.filter(function (v) { return applied.indexOf(v[0]) >= 0; })
                           .map(function (v) { return v[1]; });
    return RB.dropdown(o.label, RB.ddValue(appliedLabels),
                       appliedLabels.length > 0, kids, o.help);
  };

  /* THE QUALITY LADDER, BEST FIRST, AND THE ONLY COPY OF IT.
     Three surfaces order this facet — the browse panel's dropdown (Liquid, via
     `sb-dd-chips`'s `first:`), the all-stones rail and the builders' stone rail —
     and two of the three are JavaScript, so they read it from here. A second array
     is the `chain_fit.py` / `matching-chain.js` failure in miniature: two lists
     that agree until someone edits one, on a facet whose ORDER is the whole point.
     ⚠️ A value not in this list keeps its place at the END rather than being
     dropped, so a grade Stuller adds still shows up. */
  RB.QUALITY_ORDER = ['One Of A Kind', 'AAA', 'AA', 'A',
                      'AB', 'B', 'Standard', 'Commercial', 'Eye-Clean'];

  /* THE HELP CONTROL LIVES IN assets/sr-facet-help.js (moved 2026-09-14), with the
     one copy of its wording. It moved so the Liquid-drawn filter panels — species,
     shape, month, variety and jewelry pages, which never load this engine — can show
     the same "?" from the same text. These two keep their names so no call site in
     this file, diamond-browse.js or ring-builder-stones.js changed.
     ⚠️ EVERY SECTION THAT LOADS THIS FILE LOADS sr-facet-help.js BEFORE IT
     (stone-search, diamond-search, ring-builder). Without it these return null and
     the rails simply draw no "?" — never an error. That is also what happens under
     JavaScriptCore in verify_side_stones / verify_cart_build / verify_builder_fit,
     where neither `window.SRHelp` nor a real `document` exists.
     RB.helpButton(key) → the button wrapper, or null for an unknown key.
     RB.attachHelp(label, key) → glues it to the label's last word, so the "?" sits
     beside the words and never wraps alone under them (owner, 2026-09-14). */
  var srHelp = function () {
    return (typeof window !== 'undefined' && window.SRHelp) || null;
  };
  RB.helpButton = function (key) {
    var h = srHelp();
    return h && key ? h.button(key) : null;
  };
  RB.attachHelp = function (label, key) {
    var h = srHelp();
    return h && key ? h.attach(label, key) : null;
  };

  /* ==================================================== THE RANGE SLIDERS ====
     🚨 ONE CONTROL, TWO RAILS (owner, 2026-09-02, with
     /collections/all-gemstones?view=stone-search beside the builder's gemstone
     step: the two rails must look identical row for row). The gemstone search's
     ordered-scale slider lived in assets/diamond-browse.js and the builder drew a
     library-based lookalike — same idea, a different track, handle, readout and
     ladder. The three functions below ARE the reference control, moved onto the
     engine so both pages render it: diamond-browse.js delegates its own
     `rangeControl` / `scaleRow` / `contRow` here, and ring-builder-stones.js builds
     its Color / Clarity / Cut / Carat / Price rows from the same three calls.

     THE ENGINE OWNS RENDERING AND SCALE CONVERSION ONLY. Nothing here reads a URL
     or calls a page's apply(): every `onChange` hands back a pair of values —
     handle POSITIONS for a graded ladder, raw NUMBERS for a continuous axis — and
     the caller decides what to do with them. The browse panel writes its URL
     patch, the builder writes its own; two URL contracts, one control.

     Two overlaid native range inputs on one track — the construction
     stone-browse.css already styles for the carat and millimetre bands, so it
     needs no library and inherits the panel's look exactly. Values are INDICES
     into an ordered ladder, which is what makes "G and better" expressible.

     ⚠️ SPLIT IN TWO: `rangeControl` builds the slider, `rangeRow` wraps it in a
     labelled panel row. The dropdowns need the CONTROL ONLY — their <summary> IS
     the label, and a row's label column inside a 260px popup would leave the track
     about 90px wide. That is the same split `sb-band-row.liquid` expresses with
     its `bare` flag, and it is why the two look identical on both pages. */
  RB.rangeControl = function (label, labels, lo, hi, onChange, fmt, showScale, initial, typed) {
    var wrap = el('div', { class: 'sb-range is-upgraded' + (showScale ? '' : ' is-fielded') });
    // THE WHOLE LADDER, ALWAYS ON SCREEN — out-of-range rungs GRAYED, never hidden.
    // This is the point of an ordered-scale slider and it is what both benchmarks
    // do: seeing D–K in full is what silently teaches a first-time buyer what "G"
    // means. Without it the control shows only the two ends a shopper has already
    // chosen, which teaches nothing and hides the scale it is filtering on.
    var scale = showScale
      ? el('div', { class: 'db-scale' }, labels.map(function (t) {
          return el('span', { text: t });
        }))
      : null;
    var rng = el('div', { class: 'sb-rng' });
    var track = el('div', { class: 'sb-rng__track' });
    var fill = el('div', { class: 'sb-rng__fill' });
    track.appendChild(fill);
    var max = labels.length;
    var iLo = el('input', { class: 'sb-rng__i sb-rng__i--lo', type: 'range',
      'aria-label': label + ' minimum', min: '0', max: String(max), step: '1' });
    var iHi = el('input', { class: 'sb-rng__i sb-rng__i--hi', type: 'range',
      'aria-label': label + ' maximum', min: '0', max: String(max), step: '1' });
    iLo.value = lo; iHi.value = hi;
    rng.appendChild(track); rng.appendChild(iLo); rng.appendChild(iHi);

    // 🚨 THE BOXES ACCEPT TYPING ONLY WHERE THE CALLER SUPPLIED THE WHOLE COMMIT
    // PATH (owner decision 2026-09-02, on a Codex review finding: moving this control
    // onto the engine had made both boxes readonly, so a shopper lost the exact price
    // and carat entry the retired builder slider had — and the owner called the
    // read-only boxes a bug on the gemstone search as well, not only a regression in
    // the builder. One control, so one repair covers both pages.)
    // `typed` is { parse: text -> number|null, toPos: number -> position,
    // show: number -> text, onTyped(loValue, hiValue) } — the two the review named
    // plus the two this control cannot derive by itself: a typed value has to become
    // a thumb position while the shopper types and a text the box can print once
    // they commit, and both of those live with the scale in RB.contRow. All four or
    // none, so a future caller without a commit path cannot become editable by
    // accident; a graded ladder draws no boxes at all and is untouched.
    // The attributes are the landing pages' own (stone-browse.js fieldHtml): the
    // type stays `text` because the value carries "$", "," and a unit suffix.
    var typedOn = !!(typed && typed.parse && typed.toPos && typed.show && typed.onTyped);
    function box(side, suffix) {
      return el('input', { class: 'sb-rng__in sb-rng__in--' + side, type: 'text',
        readonly: typedOn ? null : 'readonly', 'aria-label': label + ' ' + suffix,
        inputmode: typedOn ? 'decimal' : null,
        autocomplete: typedOn ? 'off' : null,
        spellcheck: typedOn ? 'false' : null });
    }
    var outLo = box('lo', 'from'), outHi = box('hi', 'to');
    var out = el('div', { class: 'sb-rng__out' }, [
      el('span', { class: 'sb-rng__f sb-rng__f--lo' }, [outLo]),
      el('span', { class: 'sb-rng__f sb-rng__f--hi' }, [outHi])
    ]);

    // 🚨 THE RANGE IS HALF-OPEN — [lo, hi) — AND BOTH HANDLES SLIDE 0..max, SO THE
    // ENDS HAVE TO BE CLAMPED OR THEY SELECT NOTHING.
    // `max` is the number of RUNGS, so the valid rung indices are 0..max-1 while
    // `hi` is an exclusive BOUND running 1..max. Drag the low handle to the top and
    // both inputs read `max`, which resolved to [max, max+1) — a span past the end
    // of the ladder that matches ZERO stones. That is the "no D to D" bug: asking
    // for the single best grade returned an empty grid instead of the D stones.
    // Pinning at the top has to walk the LOW bound down instead of the high bound
    // up. Both handles must keep the same 0..max scale or the two thumbs stop
    // aligning on one track, which is why this is fixed here and not by giving the
    // inputs different max attributes.
    function bounds() {
      var a = Math.min(+iLo.value, +iHi.value), b = Math.max(+iLo.value, +iHi.value);
      if (b <= a) {
        if (a >= max) { a = max - 1; b = max; }   // pinned at the top -> last rung
        else b = a + 1;                            // anywhere else -> one rung wide
      }
      return [a, b];
    }
    // 🚨 UNTIL A HANDLE MOVES, THE BOXES SHOW WHAT IS ACTUALLY FILTERING.
    // `initial` is the caller's real [lo, hi] as text. Everything else here works in
    // HANDLE POSITIONS and derives a value back from them, and that round trip is
    // lossy by construction: a position is quantised, a price is not. Arriving with
    // `pmax=1000` on a scale whose step was $1,982, both handles quantised into the
    // same step, `bounds()` widened the span by one, and the box printed that step's
    // top edge — **$2,107 over a filter doing $1,000** (owner, 2026-08-10). No
    // amount of scale tuning fixes that; only not re-deriving does.
    // Once the shopper drags, the derived value IS the truth and the readout follows.
    var pristine = !!initial;
    function paint() {
      var ab = bounds(), a = ab[0], b = ab[1];
      fill.style.insetInlineStart = (a / max * 100) + '%';
      fill.style.width = ((b - a) / max * 100) + '%';
      // ONE PAIR OF STRINGS FEEDS THE BOXES AND THE HANDLES. A native range input
      // announces its `value`, and here that value is a handle POSITION — so a
      // screen reader met "slider, 282" where the box beside it read "$1,450".
      // `aria-valuetext` carries the very text the box (or the ladder) shows for
      // the current bounds, pristine rule included, so the spoken value can never
      // disagree with the printed one (assets/sr-scale.js, the rule for a new
      // slider). The reference never set it; both handles carry it now.
      var tLo, tHi;
      if (pristine) { tLo = initial[0]; tHi = initial[1]; }
      else {
        tLo = fmt ? fmt(a) : (labels[a] || labels[0]);
        tHi = fmt ? fmt(b) : (labels[b - 1] || labels[labels.length - 1]);
      }
      // ⚠️ NEVER YANK THE TEXT OUT FROM UNDER SOMEONE MID-EDIT (the landing pages'
      // own guard, stone-browse.js paint()): this also runs on every keystroke's
      // preview and while the OTHER handle is being dragged, and rewriting a focused
      // box would delete what is being typed into it.
      if (document.activeElement !== outLo) outLo.value = tLo;
      if (document.activeElement !== outHi) outHi.value = tHi;
      iLo.setAttribute('aria-valuetext', tLo);
      iHi.setAttribute('aria-valuetext', tHi);
      // Repainted on every `input`, so the ladder dims live as the handle moves
      // rather than only after the page reloads on `change`.
      if (scale) {
        for (var i = 0; i < scale.children.length; i++) {
          scale.children[i].classList.toggle('is-out', i < a || i >= b);
        }
      }
      return [tLo, tHi];
    }
    function commit() {
      // Same clamp as the readout — the value written to the URL must be the value
      // the shopper was shown, or the grid comes back disagreeing with the slider.
      var ab = bounds();
      onChange(ab[0], ab[1]);
    }
    function drag() { pristine = false; paint(); }
    iLo.addEventListener('input', drag);
    iHi.addEventListener('input', drag);
    iLo.addEventListener('change', commit);
    iHi.addEventListener('change', commit);

    /* TYPED ENTRY, MIRRORED FROM THE LANDING PAGES' CONTROL — stone-browse.js's
       parse / resolve / preview / typed — rather than invented. Each rule there is
       the record of a bug, and they are kept whole:
         • `input` only moves the thumb and the fill and NEVER commits: a text field
           fires it on every keystroke, so committing there navigates on the "1" of
           "150"; and before the preview existed the track sat still until the field
           lost focus, so the slider looked broken until you clicked away.
         • `change` is the commit — it fires exactly when a typed value is FINISHED,
           on blur and on Enter — and Enter is turned into a blur so both routes run
           the identical path rather than two that can drift.
         • a typed number never crosses the other handle: a minimum above the
           maximum returns an empty grid, which reads as a broken filter rather than
           an impossible range. Typing is just the other way to move a handle.
         • gibberish restores the applied value and applies nothing.
       🚨 THE ONE THING THAT IS NOT A MIRROR: THE TYPED NUMBER REACHES THE CALLER
       EXACTLY, BYPASSING THE POSITION ROUND TRIP. The thumb moves to the NEAREST
       position for feedback, but `onTyped` is handed the parsed value itself, never
       the value AT that position — "values are exact, never snapped" (assets/
       sr-scale.js rule 1; the budget finder is the one documented exception), and
       the `pristine` note above is the bug the round trip causes: a filter doing
       $1,000 printed $2,107. The retired builder slider had this right — its commit
       handed the typed number straight to onChange — and this keeps it. */
    function wireTyped() {
      var startPos = { lo: +iLo.value, hi: +iHi.value };
      function handle(side) { return side === 'lo' ? iLo : iHi; }
      function posFor(side, v) {
        var p = Math.min(max, Math.max(0, Math.round(typed.toPos(v))));
        // The drag path's no-crossing rule, on the thumb the shopper is watching.
        return side === 'lo' ? Math.min(p, +iHi.value) : Math.max(p, +iLo.value);
      }
      function preview(side, raw) {
        var v = typed.parse(raw);
        if (v === null) return;
        handle(side).value = posFor(side, v);
        paint();   // the focused box keeps its text — the guard in paint()
        // The handle being typed at announces what is being asked for; paint() has
        // just left it on the previously applied value.
        handle(side).setAttribute('aria-valuetext', typed.show(v));
      }
      function commitTyped(side, raw) {
        var mine = side === 'lo' ? outLo : outHi, other = side === 'lo' ? outHi : outLo;
        var v = typed.parse(raw);
        if (v === null) {
          // Gibberish: put the thumb back where this edit found it and restore the
          // applied value to the box. The commit is the end of the edit, so the box
          // is written directly rather than through the focus guard.
          handle(side).value = startPos[side];
          mine.value = paint()[side === 'lo' ? 0 : 1];
          return;
        }
        // The other end is what its own box shows — the applied value at the
        // precision the URL carries (`show` of a number, or the caller's `initial`),
        // so the pair handed on is exactly the pair on screen.
        var o = typed.parse(other.value);
        if (o === null) o = typed.parse(paint()[side === 'lo' ? 1 : 0]);
        if (o === null) { mine.value = paint()[side === 'lo' ? 0 : 1]; return; }
        v = side === 'lo' ? Math.min(v, o) : Math.max(v, o);
        var loV = side === 'lo' ? v : o, hiV = side === 'lo' ? o : v;
        handle(side).value = posFor(side, v);
        // The boxes keep showing what was asked for, through the SAME `initial` rule
        // an arriving URL uses — a typed value is as exact as one in the address bar,
        // and re-deriving it from the thumb is the $2,107 bug. Not a second display
        // rule: the readout still follows the thumb the moment a handle is dragged.
        initial = [typed.show(loV), typed.show(hiV)];
        pristine = true;
        mine.value = paint()[side === 'lo' ? 0 : 1];
        startPos = { lo: +iLo.value, hi: +iHi.value };
        typed.onTyped(loV, hiV);
      }
      [[outLo, 'lo'], [outHi, 'hi']].forEach(function (pair) {
        var side = pair[1];
        pair[0].addEventListener('focus', function () { startPos[side] = +handle(side).value; });
        pair[0].addEventListener('input', function () { preview(side, this.value); });
        pair[0].addEventListener('change', function () { commitTyped(side, this.value); });
        pair[0].addEventListener('keydown', function (e) {
          if (e.key === 'Enter') { e.preventDefault(); this.blur(); }
        });
      });
    }
    if (typedOn) wireTyped();
    paint();
    if (scale) wrap.appendChild(scale);
    wrap.appendChild(rng);
    // The two value boxes are redundant once the ladder is on screen — the rung
    // names ARE the readout — and Blue Nile drops them on the graded 4Cs for the
    // same reason while keeping them on carat and price, where the value is a
    // number the shopper may want to read exactly.
    if (!scale) wrap.appendChild(out);
    return wrap;
  };

  // `help` is a sr-facet-help.js key (2026-09-03) — it was `sub`, an `<em>` sub-line
  // under the label, until the sub-lines came off both rails. Same position, same
  // shape (a string or null); an unknown key draws nothing.
  RB.rangeRow = function (label, help, labels, lo, hi, onChange, fmt, showScale, initial, typed) {
    var wrap = RB.rangeControl(label, labels, lo, hi, onChange, fmt, showScale, initial, typed);
    var lab = el('span', { class: 'sb__label' }, [document.createTextNode(label)]);
    RB.attachHelp(lab, help);
    return el('div', { class: 'sb__row sb__row--tight' },
      [lab, el('div', { class: 'sb__ranged' }, [wrap])]);
  };

  /* A CONTINUOUS axis — price, carat, millimetres — on the same control. The
     handles still travel in positions; `SRScale` maps position <-> value, so the
     row's ladder is `sc.steps` unlabelled rungs and the readout is `fmt.show` of
     the value at each end. `fmt` is { raw: value -> what the caller stores,
     show: value -> what the shopper reads }; only `show` is read here, `raw` is
     the caller's business when `onChange(loValue, hiValue)` hands it two numbers.

     🚨 THE SCALE IS `SRScale`, THE STORE'S ONE MAPPING — see assets/sr-scale.js for
     the measurements and the four rules. A linear price track over this catalogue
     puts the median stone in the first 0.2% of its own travel; LOG makes travel
     proportional to RATIO. There is no local fallback on purpose: a silent linear
     fallback is exactly the 88.7%-in-one-step bug this replaced, and it would be
     invisible.

     ⚠️ RESOLVED WHEN THE ROW IS BUILT, NEVER WHEN THIS FILE LOADS.
     sections/ring-builder.liquid tags ring-builder.js BEFORE sr-scale.js — the
     engine is loaded on every builder step and the scale only where a slider is
     drawn — so a reference hoisted to this file's top would read undefined on the
     one page that needs it, and nothing would throw until a shopper opened the
     rail. verify_slider_scales.py [4] asserts the reference stays inside this
     function. */
  /* TEXT -> NUMBER FOR A TYPED BOUND — the landing pages' own rule (stone-browse.js
     parse()), copied rather than re-decided. Tolerant on purpose: our boxes print
     "$130,775", "5.01 ct" and "15.26 mm", and shoppers paste "1,250" or "$1250".
     Commas come out FIRST (they are separators inside a single number), then the
     first numeric run wins and any trailing junk — a unit, a "+", a second number —
     is ignored.
     ⚠️ TAKE THE FIRST NUMBER, DO NOT STRIP-AND-CONCATENATE. Deleting every non-digit
     turns a pasted RANGE into one huge number: "50-200" became 50200, which then
     clamped to the ceiling and returned a nearly empty result set from an input that
     looked entirely reasonable.
     🚨 A LEADING DECIMAL IS THE COMMONEST CARAT SHORTHAND AND THE FIRST PATTERN ATE IT
     (2026-09-03, found by Codex on the adversarial gate, live at the time on the search
     AND all three builders). `\d+(?:\.\d+)?` requires a digit BEFORE the point, so the
     dot is skipped and the run starts at the 5: ".5" applied **5.00 ct**, ".25" applied
     25.00, ".9 carat" applied 9. A shopper asking for a half carat was shown five-carat
     stones, or an empty grid, from an input that is ordinary trade shorthand — and it
     fails SILENTLY, because 5.00 is a perfectly valid carat filter.
     ⚠️ `\.\d*` on the first branch, not `\.\d+`, so "1." still reads 1 as it always did.
     ⚠️ THE SAME PATTERN IS IN `stone-browse.js` (the 21 species/shape/colour landing
     pages) and was fixed in the same pass — this one was copied FROM there, so fixing
     only the engine would have left the older surface broken. */
  var parseTyped = function (raw) {
    var m = String(raw).replace(/,/g, '').match(/(?:\d+(?:\.\d*)?|\.\d+)/);
    return m ? parseFloat(m[0]) : null;
  };

  RB.contRow = function (label, help, lo, hi, min, max, onChange, fmt) {
    var sc = window.SRScale.make(min, max);
    return RB.rangeRow(label, help, new Array(sc.steps), sc.toI(lo), sc.toI(hi),
      function (a, b) { onChange(sc.toV(a), sc.toV(b)); },
      function (i) { return fmt.show(sc.toV(i)); },
      false,
      // The exact values now filtering — see the `pristine` note in rangeControl.
      [fmt.show(lo), fmt.show(hi)],
      // TYPED ENTRY (owner decision 2026-09-02; see the note in rangeControl). The
      // exact parsed number goes to the SAME `onChange` the drag path calls, so the
      // caller's `fmt.raw` and its URL keys (pmin/pmax, ctmin/ctmax, the millimetre
      // keys) apply untouched — the value is only clamped to this row's own
      // [min, max] first, so a typed $1 or $9,999,999 lands on the pool's real ends
      // exactly as a thumb pinned there would.
      { parse: function (raw) {
          var v = parseTyped(raw);
          return v === null ? null : Math.min(max, Math.max(min, v));
        },
        toPos: sc.toI, show: fmt.show, onTyped: onChange });
  };

  /* 🚨 SILHOUETTES ARE CLONED, NEVER DRAWN. `snippets/gem-shape-icon.liquid` is the
     store's canonical shape set and the species pages clone it out of a hidden
     sprite; the builder used to carry its OWN hand-written `SHAPE_SVG` table, so a
     round in the builder and a round on /collections/sapphires were two different
     drawings — exactly the inconsistency the owner reported. The sprite is rendered
     by sections/ring-builder.liquid.

     ⚠️ Returns null when the sprite has no such shape, and every caller must cope:
     the builder's vocabulary and the sprite's are maintained in different files, so
     a shape can legitimately be missing. A missing icon must degrade to a labelled
     tile, never to an empty box. */
  var _sprite = null;
  RB.shapeArt = function (name) {
    if (_sprite === null) _sprite = document.querySelector('[data-rb-sprite]') || false;
    if (!_sprite) return null;
    var src = _sprite.querySelector('[data-shape="' + String(name).toUpperCase() + '"] svg');
    return src ? src.cloneNode(true) : null;
  };

  // One shape tile, identical in markup to the species pages' `.sb-shape`.
  RB.shapeTile = function (opts) {
    var a = el('button', {
      class: 'sb-shape' + (opts.on ? ' is-active' : ''), type: 'button',
      'aria-pressed': String(!!opts.on),
      title: opts.label,
      onclick: opts.onclick
    });
    var art = el('span', { class: 'sb-shape__art' });
    var ic = RB.shapeArt(opts.shape);
    if (ic) art.appendChild(ic);
    a.appendChild(art);
    a.appendChild(el('span', { class: 'sb-shape__t', text: opts.label }));
    if (opts.n != null) a.appendChild(el('span', { class: 'sb-shape__n', text: String(opts.n) }));
    return a;
  };

  // A colour/metal swatch tile. The species pages' `.sb-colour` is an <a> driven by
  // href; the builder filters in place, so this is a button wearing the same
  // classes — stone-browse.css styles them by class, not by element.
  // ⚠️ THE SWATCH COLOUR GOES IN `--sw`, NOT `background`. `.sb-colour__sw` paints
  // itself with `background-image: var(--sw)`, so a `background:` here is overridden
  // by the stylesheet and every swatch renders as an empty grey circle.
  // ⚠️ `opts.cls` IS HOW THE SHARED HUE PALETTE GETS IN, AND IT MUST WIN OVER `sw`.
  // `stone-list.css` owns the store's 26 colours as `--co-*` and applies them
  // through `.sb-colour--<hue> .sb-colour__sw` — specificity (0,2,0) against the
  // inline custom property's (0,1,0), which is the intended order and not a clash
  // (diamond-browse.js carries the same note). So a caller passes the modifier
  // class for a hue the store has a house colour for, and `sw` is only ever the
  // FLOOR for one it does not. Never copy those 26 values into a caller: two
  // palettes disagreeing about Blue is the thing this arrangement prevents.
  RB.swatchTile = function (opts) {
    var a = el('button', {
      class: 'sb-colour' + (opts.cls ? ' ' + opts.cls : '') + (opts.on ? ' is-active' : ''),
      type: 'button',
      'aria-pressed': String(!!opts.on), title: opts.label, onclick: opts.onclick
    });
    a.appendChild(el('span', { class: 'sb-colour__sw', style: '--sw:' + opts.sw }));
    a.appendChild(el('span', { class: 'sb-colour__t', text: opts.label }));
    if (opts.n != null) a.appendChild(el('span', { class: 'sb-colour__n', text: String(opts.n) }));
    return a;
  };

  // The horizontal, snapping, fade-edged scroller the panel uses for long rows.
  RB.scroller = function (kids, mod) {
    return el('div', { class: 'sb__scroll' + (mod ? ' ' + mod : '') }, kids);
  };

  /* 🚨 THE STICKY FILTER BAR HAS TO PARK BELOW THE STICKY HEADER, AND THE HEIGHT
     IS MEASURED — NEVER TYPED (2026-08-13).
     `.rb-filterbar` and `.section-header` both pin at the top of the viewport, and
     since theme.css put the header at z-index 50 (so the mobile MENU can no longer
     be painted over — see that file) the header wins the strip. Left at `top: 0`
     the bar would slide underneath it and the shopper would lose the Filters
     button the moment they scrolled.
     ⚠️ `--header-height` is NOT usable here. header.liquid's ResizeObserver is
     supposed to publish it and does not (component-facets.css records the same
     finding, verified live 2026-07-27, and carries a `12rem` fallback) — and that
     12rem is the DESKTOP header measured on a desktop-only rule, which is not the
     number a phone needs. So this measures the element itself.
     ⚠️ A header that is not actually pinned must yield 0, not its height: the
     `position: sticky` in header.liquid is set unconditionally for a Safari
     stacking fix, so `position` alone says nothing. `top` resolving to a length is
     what separates a pinned header from one that scrolls away. */
  RB.syncHeaderOffset = function () {
    var hdr = document.querySelector('.section-header');
    var px = 0;
    if (hdr) {
      var cs = getComputedStyle(hdr);
      var top = parseFloat(cs.top);            // 'auto' -> NaN -> not pinned
      if ((cs.position === 'sticky' || cs.position === 'fixed') && !isNaN(top)) {
        // getBoundingClientRect over offsetHeight: the header is a transformed,
        // fractional-height box and a rounded number leaves a hairline of grid
        // showing through above the bar on every second device.
        px = Math.round(hdr.getBoundingClientRect().height + top);
      }
    }
    document.documentElement.style.setProperty('--rb-header-h', px + 'px');
    return px;
  };

  /* Re-measure when the header itself changes size (logo swap, announcement bar
     closing, orientation change). ⚠️ Observe the HEADER, not the window: the
     header's height changes at breakpoints a `resize` listener would also catch,
     but it changes on a cart-count reflow too, which fires no window event. */
  RB.watchHeaderOffset = function () {
    if (RB._hdrWatch) return;
    RB._hdrWatch = true;
    RB.syncHeaderOffset();
    var hdr = document.querySelector('.section-header');
    if (hdr && window.ResizeObserver) {
      new ResizeObserver(function () { RB.syncHeaderOffset(); }).observe(hdr);
    }
    // The first measurement can land before webfonts settle the header's height.
    window.addEventListener('load', function () { RB.syncHeaderOffset(); });
  };

  /* ⚠️ THIS COPY CAN BE DRAWN AGAIN IN PLACE (2026-09-17): the drawer below and RB.mountSort keep an open drawer open
     and add no listeners on a redraw. ring-builder-pages.js hands a picker to the no-reload helper only when it sees
     this: for ~20 minutes after a release Shopify's caches can pair it with the previous copy of this file, whose
     drawer marks the rail inert on every draw — an open drawer would stop answering taps. */
  RB.redraws = true;

  RB.mountFilterDrawer = function (root, rail, opts) {
    opts = opts || {};
    var fb = root.querySelector('[data-rb-filterbar]');
    if (!fb || !rail) return;
    RB.watchHeaderOffset();
    /* 🚨 A REDRAW MUST NOT CLOSE, OR DOUBLE, THE DRAWER (2026-09-17). The settings grid now redraws in place on every
       filter (assets/sr-client-filters.js) and calls this each time. The page-wide pieces — the scrim's click,
       Escape, the resize watcher and the first "closed" state — are wired ONCE per rail and always call the NEWEST
       draw's setOpen (through `drawer.setOpen`), so they never act on a button a redraw has replaced. The open
       state lives on the rail and is carried across the redraw, so a shopper filtering inside the drawer stays in it. */
    var drawer = rail.__rbDrawer || (rail.__rbDrawer = {});
    var isOpen = rail.getAttribute('data-open') === 'true';
    fb.innerHTML = '';
    var openBtn = el('button', { type: 'button', 'aria-expanded': String(isOpen) },
      [el('span', { text: 'Filters' })]);
    if (opts.activeCount) {
      openBtn.appendChild(el('span', { class: 'rb-fb-count', text: String(opts.activeCount) }));
    }
    var scrim = root.querySelector('[data-rb-scrim]');
    function setOpen(open) {
      rail.setAttribute('data-open', String(open));
      RB.syncDrawerA11y(rail, open);            // a closed drawer leaves the tab order
      if (scrim) scrim.hidden = !open;
      openBtn.setAttribute('aria-expanded', String(open));
      document.body.style.overflow = open ? 'hidden' : '';
      if (open && window.trapFocus) window.trapFocus(rail, rail.querySelector('button'));
      else if (window.removeTrapFocus) window.removeTrapFocus(openBtn);
    }
    openBtn.addEventListener('click', function () { setOpen(true); });
    drawer.setOpen = setOpen;
    if (!drawer.wired) {
      drawer.wired = true;
      if (scrim) scrim.addEventListener('click', function () { drawer.setOpen(false); });
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && rail.getAttribute('data-open') === 'true') drawer.setOpen(false);
      });
      RB.watchDrawerA11y(rail, function () { return rail.getAttribute('data-open') === 'true'; },
                         function () { drawer.setOpen(false); });
    }
    RB.syncDrawerA11y(rail, isOpen);
    fb.appendChild(openBtn);
    if (opts.sortLabel) {
      fb.appendChild(el('button', {
        type: 'button', text: opts.sortLabel,
        onclick: function () {
          var b = root.querySelector('.rb-sort__btn');
          if (b) { b.scrollIntoView({ block: 'nearest' }); b.click(); }
        }
      }));
    }
    if (!rail.querySelector('.rb-rail__head')) {
      // the live count belongs INSIDE the drawer too — the shopper is changing
      // filters here and cannot see the count on the page behind the scrim
      if (opts.countLabel) {
        rail.insertBefore(el('div', { class: 'rb-rail__count', text: opts.countLabel }),
                          rail.firstChild);
      }
      rail.insertBefore(el('div', { class: 'rb-rail__head' }, [
        el('strong', { text: 'Filters' }),
        el('button', { class: 'rb-rail__close', type: 'button', 'aria-label': 'Close filters',
          text: '✕', onclick: function () { setOpen(false); } })
      ]), rail.firstChild);
      rail.appendChild(el('div', { class: 'rb-rail__apply' }, [
        el('button', { class: 'btn btn-primary', type: 'button',
          text: opts.applyLabel || 'Show results',
          onclick: function () { setOpen(false); } })
      ]));
    }
    // A drawer redrawn while open keeps its focus trap on the controls it now holds: the trap remembers its first and
    // last control, and the redraw replaced them. The rail takes no focus itself, so nothing moves.
    if (isOpen && window.trapFocus) window.trapFocus(rail, rail);
    return { setOpen: setOpen };
  };

  /* ---------------------------------------------------------- step bar --- */
  // `active` is the step this PAGE is — and pages do not know their own number
  // any more, because the number depends on how the shopper came in. It is
  // passed as 'mount' | 'stone' | 'review'; the old numeric form still works so
  // no caller had to change on the day this landed.
  /* ------------------------------------------------ step-bar thumbnail ---- */
  // 🚨 THE BAR SHOWED YELLOW GOLD WHATEVER THE SHOPPER PICKED. `s.img` is the
  // FEATURED media — one metal, one shape, chosen at branding time — so a rose
  // gold build carried a yellow gold thumbnail through every step, next to the
  // words naming the metal they chose. Same class of defect as the grid tile, one
  // surface over, and the metal is the half that is unmistakable at this size.
  //
  // 🚨 IT IS A ~34px SLOT AND IT WAS LOADING THE FULL 1,376px JPEG (85 KB) on
  // every step. `&width=` is a Shopify CDN transform: 192px is 5.6 KB, covers the
  // slot to 5x, and is a 15x saving on a request the shopper never sees.
  // (`.rb-step__thumb` is 3.6rem, and rem is ~9.36px in this theme's declarations
  // — see the theme's two-rem rule — so ~34px, hidden entirely under the narrow
  // breakpoint.)
  //
  // ⚠️ METAL, NOT SHAPE, AND THAT IS A COST DECISION. The tile shard could give
  // the exact shape, but a PENDANT render is clipped at every rendition except
  // `$zoom$` (measured: $standard$ 300px and $xlarge$ 640px both clip 8/8, fill
  // 1.000 sd 0.000) — so a shape-accurate pendant thumbnail costs **89 KB** for a
  // 34px picture, against 5.6 KB here. At this size a round centre and an oval one
  // differ by a few pixels while the metal is the whole tile. Rings could use
  // `$standard$` at 10 KB, but then the thumbnail would change source between
  // waves and between steps, which is how two pictures of one ring get shipped.
  RB.THUMB_PX = 192;

  RB.settingThumb = function (build, s) {
    if (!s) return null;
    // The same default the setting page and the grid use, so the bar agrees with
    // the picture the shopper is looking at before they ever touch the metal rail.
    var m = build.metal ||
      (s.metals && s.metals.indexOf('14K Yellow Gold') >= 0 ? '14K Yellow Gold'
                                                            : (s.metals || [])[0]);
    var u = (m && (s.imgByMetal || {})[m]) || s.img;
    // A metal with no stored photo of its own (a two-tone metal before its photo run, or any metal whose photograph is
    // still being made: owner 2026-10-05) is DRAWN — the grid's tile render on step 1, the chosen seat's lead angle
    // elsewhere, at the 300px rendition — never shown in the featured photo's metal; with nothing to draw, no thumbnail.
    if (m && !(s.imgByMetal || {})[m]) {
      var t = RB.tileFrames(build.style, build.shape || 'ROUND', m);
      var a = t ? null : RB.anglesFor(build.style, build.seat, m, null);
      return t ? t.img : (a && a.length ? a[0].url.replace(/\$[a-z]+\$$/, '$standard$') : null);
    }
    return RB.thumbUrl(u);
  };

  // ⚠️ ONLY a Shopify CDN url takes `width`. Stuller's renderer names its size in
  // the `$rendition$` segment and would ignore this, so restricting it keeps the
  // parameter meaningful rather than decorative. The url already carries `?v=`,
  // hence `&`; the test is on the url, never on an assumed shape.
  RB.thumbUrl = function (u) {
    if (!u || u.indexOf('cdn.shopify.com') < 0 && u.indexOf('/cdn/shop/') < 0) return u || null;
    if (/[?&]width=/.test(u)) return u;
    return u + (u.indexOf('?') >= 0 ? '&' : '?') + 'width=' + RB.THUMB_PX;
  };

  /* ------------------------------------------------ grid tile images ---- */
  // 🚨 THE GRIDS DREW EVERY TILE FROM THE FULL MASTER (site audit 2026-09-16,
  // M002). The shards keep Shopify's bare `image.url` — on purpose: the detail
  // pages, the dome shot and the step bar start from it, and
  // build_builder_assets.py rebuilds the shards every night — and the cards put
  // it straight into `src`, so a 163px phone tile downloaded a 765–1,973px
  // original: a full scroll of the pendant mountings fetched 228 photos, 11.4 MB.
  // The size is chosen HERE, when a card is drawn, never in the data files.
  //
  // ONE RULE FOR A SIZED CDN URL. Unlike thumbUrl above (which leaves a URL that
  // already has a width alone, so the step bar can never stack two), this SETS
  // the size: a width, height or crop already on the URL is replaced, so asking
  // twice gives the same URL. `square` adds `height` and `crop=center`, the form
  // the reference stone grid asks for (diamond-browse.js `sized()`), so a builder
  // stone card and a /collections/diamonds card name the same file.
  // ⚠️ STRINGS, NEVER `URL` / `URLSearchParams`: verify_setting_tiles.py and
  // verify_browse_links.py run this file in JavaScriptCore, which has neither,
  // and RB.renderUrl records what URLSearchParams does to a Stuller URL's spaces.
  // ⚠️ SHOPIFY'S CDN ONLY, by thumbUrl's own test: a Stuller render names its size
  // in its `$rendition$` and comes back exactly as given.
  var onShopCdn = function (u) {
    return !!u && (u.indexOf('cdn.shopify.com') >= 0 || u.indexOf('/cdn/shop/') >= 0);
  };
  RB.sizedUrl = function (u, px, square) {
    if (!onShopCdn(u)) return u || null;
    var q = u.indexOf('?');
    var keep = (q < 0 ? '' : u.slice(q + 1)).split('&').filter(function (p) {
      return p && !/^(width|height|crop)=/.test(p);
    });
    keep.push('width=' + px);
    if (square) keep.push('height=' + px, 'crop=center');
    return (q < 0 ? u : u.slice(0, q)) + '?' + keep.join('&');
  };

  // THE THEME'S OWN IMAGE STEPS (snippets/responsive-image.liquid gives the
  // product cards 180, 360, 540 ...). A builder tile is at most ~480px (the
  // settings grid two across at 1023px), so 1080 covers it at 2x.
  RB.TILE_W = [180, 360, 540, 720, 900, 1080];

  // A grid tile's image, the theme's product-card answer: a srcset of those steps
  // and the `sizes` the caller measured, so the browser fetches what the tile
  // needs at the screen's density. `src` (720, sharp on any tile) is only for a
  // browser that ignores srcset. A URL that is not Shopify's — RB.tileFrames'
  // Stuller renders — comes back as `{ src }` alone, exactly as given.
  RB.tileImg = function (u, sizes) {
    if (!onShopCdn(u)) return { src: u };
    if (!sizes) return { src: RB.sizedUrl(u, 720) };
    return {
      sizes: sizes,
      srcset: RB.TILE_W.map(function (w) { return RB.sizedUrl(u, w) + ' ' + w + 'w'; }).join(', '),
      src: RB.sizedUrl(u, 720)
    };
  };

  // One sizing total for the navigation quote and review summary; the charge is per ring.
  RB.sizingTotal = function (build) {
    if (!RB.isSized(build) || !build.size || typeof RB.sizingFor !== 'function') return 0;
    var z = RB.sizingFor(build, build.size);
    return z.kind === 'charge' ? z.charge * RB.pairQty(build) : 0;
  };

  RB.stepBar = function (mount, build, active) {
    var s = build.style ? RB.setting(build.style) : null;
    /* `opts.unmade` (M147, owner 2026-09-27): the setting page is showing a metal or
       accent the SHOPPER chose that this center size is not made in. The setting step
       then quotes no price and is not done, so no step after it opens: nothing in the
       bar carries that pair on as if it could be ordered. Every other caller passes
       no `opts` and draws exactly as before. */
    var opts = arguments[3];
    var unmade = !!(opts && opts.unmade);
    var sv = RB.settingVariant(build);
    var kind = RB.KINDS[RB.kind(build)];
    var stoneFirst = RB.isStoneFirst(build);
    // Every price in this bar is a per-UNIT variant price, and a pair orders two
    // of each half — see the same multiplication on the review page. Without it
    // the bar quotes half of what the cart charges. 1 for rings and pendants.
    var q = kind.pair || 1;
    var ACTIVE_KEY = { 1: 'mount', 2: 'stone', 3: 'review' };
    var activeKey = ACTIVE_KEY[active] || active;

    /* ACCENT STONES (owner option 1, 2026-11-03): the setting step quotes the empty mounting WITH its accent charge.
       Where the accent menu is not loaded on this page the charge is unknown, so it quotes "from" the mounting alone. */
    var accQ = (build.accentStone && typeof RB.accentFor === 'function') ? RB.accentFor(build) : null;
    var accUnknown = !!(build.accentStone && !accQ);
    var mountStep = {
      key: 'mount',
      label: 'Choose a ' + kind.mount, href: RB.pages.settings,
      value: s ? RB.settingName(s.title) : 'Browse ' + kind.mount + 's',
      price: unmade ? null : sv ? (sv[1] + (accQ ? accQ.charge : 0)) * q : (s ? s.min * q : null),
      // The exact price needs rb-setting-variants.js, and that 14,233-entry
      // map is deliberately NOT loaded on steps 1-2b (it is the difference
      // between a 6 KB and a 225 KB first load). So outside review we only
      // have the style's MINIMUM, which for a configured setting can sit well
      // under what it will actually cost — $865 against $975 on style 222142.
      // Say "from" rather than quietly quoting the cheapest variant.
      from: (!sv && !!s) || (!!sv && accUnknown),
      done: !!build.style && !unmade, img: RB.settingThumb(build, s)
    };
    // A STONE IN THE URL THAT WOULD NOT RESOLVE IS NOT A CHOSEN STONE. It reads
    // as one everywhere else (`build.stone` is set), which is exactly how a
    // broken deep link came to render a confident "✓ Selected". `done` is false
    // here so the tick, the "Change" affordance and — through the positional
    // gate below — the next step all treat the build as still needing a stone.
    var missing = !!window.RB_STONE_MISSING;
    var stoneStep = {
      key: 'stone',
      label: 'Choose a gemstone',
      href: RB.stonesPage(build),
      value: !build.stone ? 'Browse gemstones'
           : missing ? 'No longer available — choose another'
           : (window.RB_STONE_TITLE || 'Selected'),
      price: (!missing && window.RB_STONE_PRICE) ? window.RB_STONE_PRICE * q : null,
      // Same 34px slot, same saving — the stone card's image is a full-size
      // Shopify master too.
      done: !!build.stone && !missing,
      img: missing ? null : RB.thumbUrl(window.RB_STONE_IMG || null)
    };
    // 🚨 THE GATE IS POSITIONAL, NOT PER-HALF. It used to be `disabled:
    // !build.style` hardcoded onto the gemstone step, which is what made it
    // impossible to start with a stone from inside the builder at all — the only
    // stone-first path was a deep link, and that link then landed on "step 1,
    // choose a mounting". Now the SECOND step waits on the FIRST, whichever they
    // are, and the shopper is never blocked on a half they deliberately deferred.
    var ordered = stoneFirst ? [stoneStep, mountStep] : [mountStep, stoneStep];
    ordered[1].disabled = !ordered[0].done;

    /* 🚨 SIDE STONES ARE A REQUIRED STEP, NOT AN UPGRADE — because the mounting
       ships with NOTHING IN IT (owner decision 2026-08-20, measured the same day).
       The pilot is Stuller's MOUNTING SKU: sampled 24 ACTIVE variants and every one
       reports `SetWith` EMPTY and `CanBeSetWith` 3 (one centre, two sides). The
       Semi-Set SKU of the same design carries the two side diamonds welded in and
       costs $579.72 against $445.55 — so the diamonds are an ADDITION to a bare
       mounting, never something to discount out of it.
       Which makes "optional" false and dangerous in one direction only: a build that
       skipped this step ordered a mounting with two EMPTY SEATS and nothing on the
       storefront said so. Verified reachable before the fix — setting + centre alone
       added to cart at $1,350.
       ⚠️ THERE IS NO "DEFAULT DIAMONDS" BRANCH TO OFFER, and that was measured too:
       the Semi-Set product exists on EMERALD/OVAL/ROUND only — 25 of 135 centre
       seats — and we hold ZERO calibrated oval diamonds at 4x3 or 5x3, the side spec
       on 85 of them. So the step is required rather than a yes/no.
       ⚠️ APPEARS ONLY FOR A MOUNTING THAT REALLY HAS EMPTY SIDE SEATS. `RB.sideSeat`
       is null for the other 521 styles, so their bar is three steps and untouched —
       and a carried `?side=` on a style with no side seat cannot conjure a step,
       which is the same rule `normalizeToStyle` applies to every other field. */
    var barSide = (build.style && build.seat)
      ? RB.sideSeat(build.style, build.seat) : null;
    var sideStep = null;
    // one stone on a second-stone design (RB.secondStone): the owner's words, here and in review's value below
    var barSecond = RB.secondStone(build.style, barSide) && q === 1;
    if (barSide) {
      var sideN = (barSide.n || 2) * q;
      sideStep = {
        key: 'side',
        label: barSecond ? 'Choose a second stone' : 'Choose side stones',
        href: RB.sidePage(build),
        // ⚠️ THE ONLY STEP THAT CARRIES AN `extra`. `for` is a MODE and is
        // deliberately outside FIELDS, so it cannot ride along on `build` — without
        // this the step would link to the ordinary centre-stone grid.
        extra: { for: 'side' },
        value: !build.side ? RB.sideSeatLabel(barSide.spec, null, build.style, barSide.set)
             : (window.RB_SIDE_TITLE || 'Selected'),
        price: (build.side && window.RB_SIDE_PRICE)
             ? window.RB_SIDE_PRICE * sideN : null,
        done: !!build.side,
        img: RB.thumbUrl(window.RB_SIDE_IMG || null),
        disabled: !(ordered[0].done && ordered[1].done)
      };
    }
    var steps = ordered.concat(sideStep ? [sideStep] : []).concat([{
      key: 'review',
      label: 'Your ' + kind.label, href: RB.pages.review,
      // 🚨 REVIEW WAITS ON THE SIDES TOO. Leaving this on the original two would let
      // the shopper walk straight past the new step to a ring that cannot be made.
      value: (stoneStep.done && mountStep.done && (!sideStep || sideStep.done))
           ? 'Review and add to cart'
           : (sideStep && stoneStep.done && mountStep.done)
             ? (barSecond ? 'Choose your second stone' : 'Choose your side stones') : 'Complete every step',
      // accent stones: the ring's total carries their charge, and none is quoted while the charge is unknown
      price: (sv && !missing && window.RB_STONE_PRICE && !accUnknown)
           ? (sv[1] + (accQ ? accQ.charge : 0) + window.RB_STONE_PRICE) * q +
             ((sideStep && build.side && window.RB_SIDE_PRICE)
               ? window.RB_SIDE_PRICE * (barSide.n || 2) * q : 0) + RB.sizingTotal(build)
           : null,
      disabled: !(stoneStep.done && mountStep.done && (!sideStep || sideStep.done))
    }]);
    // The number is the POSITION, assigned after ordering — never a property of
    // the half. Carrying `start` on every link is what keeps it that way across
    // the whole flow rather than only on the page that set it.
    steps.forEach(function (st, i) {
      st.n = i + 1;
      st.isActive = st.key === activeKey;
      /* ⚠️ THE STEP YOU ARE STANDING ON IS NEVER DISABLED. Review is gated until every
         earlier step is done — right for the LINK, and a contradiction on the page
         itself: a shopper who reached an incomplete review saw "4 Your ring" carrying
         both `aria-current="step"` and `aria-disabled="true"`, which a screen reader
         reads as "current step, dimmed" and which strips the href from the page you
         are already on. The gate's job is to stop you ARRIVING early, and by here you
         have arrived. Incompleteness is still said out loud — the step's own value
         reads "Choose your side stones" and the CTA is off with a reason beside it. */
      if (st.isActive) st.disabled = false;
    });
    var bar = el('nav', { class: 'rb-steps',
      'aria-label': kind.piece + ' builder progress' });
    steps.forEach(function (st) {
      var a = el('a', {
        class: 'rb-step' + (st.done ? ' is-done' : ''),
        // Stone-first links carry `start`, so the numbering the shopper met on
        // the way in survives each hop — without it, clicking "Change" once both
        // halves are chosen would silently renumber the flow back to
        // mounting-first mid-build (`startedWith` can only infer stone-first
        // while there is no mounting).
        // Mounting-first emits NOTHING, because 'mount' is already what an absent
        // `start` means. Writing it out would put a new query param on every
        // builder URL in the store — including the ones already indexed — to say
        // what the default says.
        href: st.disabled ? null : RB.href(st.href, build,
          Object.assign({}, st.extra || {},
            RB.isStoneFirst(build) ? { start: 'stone' } : { start: null })),
        'aria-current': st.isActive ? 'step' : null,
        'aria-disabled': st.disabled ? 'true' : null
      }, [
        el('span', { class: 'rb-step__n', text: st.done && !st.isActive ? '✓' : String(st.n) }),
        st.img ? el('img', { class: 'rb-step__thumb', src: st.img, alt: '', loading: 'lazy' }) : null,
        el('span', { class: 'rb-step__body' }, [
          el('span', { class: 'rb-step__label', text: st.label }),
          el('span', { class: 'rb-step__value',
            text: st.value + (st.price ? ' · ' + (st.from ? 'from ' : '') + money(st.price) : '') })
        ]),
        st.done && !st.isActive ? el('span', { class: 'rb-step__edit', text: 'Change' }) : null
      ]);
      /* 🚨 THE STEP YOU ARE ON LINKED TO THE PAGE YOU ARE ON (Clarity dead taps 13-19 Sep; site-audit-2026-09-16/
         deadtap/FINDINGS.md). sr-client-filters.js catches every link to this same builder page (capture phase, then
         stopPropagation) and redraws it with the same choices, so on a phone a tap on "2 Choose a gemstone / Browse
         gemstones", above a list that starts below the fold, changed nothing visible. When the current step points at
         THIS view -- the very test sr-client-filters' sameTool() applies: same page AND the same `configure`,
         `style` and `for` (its `keep` list; a link that changes one of them is another view, e.g. "Choose a mounting"
         on a single mounting's configure view goes back to the grid, Codex review-muf0rbr7-2tx5pq) -- and the page
         has its content block ([data-rb-body]), the step becomes an in-page anchor to it (the one kind of link
         sr-client-filters leaves to the page) and a plain tap glides down to it under the pinned header (measured
         the way gem-education.js measures it; reduced motion jumps). Every other step keeps its link.
         builder-step-scroll/test_scroll.cjs. */
      if (st.isActive && a.getAttribute('href')) {
        var dest = new URL(a.getAttribute('href'), location.href);
        var stepRoot = mount.closest ? mount.closest('.rb') : null;
        var content = stepRoot && stepRoot.querySelector('[data-rb-body]');
        var here = new URLSearchParams(location.search);
        var sameView = dest.origin === location.origin && dest.pathname === location.pathname &&
          ['configure', 'style', 'for'].every(function (k) { return dest.searchParams.get(k) === here.get(k); });
        if (content && sameView) {
          if (!content.id) content.id = 'rb-content';
          a.setAttribute('href', '#' + content.id);
          a.addEventListener('click', function (ev) {
            if (ev.defaultPrevented || ev.button || ev.metaKey || ev.ctrlKey || ev.shiftKey || ev.altKey) return;
            ev.preventDefault();
            var hdr = document.querySelector('.section-header'), off = 0;
            if (hdr) {
              var cs = getComputedStyle(hdr), top = parseFloat(cs.top);
              if ((cs.position === 'sticky' || cs.position === 'fixed') && !isNaN(top)) off = hdr.getBoundingClientRect().height + top;
            }
            var reduce = !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
            window.scrollTo({ top: Math.max(0, Math.round(content.getBoundingClientRect().top + window.pageYOffset - off - 16)),
                              behavior: reduce ? 'auto' : 'smooth' });
          });
        }
      }
      bar.appendChild(a);
    });
    mount.innerHTML = '';
    mount.appendChild(bar);
    /* 🚨 THE BAR IS A HORIZONTAL SCROLLER AND IT NEVER SCROLLED TO WHERE THE SHOPPER
       WAS (2026-09-02, measured at 375px). With the 4-step side-stone bar — 562px in a
       327px box — `scrollLeft` stayed 0, so on steps 3 and 4 the bar showed steps 1-2
       ticked and the CURRENT step, the one carrying the running total, was entirely
       off-screen. Scroll it into view once layout exists; `data-scrollable` is what
       the CSS keys its right-edge fade on, so a clipped bar is visibly clipped.
       ⚠️ `scrollLeft`, never `scrollIntoView` — the latter also scrolls the PAGE
       vertically to the bar on every step change. */
    var cur = bar.querySelector('[aria-current="step"]');
    var place = function () {
      var clipped = bar.scrollWidth - bar.clientWidth;
      bar.setAttribute('data-scrollable', clipped > 2 ? 'true' : 'false');
      if (clipped > 2 && cur) {
        var target = cur.offsetLeft - Math.max(0, (bar.clientWidth - cur.offsetWidth) / 2);
        bar.scrollLeft = Math.max(0, Math.min(target, clipped));
      }
    };
    /* SYNCHRONOUSLY, AND AGAIN ON THE NEXT FRAME. `appendChild` above means reading
       `scrollWidth` here forces layout, so the placement is already correct — and a
       rAF-only version never ran in a background tab (`document.hidden`), which is
       how the first push of this measured `data-scrollable: null`. The second call
       catches a late web-font reflow; a repeat is idempotent. */
    place();
    if (window.requestAnimationFrame) requestAnimationFrame(place);
  };
})();
